var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Ribbon_1;
import { addClass, append, Event, Collection, Complex, Component, EventHandler, formatUnit, getInstance, getComponent, getUniqueID, closest, KeyboardEvents } from '@syncfusion/ej2-base';
import { isNullOrUndefined, isUndefined, NotifyPropertyChanges, Property, remove, removeClass, L10n } from '@syncfusion/ej2-base';
import { Tab, TabAnimationSettings, HScroll, Toolbar } from '@syncfusion/ej2-navigations';
import { RibbonTab, FileMenuSettings, BackStageMenu, RibbonContextualTabSettings } from '../models/index';
import { DisplayMode, RibbonLayout } from './interface';
import { ItemOrientation, RibbonItemSize, RibbonItemType } from './interface';
import { RibbonButton, RibbonComboBox, RibbonCheckBox, RibbonDropDown, RibbonSplitButton, RibbonGroupButton } from '../items/index';
import { destroyControl, getCollection, getGroup, getIndex, getItem, getItemElement, updateCommonProperty, updateControlDisabled, isTooltipPresent, getTemplateFunction, createTooltip, destroyTooltip, updateTooltipProp } from './utils';
import * as constants from './constant';
import { Popup } from '@syncfusion/ej2-popups';
import { DropDownButton, SplitButton } from '@syncfusion/ej2-splitbuttons';
import { ColorPicker } from '@syncfusion/ej2-inputs';
import { CheckBox } from '@syncfusion/ej2-buttons';
/**
 * The Ribbon Component is a structured layout to manage tools with tabs and groups.
 */
let Ribbon = Ribbon_1 = class Ribbon extends Component {
    /**
     * Constructor for creating the widget.
     *
     * @param  {RibbonModel} options - Specifies the ribbon model
     * @param  {string|HTMLDivElement} element - Specifies the target element
     */
    constructor(options, element) {
        Ribbon_1.Inject(RibbonButton, RibbonCheckBox, RibbonDropDown, RibbonSplitButton, RibbonComboBox, RibbonGroupButton);
        super(options, element);
    }
    /**
     * Initialize the control rendering.
     *
     * @returns {void}
     * @private
     */
    render() {
        this.initialize();
    }
    preRender() {
        this.keysPress = '';
        this.idIndex = 0;
        this.tooltipData = [];
        this.initialPropsData = {};
        this.hiddenElements = {};
        this.keyTipElements = {};
        this.hiddenGroups = [];
        this.itemsModel = [];
        this.targetTabs = {};
        this.isAddRemove = false;
        this.isUpdateItems = false;
        this.keyConfigs = {
            leftarrow: 'leftarrow',
            rightarrow: 'rightarrow',
            tab: 'tab',
            shiftTab: 'shift+tab'
        };
    }
    /**
     * Get the properties to be maintained in the persisted state.
     *
     * @returns {string} - Persist data
     */
    getPersistData() {
        return this.addOnPersist(['activeLayout']);
    }
    /**
     * Get component name.
     *
     * @returns {string} - Module name
     * @private
     */
    getModuleName() {
        return 'ribbon';
    }
    /**
     * To provide the array of modules needed for component rendering
     *
     * @returns {ModuleDeclaration[]} - returns module declaration.
     * @hidden
     */
    requiredModules() {
        const modules = [];
        modules.push({ member: 'ribbonButton', args: [this], name: 'RibbonButton' }, { member: 'ribbonDropDown', args: [this], name: 'RibbonDropDown' }, { member: 'ribbonSplitButton', args: [this], name: 'RibbonSplitButton' }, { member: 'ribbonCheckBox', args: [this], name: 'RibbonCheckBox' }, { member: 'ribbonComboBox', args: [this], name: 'RibbonComboBox' }, { member: 'ribbonGroupButton', args: [this], name: 'RibbonGroupButton' });
        const canInjectColorPickerModule = getItem(this.tabs, null, RibbonItemType.ColorPicker);
        const canInjectGalleryModule = getItem(this.tabs, null, RibbonItemType.Gallery);
        if (canInjectColorPickerModule) {
            modules.push({ member: 'ribbonColorPicker', args: [this], name: 'RibbonColorPicker' });
        }
        if (canInjectGalleryModule) {
            modules.push({ member: 'ribbonGallery', args: [this], name: 'RibbonGallery' });
        }
        if (this.backStageMenu.visible || this.backStageMenu.items.length) {
            modules.push({ member: 'ribbonBackstage', args: [this], name: 'RibbonBackstage' });
        }
        if (this.fileMenu.visible || this.fileMenu.menuItems.length) {
            modules.push({ member: 'ribbonFileMenu', args: [this], name: 'RibbonFileMenu' });
        }
        if (this.contextualTabs.length) {
            modules.push({ member: 'ribbonContextualTab', args: [this], name: 'RibbonContextualTab' });
        }
        if (this.enableKeyTips) {
            modules.push({ member: 'ribbonKeyTip', args: [this], name: 'RibbonKeyTip' });
        }
        return modules;
    }
    initialize() {
        this.element.id = this.element.id || getUniqueID('e-' + this.getModuleName());
        this.initializeLocale();
        addClass([this.element], ['e-rbn', ...(this.cssClass ? this.cssClass.split(constants.SPACE) : [])]);
        if (this.enableRtl) {
            this.element.classList.add(constants.RTL_CSS);
        }
        this.element.style.width = formatUnit(this.width);
        this.renderTabs();
        if (this.ribbonContextualTabModule) {
            this.ribbonContextualTabModule.addContextualTabs();
        }
        if (this.ribbonFileMenuModule) {
            this.ribbonFileMenuModule.createFileMenu(this.fileMenu);
        }
        if (this.ribbonBackstageModule) {
            this.ribbonBackstageModule.createBackStage(this.backStageMenu);
        }
        this.createHelpPaneTemplate();
        const toolbar = this.tabObj['tbObj'];
        toolbar.refreshOverflow();
        this.addTabOverflowKeyTip();
        createTooltip(this.element, this);
        this.isKeytipOpen = false;
        this.wireEvents();
        this.wireKeyboardEvent();
        this.currentControlIndex = 0;
    }
    initializeLocale() {
        this.l10n = new L10n('ribbon', {
            groupOverflow: 'More Group Options',
            overflow: 'More Options'
        }, this.locale);
        this.l10n.setLocale(this.locale);
    }
    wireEvents() {
        this.resizeListener = this.resizeHandler.bind(this);
        EventHandler.add(window, 'resize', this.resizeListener);
        EventHandler.add(document.body, 'keydown', this.keytipActionHandler, this);
        EventHandler.add(document, 'mousedown', this.mouseEventHandler, this);
        EventHandler.add(document, 'scroll', this.mouseEventHandler, this);
    }
    wireKeyboardEvent() {
        this.keyboardModuleRibbon = new KeyboardEvents(this.element, {
            keyAction: this.keyActionHandler.bind(this),
            keyConfigs: this.keyConfigs,
            eventName: 'keydown'
        });
    }
    keyActionHandler(e) {
        if (((e.key === 'Tab') && (!(e.target.classList.contains('e-tab-wrap')) && !(e.target.classList.contains('e-combobox'))))) {
            e.preventDefault();
        }
        const activeContent = this.tabObj.element.querySelector('#' + this.tabs[this.selectedTab].id + constants.CONTENT_ID);
        const controlElements = Array.prototype.slice.call(activeContent.querySelectorAll('.e-control'));
        const templateElements = Array.prototype.slice.call(activeContent.querySelectorAll('.e-ribbon-template'));
        const galleryElements = Array.prototype.slice.call(activeContent.querySelectorAll('.e-ribbon-gallery-item'));
        const ribbonControls = controlElements.concat(templateElements, galleryElements);
        const comboBoxElements = activeContent.querySelectorAll('.e-combobox');
        let comboBoxEle;
        if (comboBoxElements) {
            for (let i = 0; i < comboBoxElements.length; i++) {
                if (comboBoxElements[parseInt(i.toString(), 10)].closest('.e-input-focus')) {
                    comboBoxEle = comboBoxElements[parseInt(i.toString(), 10)];
                }
            }
        }
        if (comboBoxEle) {
            for (let i = 0; i < ribbonControls.length; i++) {
                if (ribbonControls[parseInt(i.toString(), 10)].classList.contains('e-combobox')) {
                    if (ribbonControls[parseInt(i.toString(), 10)].closest('.e-input-focus')) {
                        this.currentControlIndex = i;
                    }
                }
            }
        }
        if (this.currentControlIndex === 0) {
            let item = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
            while (item && item.classList.contains('e-disabled')) {
                this.currentControlIndex++;
                item = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
            }
        }
        if (e.target.classList.contains('e-control') || e.target.classList.contains('e-ribbon-launcher-icon') ||
            e.target.classList.contains('e-ribbon-collapse-btn') || e.target.classList.contains('e-ribbon-last-item') ||
            e.target.classList.contains('e-ribbon-first-item') || e.target.classList.contains('e-ribbon-group-of-btn') ||
            e.target.classList.contains('e-ribbon-overall-of-btn') || e.target.classList.contains('e-ribbon-template') || e.target.classList.contains('e-ribbon-gallery-item')) {
            switch (e.action) {
                case 'rightarrow':
                    this.handleNavigation(e, !this.enableRtl, ribbonControls);
                    break;
                case 'leftarrow':
                    this.handleNavigation(e, this.enableRtl, ribbonControls);
                    break;
                case 'tab':
                    if (e.target.classList.contains('e-combobox')) {
                        if (this.currentControlIndex < ribbonControls.length - 1) {
                            this.currentControlIndex++;
                        }
                    }
                    break;
                case 'shiftTab':
                    if (e.target.classList.contains('e-combobox')) {
                        if (this.currentControlIndex > 0) {
                            this.currentControlIndex--;
                        }
                    }
                    else {
                        this.tabObj.element.querySelector('.e-toolbar-item.e-active').querySelector('.e-tab-wrap').focus();
                        this.currentControlIndex = 0;
                    }
            }
        }
    }
    handleNavigation(e, enableRtl, ribbonControls) {
        let groupContainer;
        let prevGroupId;
        if (enableRtl) {
            if (this.currentControlIndex < ribbonControls.length - 1 && ribbonControls[this.currentControlIndex + 1].classList.contains('e-colorpicker')) {
                this.currentControlIndex++;
            }
        }
        else {
            if (this.currentControlIndex > 0 && ribbonControls[this.currentControlIndex - 1].classList.contains('e-colorpicker')) {
                this.currentControlIndex--;
            }
        }
        if ((!enableRtl && (this.currentControlIndex > 0)) || (enableRtl && (this.currentControlIndex < ribbonControls.length - 1))) {
            if (!e.target.classList.contains('e-combobox') && (e.target.classList.contains('e-control') || e.target.classList.contains('e-ribbon-template') || e.target.classList.contains('e-ribbon-gallery-item')) && !e.target.classList.contains('e-ribbon-last-item')) {
                if (enableRtl) {
                    this.currentControlIndex++;
                }
                else {
                    const prevGroupContainer = ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].closest('.' + constants.RIBBON_GROUP_CONTAINER);
                    if (prevGroupContainer) {
                        prevGroupId = prevGroupContainer.getAttribute('id');
                    }
                    this.currentControlIndex--;
                }
                let item = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
                while (item && item.classList.contains('e-disabled')) {
                    if (((enableRtl && this.currentControlIndex === ribbonControls.length - 1) ||
                        (!enableRtl && this.currentControlIndex === 0))) {
                        if (ribbonControls[this.currentControlIndex].closest('.e-ribbon-item').classList.contains('e-disabled')) {
                            this.tabObj.element.querySelector('.e-ribbon-collapse-btn').focus();
                            break;
                        }
                    }
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    enableRtl ? this.currentControlIndex++ : this.currentControlIndex--;
                    item = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
                }
                ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].focus();
                if (this.activeLayout === 'Classic') {
                    groupContainer = ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].closest('.' + constants.RIBBON_GROUP_CONTAINER);
                    if (enableRtl) {
                        let launcherIconEle;
                        if (groupContainer) {
                            launcherIconEle = groupContainer.querySelector('.e-ribbon-launcher-icon');
                        }
                        if (launcherIconEle) {
                            const items = groupContainer.querySelectorAll('.e-ribbon-item');
                            const elem = items[items.length - 1].querySelector('.e-control');
                            if (elem) {
                                elem.classList.add('e-ribbon-last-item');
                            }
                        }
                    }
                    else {
                        if (groupContainer) {
                            const groupContainerId = groupContainer.getAttribute('id');
                            if (prevGroupId !== groupContainerId) {
                                const launcherIconEle = groupContainer.querySelector('.e-ribbon-launcher-icon');
                                if (launcherIconEle) {
                                    ribbonControls[parseInt((this.currentControlIndex + 1).toString(), 10)].classList.add('e-ribbon-first-item');
                                }
                            }
                        }
                    }
                }
                else {
                    if (ribbonControls[parseInt((this.currentControlIndex).toString(), 10)].classList.contains('e-ribbon-first-item')) {
                        ribbonControls[parseInt((this.currentControlIndex).toString(), 10)].classList.remove('e-ribbon-first-item');
                    }
                    else if (ribbonControls[parseInt((this.currentControlIndex).toString(), 10)].classList.contains('e-ribbon-last-item')) {
                        ribbonControls[parseInt((this.currentControlIndex).toString(), 10)].classList.remove('e-ribbon-last-item');
                    }
                }
            }
        }
        else {
            if (this.activeLayout === 'Classic') {
                this.tabObj.element.querySelector('.e-ribbon-collapse-btn').focus();
            }
            if (this.activeLayout === 'Simplified') {
                const overflowButton = this.tabObj.element.querySelector('.e-ribbon-overall-of-btn');
                if (enableRtl && (overflowButton && !overflowButton.classList.contains('e-ribbon-hide'))) {
                    overflowButton.focus();
                }
                else {
                    this.tabObj.element.querySelector('.e-ribbon-collapse-btn').focus();
                }
            }
        }
        if (e.target.classList.contains('e-ribbon-last-item')) {
            if (enableRtl) {
                groupContainer = ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].closest('.' + constants.RIBBON_GROUP_CONTAINER);
                groupContainer.querySelector('.e-ribbon-launcher-icon').focus();
            }
            else {
                this.currentControlIndex--;
                ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].focus();
            }
        }
        if (!enableRtl && e.target.classList.contains('e-ribbon-first-item')) {
            groupContainer = ribbonControls[parseInt((this.currentControlIndex - 1).toString(), 10)].closest('.' + constants.RIBBON_GROUP_CONTAINER);
            const launcherIconEle = groupContainer.querySelector('.e-ribbon-launcher-icon');
            if (launcherIconEle) {
                groupContainer.querySelector('.e-ribbon-launcher-icon').focus();
            }
        }
        if (e.target.classList.contains('e-ribbon-launcher-icon')) {
            if (enableRtl) {
                this.currentControlIndex++;
                ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].focus();
                if (ribbonControls[parseInt((this.currentControlIndex - 1).toString(), 10)].classList.contains('e-ribbon-last-item')) {
                    ribbonControls[parseInt((this.currentControlIndex - 1).toString(), 10)].classList.remove('e-ribbon-last-item');
                }
            }
            else {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                this.currentControlIndex;
                ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].focus();
            }
        }
        if (e.target.classList.contains('e-ribbon-collapse-btn')) {
            if (enableRtl) {
                this.currentControlIndex = 0;
                let ribbonItem = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
                while (ribbonItem && ribbonItem.classList.contains('e-disabled')) {
                    this.currentControlIndex++;
                    ribbonItem = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
                }
                ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].focus();
            }
            else {
                const overflowButton = this.tabObj.element.querySelector('.e-ribbon-overall-of-btn');
                if ((overflowButton && !overflowButton.classList.contains('e-ribbon-hide'))) {
                    overflowButton.focus();
                }
                else {
                    this.currentControlIndex = ribbonControls.length - 1;
                    let ribbonItem = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
                    while (ribbonItem && ribbonItem.classList.contains('e-disabled')) {
                        this.currentControlIndex--;
                        ribbonItem = ribbonControls[this.currentControlIndex].closest('.e-ribbon-item');
                    }
                    ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].focus();
                }
            }
        }
        if (this.activeLayout === 'Simplified' && e.target.classList.contains('e-ribbon-overall-of-btn')) {
            if (enableRtl) {
                this.tabObj.element.querySelector('.e-ribbon-collapse-btn').focus();
            }
            else {
                this.currentControlIndex = ribbonControls.length - 1;
                ribbonControls[parseInt(this.currentControlIndex.toString(), 10)].focus();
            }
        }
    }
    resizeHandler() {
        const activeContent = this.tabObj.element.querySelector('#' + this.tabs[this.selectedTab].id + constants.CONTENT_ID);
        if (!isNullOrUndefined(activeContent)) {
            this.checkOverflow(this.selectedTab, activeContent);
        }
        if (this.scrollModule) {
            const scrollEle = this.tabObj.element.querySelector('.' + constants.HORIZONTAL_SCROLLBAR);
            this.scrollModule.scrollStep = scrollEle.offsetWidth;
        }
        if (this.activeLayout === 'Simplified') {
            const activePopups = document.querySelectorAll('.e-ribbon .e-dropdown-btn.e-active, .e-ribbon-group-overflow-ddb .e-dropdown-btn.e-active');
            const colorPickerPopups = document.querySelectorAll('.e-ribbon .e-colorpicker-wrapper.e-ribbon-control.e-ribbon-open .e-colorpicker, .e-ribbon-group-overflow-ddb .e-colorpicker-wrapper.e-ribbon-control.e-ribbon-open .e-colorpicker');
            activePopups.forEach((popup) => {
                const dropDownBtn = getInstance(popup, DropDownButton);
                if (dropDownBtn) {
                    dropDownBtn.toggle();
                }
            });
            colorPickerPopups.forEach((picker) => {
                const colorPickerObj = getInstance(picker, ColorPicker);
                if (colorPickerObj) {
                    colorPickerObj.toggle();
                    colorPickerObj.refresh();
                }
            });
        }
        const galleryPopupEle = document.querySelector('.e-ribbon-gallery-popup.e-popup-open');
        if (galleryPopupEle) {
            const popup = getComponent(galleryPopupEle, Popup);
            popup.hide();
        }
        if (this.ribbonKeyTipModule && this.enableKeyTips) {
            this.ribbonKeyTipModule.removeKeytip();
        }
    }
    mouseEventHandler() {
        if (this.ribbonKeyTipModule && this.enableKeyTips) {
            this.ribbonKeyTipModule.removeKeytip();
        }
    }
    keytipActionHandler(e) {
        if (this.enableKeyTips) {
            let isKeyTipPresent = false;
            const keyPress = e.key;
            if (e.altKey && e.key === 'Meta') {
                const activePopup = document.querySelectorAll('.e-ribbon .e-dropdown-btn.e-active, .e-ribbon-group-overflow-ddb .e-dropdown-btn.e-active');
                if (activePopup.length) {
                    for (let i = 0; i < activePopup.length; i++) {
                        const dropDownBtn = getInstance(activePopup[parseInt(i.toString(), 10)], DropDownButton);
                        dropDownBtn.toggle();
                    }
                    this.ribbonKeyTipModule.removeKeytip();
                }
                else {
                    if (!this.isKeytipOpen) {
                        const backstagePopup = document.querySelector('.e-ribbon-backstage-popup');
                        if (backstagePopup && backstagePopup.classList.contains('e-popup-open')) {
                            this.ribbonBackstageModule.hideBackstage();
                        }
                        this.ribbonKeyTipModule.createKeytip('tab');
                    }
                    else {
                        this.ribbonKeyTipModule.removeKeytip();
                    }
                }
            }
            else if (e.key === 'Escape' || e.key === 'Tab' || e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                this.ribbonKeyTipModule.removeKeytip(e.key);
            }
            else {
                const keyTipItems = document.querySelectorAll('.e-ribbon-keytip');
                if (keyTipItems) {
                    for (let i = 0; i < keyTipItems.length; i++) {
                        const keyTipItem = keyTipItems[parseInt(i.toString(), 10)];
                        if (keyTipItem.innerHTML.toLowerCase() === keyPress) {
                            isKeyTipPresent = true;
                            this.ribbonKeyTipModule.keytipPress(keyPress);
                            break;
                        }
                    }
                    if (!isKeyTipPresent) {
                        this.checkKeyTipPresent(keyPress, this.keysPress.length);
                    }
                }
            }
        }
    }
    checkKeyTipPresent(keyTip, length) {
        const keyTipItems = document.querySelectorAll('.e-ribbon-keytip');
        for (let i = 0; i < keyTipItems.length; i++) {
            const keyTipItem = keyTipItems[parseInt(i.toString(), 10)];
            if (keyTipItem.innerHTML.length > 1 && keyTipItem.innerHTML[parseInt(length.toString(), 10)].toLowerCase() === keyTip) {
                this.keysPress += keyTip;
                this.ribbonKeyTipModule.keytipPress(this.keysPress);
                this.removeKeytip(this.keysPress);
                break;
            }
        }
    }
    removeKeytip(keyTip) {
        const keyTipItems = document.querySelectorAll('.e-ribbon-keytip');
        for (let i = 0; i < keyTipItems.length; i++) {
            const keyTipItem = keyTipItems[parseInt(i.toString(), 10)];
            if (keyTipItem.innerHTML[0].toLowerCase() !== keyTip && keyTip !== '') {
                remove(keyTipItem);
            }
        }
    }
    addKeyTip(tabIndex, keyTip, id, type) {
        if (this.keyTipElements && this.keyTipElements[parseInt(tabIndex.toString(), 10)]) {
            let isKeyTipExist = false;
            if (!(this.keyTipElements[parseInt(tabIndex.toString(), 10)][`${type}`])) {
                this.keyTipElements[parseInt(tabIndex.toString(), 10)][`${type}`] = [];
            }
            if (Object.keys(this.keyTipElements[parseInt(tabIndex.toString(), 10)][`${type}`]).length) {
                const keytipData = this.keyTipElements[parseInt(tabIndex.toString(), 10)][`${type}`];
                for (let i = 0; i < Object.keys(this.keyTipElements[parseInt(tabIndex.toString(), 10)][`${type}`]).length; i++) {
                    if (keytipData[parseInt(i.toString(), 10)].id === id) {
                        isKeyTipExist = true;
                    }
                }
                if (!isKeyTipExist) {
                    this.keyTipElements[parseInt(tabIndex.toString(), 10)][`${type}`].
                        push({ id: id, type: type, keyTip: keyTip });
                }
            }
            else {
                this.keyTipElements[parseInt(tabIndex.toString(), 10)][`${type}`].push({ id: id, type: type, keyTip: keyTip });
            }
        }
    }
    renderTabs() {
        this.tabsInternal = this.tabs.slice();
        this.tabsInternal = this.checkID(this.tabsInternal, 'tab', this.element.id);
        this.setProperties({ tabs: this.tabsInternal }, true);
        const tabEle = this.createElement('div', {
            id: this.element.id + constants.TAB_ID
        });
        this.element.appendChild(tabEle);
        this.validateItemSize();
        const tabItems = this.createTabItems(this.tabs);
        this.tabObj = new Tab({
            cssClass: constants.RIBBON_TAB,
            selectedItem: this.selectedTab,
            overflowMode: 'Popup',
            width: this.width,
            items: tabItems,
            enableRtl: this.enableRtl,
            created: this.tabCreated.bind(this),
            selected: this.ribbonTabSelected.bind(this),
            selecting: this.ribbonTabSelecting.bind(this),
            animation: this.tabAnimation
        });
        this.tabObj.appendTo(tabEle);
        //Set the width value as "0px" with unit for proper calculation.
        this.element.style.setProperty(constants.RIBBON_FILE_MENU_WIDTH, '0px');
        this.element.style.setProperty(constants.RIBBON_HELP_PANE_TEMPLATE_WIDTH, '0px');
        const toolbarEle = tabEle.querySelector('.e-toolbar');
        const toolbar = getComponent(toolbarEle, Toolbar);
        toolbar.setProperties({ width: 'calc(100% - var(--fileMenuWidth) - var(--helpTemplateWidth))' });
        this.element.classList[this.isMinimized ? 'add' : 'remove'](constants.RIBBON_MINIMIZE);
    }
    minimize(val) {
        if (val === this.isMinimized) {
            return;
        }
        const eventArgs = { cancel: false };
        this.trigger(val ? 'ribbonCollapsing' : 'ribbonExpanding', eventArgs, (args) => {
            if (args.cancel) {
                return;
            }
            this.setProperties({ isMinimized: val }, true);
            this.element.classList.toggle(constants.RIBBON_MINIMIZE, this.isMinimized);
            //to overwrite inline styles from hscroll
            this.tabObj.element.querySelector('.e-content').style.display = val ? 'none' : 'block';
            if (!val) {
                this.refreshLayout();
            }
        });
    }
    toggleLayout(args) {
        this.setProperties({ activeLayout: this.activeLayout === 'Simplified' ? 'Classic' : 'Simplified' }, true);
        this.switchLayout();
        const eventArgs = { activeLayout: this.activeLayout, event: args };
        this.trigger('ribbonLayoutSwitched', eventArgs);
    }
    tabCreated() {
        if (!this.hideLayoutSwitcher) {
            this.addExpandCollapse();
        }
        this.renderInitialTab(this.selectedTab);
    }
    ribbonTabSelected(e) {
        e.preventFocus = true;
        this.isAddRemove = false;
        const selectedTabId = e.selectedItem.getAttribute('data-id');
        let selectedIndex = getIndex(this.tabs, ((tab) => (tab.id === selectedTabId)));
        selectedIndex = selectedIndex === -1 ? this.selectedTab : selectedIndex;
        const selectedContent = this.tabObj.items[parseInt(selectedIndex.toString(), 10)].content;
        if ((!selectedContent.querySelector('.' + constants.RIBBON_GROUP)) && (this.tabs[parseInt(selectedIndex.toString(), 10)].groups.length !== 0)) {
            const elements = this.createGroups(this.tabs[parseInt(selectedIndex.toString(), 10)].groups, selectedIndex);
            append(elements, selectedContent);
        }
        const isContextual = this.isContextualTab(selectedTabId);
        this.updateSelectedState(selectedTabId);
        const eventArgs = { previousIndex: this.selectedTab, selectedIndex: selectedIndex, isContextual: isContextual };
        this.setProperties({ selectedTab: selectedIndex }, true);
        this.calculateHiddenElementsWidth(selectedIndex);
        if (this.isUpdateItems) {
            for (let i = 0; i < this.itemsModel.length; i++) {
                const item = this.itemsModel[parseInt(i.toString(), 10)];
                if (this.selectedTab === this.targetTabs[item.id]) {
                    this.updateItem(item);
                    this.itemsModel.splice(i, 1);
                    i--;
                }
            }
            if (this.itemsModel.length === 0) {
                this.isUpdateItems = false;
            }
        }
        if (this.ribbonGalleryModule) {
            this.ribbonGalleryModule.checkAvailableHeight(e.selectedContent.firstChild);
        }
        this.checkOverflow(selectedIndex, selectedContent);
        if (this.activeLayout === 'Simplified' && this.overflowDDB) {
            const overflowTarget = this.overflowDDB.target;
            const ofTabContainer = overflowTarget.querySelector('.' + constants.RIBBON_TAB_ACTIVE);
            if (ofTabContainer) {
                ofTabContainer.classList.remove(constants.RIBBON_TAB_ACTIVE);
            }
            const activeTab = overflowTarget.querySelector('#' + selectedTabId + constants.OVERFLOW_ID);
            if (activeTab) {
                activeTab.classList.add(constants.RIBBON_TAB_ACTIVE);
                this.overflowDDB.element.classList.remove(constants.HIDE_CSS);
                this.checkOverflowHiddenItems(false, selectedIndex);
            }
            else {
                this.overflowDDB.element.classList.add(constants.HIDE_CSS);
            }
        }
        this.trigger('tabSelected', eventArgs);
    }
    updateSelectedState(tabID) {
        if (this.contextualTabs.length) {
            for (let i = 0; i < this.contextualTabs.length; i++) {
                let isSelected = false;
                for (let j = 0; j < this.contextualTabs[parseInt(i.toString(), 10)].tabs.length; j++) {
                    if (this.contextualTabs[parseInt(i.toString(), 10)].tabs[parseInt(j.toString(), 10)].id === tabID) {
                        isSelected = true;
                        break;
                    }
                }
                this.contextualTabs[parseInt(i.toString(), 10)].
                    setProperties({ isSelected: isSelected }, true);
            }
        }
    }
    checkOverflow(tabIndex, activeContent) {
        const tabContent = activeContent.closest('.' + constants.TAB_CONTENT);
        const isOverFlow = tabContent.offsetWidth < activeContent.offsetWidth;
        if (isOverFlow && !this.scrollModule) {
            if (this.activeLayout === 'Classic') {
                // Defines whether the shrinking is breaked due to insufficient space.
                let isBreak = false;
                isBreak = this.checkGroupShrinking(tabIndex, tabContent, activeContent, true);
                if (!isBreak && (tabContent.offsetWidth < activeContent.offsetWidth)) {
                    isBreak = this.checkGroupShrinking(tabIndex, tabContent, activeContent, false);
                }
                if (tabContent.offsetWidth < activeContent.offsetWidth) {
                    this.createOverflowDropdown(tabIndex, tabContent, activeContent);
                }
            }
            else {
                this.checkSimplifiedItemShrinking(tabIndex, tabContent, activeContent);
                if (tabContent.offsetWidth < activeContent.offsetWidth) {
                    this.createSimplfiedOverflow(tabContent, activeContent, tabIndex);
                }
            }
            //Adds Scroll if the tabwidth is less the content width even after adding overflow dropdown.
            if ((tabContent.offsetWidth < activeContent.offsetWidth) && (!this.scrollModule)) {
                this.scrollModule = new HScroll({
                    enableRtl: this.enableRtl
                }, this.tabObj.element.querySelector('.' + constants.TAB_CONTENT));
            }
        }
        else if (!isOverFlow) {
            this.destroyScroll();
            if (this.activeLayout === 'Classic') {
                let isBreak = false;
                isBreak = this.removeOverflowDropdown(tabContent, activeContent, false, tabIndex);
                //Check for expanding small items to medium items.
                if (!isBreak && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                    isBreak = this.checkGroupExpanding(tabIndex, tabContent, activeContent, true);
                }
                //Check for expanding medium items to large items.
                if (!isBreak && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                    isBreak = this.checkGroupExpanding(tabIndex, tabContent, activeContent, false);
                }
            }
            else {
                this.removeSimplfiedOverflow(tabContent, activeContent, tabIndex);
                if (tabContent.offsetWidth > activeContent.offsetWidth) {
                    this.checkSimplifiedItemExpanding(tabIndex, tabContent, activeContent);
                }
            }
        }
        this.addTabOverflowKeyTip();
    }
    addTabOverflowKeyTip() {
        const tabOverflow = this.tabObj.element.querySelector('#_nav');
        if (tabOverflow) {
            this.keyTipElements['taboverflow'] = [];
            this.keyTipElements['taboverflow'].push({ id: tabOverflow.id, type: 'taboverflow', keyTip: '00' });
        }
        else {
            delete (this.keyTipElements['taboverflow']);
        }
    }
    checkSimplifiedItemShrinking(tabIndex, tabContent, activeContent) {
        const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
        for (let i = (tab.groups.length - 1); (i >= 0); i--) {
            const group = tab.groups[parseInt(i.toString(), 10)];
            const groupContainer = tabContent.querySelector('#' + group.id + constants.CONTAINER_ID);
            for (let j = 0; ((j < group.collections.length) && (tabContent.offsetWidth < activeContent.offsetWidth)); j++) {
                const collection = group.collections[parseInt(j.toString(), 10)];
                for (let k = collection.items.length; ((k >= 1) && (tabContent.offsetWidth < activeContent.offsetWidth)); k--) {
                    const item = collection.items[k - 1];
                    if (((item.allowedSizes & RibbonItemSize.Small) && (item.allowedSizes & RibbonItemSize.Medium))
                        && (item.activeSize === RibbonItemSize.Medium) && (item.displayOptions & DisplayMode.Simplified)) {
                        const itemContainer = groupContainer.querySelector('#' + item.id + constants.CONTAINER_ID);
                        if (itemContainer) {
                            const itemEle = itemContainer.querySelector('#' + item.id);
                            itemContainer.setAttribute('data-medium-width', activeContent.offsetWidth.toString());
                            item.setProperties({ activeSize: RibbonItemSize.Small }, true);
                            this.setItemSize(itemEle, item);
                        }
                    }
                }
            }
        }
    }
    checkSimplifiedItemExpanding(tabIndex, tabContent, activeContent) {
        const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
        for (let i = (tab.groups.length - 1); (i >= 0); i--) {
            const group = tab.groups[parseInt(i.toString(), 10)];
            const groupContainer = tabContent.querySelector('#' + group.id + constants.CONTAINER_ID);
            for (let j = 0; ((j < group.collections.length) && (tabContent.offsetWidth > activeContent.offsetWidth)); j++) {
                const collection = group.collections[parseInt(j.toString(), 10)];
                for (let k = collection.items.length; ((k >= 1) && (tabContent.offsetWidth > activeContent.offsetWidth)); k--) {
                    const item = collection.items[k - 1];
                    if (((item.allowedSizes & RibbonItemSize.Small) && (item.allowedSizes & RibbonItemSize.Medium))
                        && (item.activeSize === RibbonItemSize.Small) && (item.displayOptions & DisplayMode.Simplified)) {
                        const itemContainer = groupContainer.querySelector('#' + item.id + constants.CONTAINER_ID);
                        if (itemContainer) {
                            const valString = itemContainer.getAttribute('data-medium-width');
                            const value = valString ? parseInt(valString, 10) : null;
                            if (value && (tabContent.offsetWidth > value)) {
                                itemContainer.removeAttribute('data-medium-width');
                                const itemEle = itemContainer.querySelector('#' + item.id);
                                item.setProperties({ activeSize: RibbonItemSize.Medium }, true);
                                this.setItemSize(itemEle, item);
                            }
                        }
                    }
                }
            }
        }
    }
    createSimplfiedOverflow(tabContent, activeContent, tabIndex) {
        const orderedGroups = this.getGroupResizeOrder(true, tabIndex);
        let isEmptyCollection;
        for (let i = 0; ((i < orderedGroups.length) && (tabContent.offsetWidth < activeContent.offsetWidth)); i++) {
            let isGroupUpdated = false;
            const group = orderedGroups[parseInt(i.toString(), 10)];
            const groupEle = tabContent.querySelector('#' + group.id);
            const groupContainer = groupEle.querySelector('#' + group.id + constants.CONTAINER_ID);
            for (let j = group.collections.length; ((j >= 1) && (tabContent.offsetWidth < activeContent.offsetWidth)); j--) {
                const collection = group.collections[parseInt((j - 1).toString(), 10)];
                const collectionEle = groupEle.querySelector('#' + collection.id);
                for (let k = collection.items.length; ((k >= 1) && (tabContent.offsetWidth < activeContent.offsetWidth)); k--) {
                    const item = collection.items[k - 1];
                    const itemContainer = collectionEle.querySelector('#' + item.id + constants.CONTAINER_ID);
                    if (((item.displayOptions === DisplayMode.Auto) ||
                        (item.displayOptions === (DisplayMode.Simplified | DisplayMode.Overflow))) && !isNullOrUndefined(itemContainer)) {
                        let groupHidden = false;
                        let itemHidden = false;
                        let isAllItemHidden = false;
                        let isEmptyCollection = false;
                        let groupItems;
                        if (groupEle.classList.contains('e-hidden') || groupEle.classList.contains('e-hide-group')) {
                            groupItems = groupEle.querySelectorAll('.e-ribbon-item.e-hidden');
                            if (groupItems.length) {
                                for (let i = 0; i < groupItems.length; i++) {
                                    groupItems[parseInt(i.toString(), 10)].classList.remove('e-hidden');
                                }
                            }
                            if (groupEle.classList.contains('e-hide-group')) {
                                isAllItemHidden = true;
                                groupEle.classList.remove('e-hide-group');
                                groupEle.classList.remove('e-ribbon-emptyCollection');
                                if (this.hiddenGroups.indexOf(groupEle.id) !== -1) {
                                    this.hiddenGroups.splice(this.hiddenGroups.indexOf(groupEle.id), 1);
                                }
                            }
                            else {
                                groupHidden = true;
                                groupEle.classList.remove('e-hidden');
                            }
                            if (!isGroupUpdated) {
                                this.calculateOverflowItemsWidth(groupEle.offsetWidth, false, tabIndex);
                                this.calculateMediumDataWidth(groupEle.offsetWidth, tabIndex, false);
                                isGroupUpdated = true;
                            }
                        }
                        else {
                            if (itemContainer.classList.contains('e-hidden')) {
                                itemHidden = true;
                                itemContainer.classList.remove('e-hidden');
                                if (groupEle.classList.contains('e-ribbon-emptyCollection')) {
                                    isEmptyCollection = true;
                                    groupEle.classList.remove('e-ribbon-emptyCollection');
                                }
                                this.calculateOverflowItemsWidth(itemContainer.offsetWidth, false, tabIndex);
                                this.calculateMediumDataWidth(itemContainer.offsetWidth, tabIndex, false);
                            }
                        }
                        itemContainer.setAttribute('data-simplified-width', (activeContent.offsetWidth).toString());
                        if (itemHidden) {
                            itemContainer.classList.add('e-hidden');
                        }
                        if (groupItems && groupItems.length) {
                            for (let i = 0; i < groupItems.length; i++) {
                                groupItems[parseInt(i.toString(), 10)].classList.add('e-hidden');
                            }
                        }
                        if (groupHidden) {
                            groupEle.classList.add('e-hidden');
                        }
                        if (isAllItemHidden) {
                            groupEle.classList.add('e-hide-group');
                            groupEle.classList.add('e-ribbon-emptyCollection');
                        }
                        if (isEmptyCollection) {
                            groupEle.classList.add('e-ribbon-emptyCollection');
                        }
                        this.createOverflowPopup(item, tabIndex, group.enableGroupOverflow, group.id, group.header, itemContainer, groupContainer, true);
                        if (item.activeSize === RibbonItemSize.Small) {
                            const itemEle = itemContainer.querySelector('#' + item.id);
                            item.setProperties({ activeSize: RibbonItemSize.Medium }, true);
                            this.setItemSize(itemEle, item);
                        }
                        if ((item.type === RibbonItemType.DropDown) || (item.type === RibbonItemType.SplitButton) ||
                            (item.type === RibbonItemType.GroupButton) || (item.type === RibbonItemType.Gallery)) {
                            this.updatePopupItems(item, itemContainer, group.enableGroupOverflow, true);
                        }
                    }
                }
            }
            if (!(group.enableGroupOverflow || groupEle.querySelector('.' + constants.RIBBON_ITEM))) {
                groupEle.classList.add('e-ribbon-emptyCollection');
            }
            const itemsLength = groupEle.querySelectorAll('.' + constants.RIBBON_ITEM);
            if (itemsLength && !group.enableGroupOverflow) {
                isEmptyCollection = this.checkEmptyCollection(itemsLength);
                if (isEmptyCollection) {
                    groupEle.classList.add('e-ribbon-emptyCollection');
                }
            }
            this.checkOverflowHiddenItems(group.enableGroupOverflow, tabIndex, group.id);
        }
    }
    checkEmptyCollection(itemsLength) {
        let isEmptyCollection = true;
        for (let i = 0; i < itemsLength.length; i++) {
            if (!(itemsLength[parseInt(i.toString(), 10)].classList.contains('e-hidden'))) {
                isEmptyCollection = false;
                break;
            }
        }
        return isEmptyCollection;
    }
    updatePopupItems(item, itemEle, isGroupOF, isMenu) {
        const dropdown = getComponent(itemEle.querySelector('#' + item.id), (item.type === RibbonItemType.DropDown || item.type === RibbonItemType.Gallery || item.type === RibbonItemType.GroupButton) ? DropDownButton : SplitButton);
        const dropDownPopup = dropdown.dropDown;
        // popup is on right if (isGroupOF && isMenu)
        // The position is reversed if RTL is enabled.
        // isRight = ((isGroupOF && isMenu) && !this.enableRtl ) || (!(isGroupOF && isMenu) && this.enableRtl)  ==> (isGroupOF && isMenu) !== this.enableRtl
        const isLeft = (isGroupOF && isMenu) === this.enableRtl;
        if (dropDownPopup) {
            dropDownPopup.setProperties({ position: { X: isLeft ? 'left' : 'right', Y: isMenu ? 'top' : 'bottom' } }, true);
            if (isMenu) {
                dropdown.beforeOpen = (e) => {
                    if (isLeft) {
                        if (item.type === RibbonItemType.Gallery && this.ribbonGalleryModule) {
                            this.ribbonGalleryModule.checkCollision(dropDownPopup, dropDownPopup.element);
                        }
                        dropDownPopup.element.style.setProperty('visibility', 'hidden');
                        dropDownPopup.element.style.setProperty('display', 'block');
                        dropDownPopup.setProperties({ offsetX: -1 * dropDownPopup.element.offsetWidth });
                        dropDownPopup.element.style.removeProperty('display');
                        dropDownPopup.element.style.removeProperty('visibility');
                    }
                    if (item.splitButtonSettings.beforeOpen) {
                        item.splitButtonSettings.beforeOpen.call(this, e);
                    }
                    if (item.dropDownSettings.beforeOpen) {
                        item.dropDownSettings.beforeOpen.call(this, e);
                    }
                };
            }
            else {
                dropDownPopup.setProperties({ offsetX: 0 }, true);
                dropdown.beforeOpen = null;
            }
        }
    }
    removeSimplfiedOverflow(tabContent, activeContent, tabIndex, isClear = false) {
        const orderedGroups = this.getGroupResizeOrder(false, tabIndex);
        let flag = true;
        let isEmptyCollection;
        for (let i = 0; ((i < orderedGroups.length) && flag); i++) {
            const group = orderedGroups[parseInt(i.toString(), 10)];
            let overflowDDB;
            let overflowtarget;
            if (group.enableGroupOverflow) {
                const overflowDDBEle = tabContent.querySelector('#' + group.id + constants.GROUPOF_BUTTON_ID);
                if (overflowDDBEle) {
                    overflowDDB = getInstance(overflowDDBEle, DropDownButton);
                    overflowtarget = overflowDDB.target;
                }
            }
            else {
                overflowDDB = this.overflowDDB;
                overflowtarget = this.overflowDDB ? this.overflowDDB.target : null;
            }
            for (let j = 0; ((j < group.collections.length) && flag); j++) {
                const collection = group.collections[parseInt(j.toString(), 10)];
                // eslint-disable-next-line max-len
                for (let k = 0; ((k < collection.items.length) && flag && !isClear && (tabContent.offsetWidth > activeContent.offsetWidth)); k++) {
                    const item = collection.items[parseInt(k.toString(), 10)];
                    let itemContainer;
                    if (overflowtarget) {
                        itemContainer = overflowtarget.querySelector('#' + item.id + constants.CONTAINER_ID);
                    }
                    if (((item.displayOptions === DisplayMode.Auto) ||
                        (item.displayOptions === (DisplayMode.Simplified | DisplayMode.Overflow))) && !isNullOrUndefined(itemContainer)) {
                        let width = parseInt(itemContainer.getAttribute('data-simplified-width'), 10);
                        const groupItemEle = tabContent.querySelector('#' + group.id);
                        if (itemContainer.classList.contains('e-hidden') || groupItemEle.classList.contains('e-hidden')) {
                            width = Math.abs(width - activeContent.offsetWidth);
                        }
                        if (!isClear && (tabContent.offsetWidth < width)) {
                            flag = false;
                            break;
                        }
                        const groupEle = tabContent.querySelector('#' + collection.id);
                        if ((item.type === RibbonItemType.DropDown) || (item.type === RibbonItemType.SplitButton) ||
                            (item.type === RibbonItemType.GroupButton) || (item.type === RibbonItemType.Gallery)) {
                            this.updatePopupItems(item, itemContainer, group.enableGroupOverflow, false);
                        }
                        groupEle.append(itemContainer);
                        if (itemContainer.classList.contains('e-hidden') || groupItemEle.classList.contains('e-hidden')) {
                            itemContainer.setAttribute('data-simplified-width', width.toString());
                            let isGroupHidden = false;
                            let widthDifference = 0;
                            if (itemContainer.classList.contains('e-hidden')) {
                                itemContainer.classList.remove('e-hidden');
                                if (groupItemEle.classList.contains('e-hide-group')) {
                                    isGroupHidden = true;
                                    widthDifference = this.checkWidthDifference(itemContainer, groupItemEle);
                                }
                                width = itemContainer.offsetWidth + widthDifference;
                                itemContainer.classList.add('e-hidden');
                            }
                            this.calculateOverflowItemsWidth(width, true, tabIndex);
                            this.calculateMediumDataWidth(width, tabIndex, true);
                            if (isGroupHidden) {
                                groupItemEle.classList.add('e-hide-group');
                                groupItemEle.classList.add('e-ribbon-emptyCollection');
                            }
                        }
                        this.removeOverflowEvent(item, itemContainer);
                        if (item.allowedSizes & RibbonItemSize.Small) {
                            item.setProperties({ activeSize: RibbonItemSize.Small }, true);
                            this.setItemSize(itemContainer.querySelector('#' + item.id), item);
                        }
                        const groupElement = tabContent.querySelector('#' + group.id);
                        const itemEle = groupElement.querySelector('.' + constants.RIBBON_ITEM);
                        if (groupElement.classList.contains('e-ribbon-emptyCollection') && itemEle !== null) {
                            const itemsLength = groupElement.querySelectorAll('.' + constants.RIBBON_ITEM);
                            if (itemsLength) {
                                isEmptyCollection = this.checkEmptyCollection(itemsLength);
                                if (!isEmptyCollection) {
                                    groupElement.classList.remove('e-ribbon-emptyCollection');
                                }
                            }
                        }
                    }
                }
            }
            if (overflowDDB) {
                if (group.enableGroupOverflow) {
                    if (overflowtarget.childElementCount === 0 ||
                        (overflowtarget.childElementCount === 1 && this.isHeaderVisible(overflowtarget, group.id))) {
                        this.removeOverflowButton(overflowDDB);
                    }
                }
                else {
                    const ofGroupContainer = overflowtarget.querySelector('#' + group.id + constants.CONTAINER_ID);
                    if (ofGroupContainer && ofGroupContainer.childElementCount === 1) {
                        ofGroupContainer.remove();
                    }
                    const ofTabContainer = overflowtarget.querySelector('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                    if (ofTabContainer && ofTabContainer.childElementCount === 0) {
                        ofTabContainer.remove();
                        this.overflowDDB.element.classList.add(constants.HIDE_CSS);
                    }
                }
            }
        }
        for (let i = 0; i < orderedGroups.length; i++) {
            this.checkOverflowHiddenItems(orderedGroups[parseInt(i.toString(), 10)].enableGroupOverflow, tabIndex, orderedGroups[parseInt(i.toString(), 10)].id);
        }
        if (this.overflowDDB) {
            const overflowEle = this.overflowDDB.target;
            if (overflowEle.childElementCount === 0) {
                this.removeOverflowButton(this.overflowDDB);
                this.overflowDDB = null;
            }
        }
    }
    checkOverflowHiddenItems(isGroupOF, tabIndex, groupId) {
        if (isGroupOF) {
            const overflowDDB = document.querySelector('#' + groupId + constants.GROUPOF_BUTTON_ID);
            if (overflowDDB) {
                const overflowButton = getInstance(overflowDDB, DropDownButton);
                const overflowBtnTarget = overflowButton.target;
                const itemEle = overflowBtnTarget.querySelectorAll('.e-ribbon-item');
                let isHidden = true;
                for (let i = 0; i < itemEle.length; i++) {
                    if (!(itemEle[parseInt(i.toString(), 10)].classList.contains('e-hidden'))) {
                        isHidden = false;
                        break;
                    }
                }
                overflowButton.element.classList[isHidden ? 'add' : 'remove']('e-hidden');
            }
        }
        else {
            if (this.overflowDDB) {
                let isGroupHidden = true;
                let isItemHidden;
                const overflowEle = this.overflowDDB.target;
                const ofTabContainer = overflowEle.querySelector('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                if (ofTabContainer) {
                    for (let k = 0; k < ofTabContainer.children.length; k++) {
                        isItemHidden = true;
                        const overflowTab = ofTabContainer.children[parseInt(k.toString(), 10)];
                        const groupTabContainer = overflowTab.querySelectorAll('.e-ribbon-item');
                        for (let n = 0; n < groupTabContainer.length; n++) {
                            if (!(groupTabContainer[parseInt(n.toString(), 10)].classList.contains('e-hidden'))) {
                                isItemHidden = false;
                                break;
                            }
                        }
                        overflowTab.classList[isItemHidden ? 'add' : 'remove']('e-hide-group');
                        if (!(overflowTab.classList.contains('e-hide-group')) && !(overflowTab.classList.contains('e-hidden'))) {
                            isGroupHidden = false;
                        }
                    }
                    this.overflowDDB.element.classList[isGroupHidden ? 'add' : 'remove'](constants.HIDE_CSS);
                }
            }
        }
    }
    createOverflowPopup(item, tabIndex, isGroupOF, groupId, groupHeader, itemEle, groupContainer, isResize) {
        let overflowButton;
        let overflowtarget;
        const itemProp = getGroup(this.tabs, groupId);
        const contentEle = this.tabObj.items[parseInt(tabIndex.toString(), 10)].content;
        const groupEle = contentEle.querySelector('#' + groupId);
        if (isGroupOF) {
            const overflowDDB = groupContainer.querySelector('#' + groupId + constants.GROUPOF_BUTTON_ID);
            if (!overflowDDB) {
                overflowButton = this.addOverflowButton(groupId + constants.GROUPOF_BUTTON_ID, isGroupOF);
                overflowButton.element.classList.add(constants.RIBBON_GROUP_OF_BUTTON);
                groupContainer.appendChild(overflowButton.element);
            }
            else {
                overflowButton = getInstance(overflowDDB, DropDownButton);
            }
            this.addKeyTip(tabIndex, '0' + (itemProp.groupIndex + 1), overflowButton.element.id, 'grpofbtn');
            overflowtarget = overflowButton.target;
            const overflowBtnTarget = overflowButton.target;
            const headerEle = overflowBtnTarget.querySelector('#' + groupId + constants.GROUPOF_BUTTON_ID + constants.HEADER_ID);
            if (!headerEle) {
                if (itemProp.group.overflowHeader) {
                    const groupHeader = this.createElement('div', {
                        className: constants.RIBBON_OVERFLOW_HEADER,
                        id: groupId + constants.GROUPOF_BUTTON_ID + constants.HEADER_ID,
                        innerHTML: itemProp.group.overflowHeader
                    });
                    overflowBtnTarget.append(groupHeader);
                }
            }
            if (groupEle) {
                if (groupEle.classList.contains('e-disabled')) {
                    overflowBtnTarget.classList.add('e-disabled');
                }
                if (groupEle.classList.contains('e-hidden')) {
                    overflowBtnTarget.classList.add('e-hidden');
                }
                if (groupEle.classList.contains('e-hide-group')) {
                    overflowBtnTarget.classList.add('e-hide-group');
                }
            }
            if (isResize) {
                overflowBtnTarget.insertBefore(itemEle, overflowBtnTarget.querySelector('.' + constants.RIBBON_ITEM));
            }
            else {
                overflowBtnTarget.append(itemEle);
            }
        }
        else {
            if (!this.overflowDDB) {
                this.overflowDDB = this.addOverflowButton(this.tabObj.element.id + constants.OVRLOF_BUTTON_ID, isGroupOF);
                this.tabObj.element.insertBefore(this.overflowDDB.element, this.collapseButton);
                this.overflowDDB.element.classList.add(constants.RIBBON_OVERALL_OF_BUTTON);
                this.createOfTabContainer(groupId, groupHeader, itemEle, tabIndex);
            }
            else {
                this.overflowDDB.element.classList.remove(constants.HIDE_CSS);
                const overflowEle = this.overflowDDB.target;
                const ofTabContainer = overflowEle.querySelector('#' +
                    this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                if (ofTabContainer) {
                    let ofGroupContainer = overflowEle.querySelector('#' + groupId + constants.CONTAINER_ID);
                    if (!ofGroupContainer) {
                        ofGroupContainer = itemProp.group.overflowHeader ? this.createGroupContainer(groupId, itemProp.group.overflowHeader) : this.createGroupContainer(groupId, groupHeader);
                        if (groupEle) {
                            if (groupEle.classList.contains('e-disabled')) {
                                ofGroupContainer.classList.add('e-disabled');
                            }
                            if (groupEle.classList.contains('e-hidden')) {
                                ofGroupContainer.classList.add('e-hidden');
                            }
                            if (groupEle.classList.contains('e-hide-group')) {
                                ofGroupContainer.classList.add('e-hide-group');
                            }
                        }
                        ofTabContainer.append(ofGroupContainer);
                    }
                    if (isResize) {
                        ofGroupContainer.insertBefore(itemEle, ofGroupContainer.querySelector('.' + constants.RIBBON_ITEM));
                    }
                    else {
                        ofGroupContainer.append(itemEle);
                    }
                }
                else {
                    this.createOfTabContainer(groupId, groupHeader, itemEle, tabIndex);
                }
            }
            overflowButton = this.overflowDDB;
            overflowtarget = this.overflowDDB ? this.overflowDDB.target : null;
        }
        if (itemEle !== null) {
            this.addOverflowEvents(item, itemEle, overflowButton);
        }
        if (overflowtarget) {
            if (item.keyTip) {
                this.addKeyTip(tabIndex, item.keyTip, item.id, 'popupitem');
            }
        }
    }
    addOverflowEvents(item, itemEle, overflowButton) {
        switch (item.type) {
            case 'Button':
                this.ribbonButtonModule.addOverFlowEvents(item, itemEle, overflowButton);
                break;
            case 'DropDown':
                this.ribbonDropDownModule.addOverFlowEvents(item, itemEle, overflowButton);
                break;
            case 'SplitButton':
                this.ribbonSplitButtonModule.addOverFlowEvents(item, itemEle, overflowButton);
                break;
            case 'CheckBox':
                this.ribbonCheckBoxModule.addOverFlowEvents(item, itemEle, overflowButton);
                break;
            case 'ColorPicker':
                this.ribbonColorPickerModule.addOverFlowEvents(item, itemEle, overflowButton);
                break;
            case 'ComboBox':
                this.ribbonComboBoxModule.addOverFlowEvents(item, itemEle, overflowButton);
                break;
            case 'Gallery':
                if (this.activeLayout === 'Simplified') {
                    this.ribbonGalleryModule.addOverFlowEvents(item, itemEle);
                }
                break;
            case 'GroupButton':
                this.ribbonGroupButtonModule.addOverFlowEvents(item, itemEle, overflowButton);
                break;
        }
    }
    createOfTabContainer(groupId, groupHeader, itemEle, tabIndex) {
        const ofTabContainer = this.createElement('div', {
            id: this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID,
            className: constants.RIBBON_OF_TAB_CONTAINER
        });
        const overflowtarget = this.overflowDDB.target;
        overflowtarget.append(ofTabContainer);
        const itemProp = getGroup(this.tabs, groupId);
        const ofGroupContainer = itemProp.group.overflowHeader ?
            this.createGroupContainer(groupId, itemProp.group.overflowHeader) : this.createGroupContainer(groupId, groupHeader);
        ofGroupContainer.append(itemEle);
        ofTabContainer.append(ofGroupContainer);
        if (tabIndex === this.selectedTab) {
            ofTabContainer.classList.add(constants.RIBBON_TAB_ACTIVE);
        }
        const groupEle = document.querySelector('#' + groupId);
        if (groupEle) {
            if (groupEle.classList.contains('e-disabled')) {
                ofGroupContainer.classList.add('e-disabled');
            }
            if (groupEle.classList.contains('e-hidden')) {
                ofGroupContainer.classList.add('e-hidden');
            }
            if (groupEle.classList.contains('e-hide-group')) {
                ofGroupContainer.classList.add('e-hide-group');
            }
        }
    }
    checkGroupShrinking(tabIndex, tabContent, activeContent, isLarge) {
        let isOverFlow = true;
        let isBreak = false;
        const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
        for (let j = (tab.groups.length - 1); (isOverFlow && (j >= 0)); j--) {
            // eslint-disable-next-line max-len
            isBreak = isLarge ? this.checkLargeToMedium(tabIndex, tab, j, tabContent, activeContent) : this.checkMediumToSmall(tabIndex, tab, j, tabContent, activeContent);
            isOverFlow = !isBreak && (tabContent.offsetWidth < activeContent.offsetWidth);
        }
        return isBreak;
    }
    checkValidCollectionLength(collections) {
        let count = 0;
        for (let i = 0; i < collections.length; i++) {
            const items = collections[parseInt(i.toString(), 10)].items;
            for (let ind = 0; ind < items.length; ind++) {
                if (items[parseInt(ind.toString(), 10)].displayOptions & DisplayMode.Classic) {
                    count++;
                    break;
                }
            }
            if (count > 1) {
                return false;
            }
        }
        return count === 1;
    }
    checkClassicCollection(collections, n, isIncrement) {
        const items = collections[parseInt(n.toString(), 10)].items;
        for (let ind = 0; ind < items.length; ind++) {
            if (items[parseInt(ind.toString(), 10)].displayOptions & DisplayMode.Classic) {
                return n;
            }
        }
        n = isIncrement ? n + 1 : n - 1;
        if (isIncrement) {
            return (n === collections.length) ? n : this.checkClassicCollection(collections, n, isIncrement);
        }
        else {
            return (n < 0) ? n : this.checkClassicCollection(collections, n, isIncrement);
        }
    }
    checkClassicItem(items, n, isIncrement) {
        const item = items[parseInt(n.toString(), 10)];
        if (item.displayOptions & DisplayMode.Classic) {
            return n;
        }
        n = isIncrement ? n + 1 : n - 1;
        if (isIncrement) {
            return (n === items.length) ? n : this.checkClassicItem(items, n, isIncrement);
        }
        else {
            return (n < 0) ? n : this.checkClassicItem(items, n, isIncrement);
        }
    }
    checkLargeToMedium(tabIndex, tab, groupIndex, tabContent, activeContent, shouldSkip = false) {
        const group = tab.groups[parseInt(groupIndex.toString(), 10)];
        if (group.isCollapsed && !shouldSkip) {
            return false;
        }
        const canReduceCollection = (collection) => {
            return (collection.items.length === 1) && canReduceItem(collection.items[0]);
        };
        const canReduceItem = (item) => {
            return (item.allowedSizes & RibbonItemSize.Medium) && (item.activeSize === RibbonItemSize.Large);
        };
        const createShrinkEle = (id, firstItem, start, end) => {
            const shrinkEle = this.createElement('div', {
                className: 'e-ribbon-shrink' + constants.SPACE + constants.RIBBON_ROW,
                id: id + '_shrink_container' + start,
                attrs: { 'data-start': start.toString(), 'data-end': end.toString() }
            });
            firstItem.parentElement.insertBefore(shrinkEle, firstItem);
            if (!shouldSkip) {
                shrinkEle.setAttribute('data-large-width', activeContent.offsetWidth.toString());
            }
            return shrinkEle;
        };
        const moveItemToColumn = (start, end) => {
            const collection = this.tabs[parseInt(tabIndex.toString(), 10)]
                .groups[parseInt(groupIndex.toString(), 10)].collections[0];
            const firstItem = activeContent.querySelector('#' + collection.items[parseInt(start.toString(), 10)].id + constants.CONTAINER_ID);
            const shrinkEle = shouldSkip ? activeContent.querySelector('#' + collection.id + '_shrink_container' + start) :
                createShrinkEle(collection.id, firstItem, start, end);
            for (let i = start; i <= end; i++) {
                const item = collection.items[parseInt(i.toString(), 10)];
                if (!(item.displayOptions & DisplayMode.Classic)) {
                    continue;
                }
                const ele = activeContent.querySelector('#' + item.id + constants.CONTAINER_ID);
                shrinkEle.appendChild(ele);
                item.setProperties({ activeSize: RibbonItemSize.Medium }, true);
                if (item.type === RibbonItemType.GroupButton && this.activeLayout === 'Classic') {
                    this.setItemSize(ele.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID), item);
                }
                else {
                    this.setItemSize(ele.querySelector('#' + item.id), item);
                }
            }
        };
        const moveCollectionToColumn = (start, end) => {
            const group = this.tabs[parseInt(tabIndex.toString(), 10)]
                .groups[parseInt(groupIndex.toString(), 10)];
            const firstItem = activeContent.querySelector('#' + group.collections[parseInt(start.toString(), 10)].id);
            const shrinkEle = shouldSkip ? activeContent.querySelector('#' + group.id + '_shrink_container' + start) :
                createShrinkEle(group.id, firstItem, start, end);
            for (let i = start; i <= end; i++) {
                const collection = group.collections[parseInt(i.toString(), 10)];
                const ele = activeContent.querySelector('#' + collection.id);
                shrinkEle.appendChild(ele);
                collection.items[0].setProperties({ activeSize: RibbonItemSize.Medium }, true);
                if (collection.items[0].type === RibbonItemType.GroupButton && this.activeLayout === 'Classic') {
                    this.setItemSize(ele.querySelector('#' + collection.items[0].id + constants.RIBBON_GROUP_BUTTON_ID), collection.items[0]);
                }
                else {
                    this.setItemSize(ele.querySelector('#' + collection.items[0].id), collection.items[0]);
                }
            }
        };
        const orientation = group.orientation;
        if (orientation === ItemOrientation.Column) {
            for (let k = (group.collections.length - 1); k > 0; k--) {
                //to avoid negative index while checking for the second collection
                k = this.checkClassicCollection(group.collections, k, false);
                let l = k - 1;
                //Checks the element rendered at position n
                if ((l >= 0) && canReduceCollection(group.collections[parseInt(k.toString(), 10)])) {
                    l = this.checkClassicCollection(group.collections, l, false);
                    //Checks the element rendered at position n-1
                    if ((l >= 0) && canReduceCollection(group.collections[parseInt(l.toString(), 10)])) {
                        let m = l - 1;
                        if (m >= 0) {
                            m = this.checkClassicCollection(group.collections, m, false);
                        }
                        //Checks the element rendered at position n-2
                        if ((m >= 0) && canReduceCollection(group.collections[parseInt(m.toString(), 10)])) {
                            moveCollectionToColumn(m, k);
                        }
                        else {
                            moveCollectionToColumn(l, k);
                        }
                        k = m;
                        if (!shouldSkip && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                            return true;
                        }
                    }
                    else {
                        k = l;
                    }
                }
            }
        }
        else {
            if (this.checkValidCollectionLength(group.collections)) {
                const collection = group.collections[0];
                for (let k = (collection.items.length - 1); k > 0; k--) {
                    //to avoid negative index while checking for the second item
                    k = this.checkClassicItem(collection.items, k, false);
                    let l = k - 1;
                    //Checks the element rendered at position n
                    if ((l >= 0) && canReduceItem(collection.items[parseInt(k.toString(), 10)])) {
                        l = this.checkClassicItem(collection.items, l, false);
                        //Checks the element rendered at position n-1
                        if ((l >= 0) && canReduceItem(collection.items[parseInt(l.toString(), 10)])) {
                            let m = l - 1;
                            //Checks the element rendered at position n-2
                            if (m >= 0) {
                                m = this.checkClassicItem(collection.items, m, false);
                            }
                            if ((m >= 0) && canReduceItem(collection.items[parseInt(m.toString(), 10)])) {
                                moveItemToColumn(m, k);
                            }
                            else {
                                moveItemToColumn(l, k);
                            }
                            k = m;
                            if (!shouldSkip && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                                return true;
                            }
                        }
                        else {
                            k = l;
                        }
                    }
                }
            }
        }
        return false;
    }
    checkMediumToSmall(tabIndex, tab, groupIndex, tabContent, activeContent, shouldSkip = false) {
        const group = tab.groups[parseInt(groupIndex.toString(), 10)];
        if (group.isCollapsed && !shouldSkip) {
            return false;
        }
        const orientation = group.orientation;
        const ele = activeContent.querySelector('#' + group.id);
        const shrinkColumns = ele.querySelectorAll('.' + 'e-ribbon-shrink');
        const canReduceItem = (item) => {
            return (item.allowedSizes & RibbonItemSize.Small) && (item.activeSize === RibbonItemSize.Medium);
        };
        const reduceItemsToSmall = (collectionIndex, start, end, middle = null) => {
            const collection = this.tabs[parseInt(tabIndex.toString(), 10)]
                .groups[parseInt(groupIndex.toString(), 10)].collections[parseInt(collectionIndex.toString(), 10)];
            const reduce = (i) => {
                const item = collection.items[parseInt(i.toString(), 10)];
                if (item.displayOptions & DisplayMode.Classic) {
                    let ele = activeContent.querySelector('#' + item.id);
                    item.setProperties({ activeSize: RibbonItemSize.Small }, true);
                    if (item.type === RibbonItemType.GroupButton) {
                        ele = activeContent.querySelector('#' + item.id + '_grpbtn');
                    }
                    this.setItemSize(ele, item);
                }
            };
            reduce(start);
            if (middle) {
                reduce(middle);
            }
            reduce(end);
        };
        const reduceCollectionsToSmall = (index, start, end, middle = null) => {
            const group = this.tabs[parseInt(tabIndex.toString(), 10)]
                .groups[parseInt(groupIndex.toString(), 10)];
            if (!shouldSkip) {
                shrinkColumns[parseInt(index.toString(), 10)].setAttribute('data-medium-width', activeContent.offsetWidth.toString());
            }
            const reduce = (i) => {
                const collection = group.collections[parseInt(i.toString(), 10)];
                if (collection.items[0].displayOptions & DisplayMode.Classic) {
                    let ele = activeContent.querySelector('#' + collection.items[0].id);
                    collection.items[0].setProperties({ activeSize: RibbonItemSize.Small }, true);
                    if (collection.items[0].type === RibbonItemType.GroupButton) {
                        ele = activeContent.querySelector('#' + collection.items[0].id + constants.RIBBON_GROUP_BUTTON_ID);
                    }
                    this.setItemSize(ele, collection.items[0]);
                }
            };
            reduce(start);
            if (middle) {
                reduce(middle);
            }
            reduce(end);
        };
        const setWidth = (id) => {
            if (!shouldSkip) {
                const ele = activeContent.querySelector('#' + id);
                ele.setAttribute('data-medium-width', activeContent.offsetWidth.toString());
            }
        };
        if (orientation === ItemOrientation.Column) {
            if (shrinkColumns.length > 0) {
                for (let k = (shrinkColumns.length - 1); k >= 0; k--) {
                    const start = parseInt(shrinkColumns[parseInt(k.toString(), 10)].getAttribute('data-start'), 10);
                    const end = parseInt(shrinkColumns[parseInt(k.toString(), 10)].getAttribute('data-end'), 10);
                    //only 2 or 3 itmes alone can be present in shrinked column
                    const l = this.checkClassicCollection(group.collections, start + 1, false); //next valid item
                    if (canReduceItem(group.collections[parseInt(start.toString(), 10)].items[0])
                        && canReduceItem(group.collections[parseInt(l.toString(), 10)].items[0])) {
                        if (end === l) { //if only 2 item, then next valid item will be the end item, else check for 3 rd item satus.
                            reduceCollectionsToSmall(k, start, end);
                        }
                        else if (canReduceItem(group.collections[parseInt(end.toString(), 10)].items[0])) {
                            reduceCollectionsToSmall(k, start, end, l);
                        }
                        if (!shouldSkip && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                            return true;
                        }
                    }
                }
            }
            for (let k = (group.collections.length - 1); k >= 0; k--) {
                const collection = group.collections[parseInt(k.toString(), 10)];
                const classicItems = [];
                for (let i = 0; i < collection.items.length; i++) {
                    if (collection.items[parseInt(i.toString(), 10)].displayOptions & DisplayMode.Classic) {
                        classicItems.push(i);
                    }
                }
                //If items length is 1 then, it would have been already check for shrinked column
                if ((classicItems.length > 1)) {
                    if (canReduceItem(collection.items[classicItems[0]]) && canReduceItem(collection.items[classicItems[1]])) {
                        if (classicItems.length === 2) {
                            setWidth(collection.id);
                            reduceItemsToSmall(k, classicItems[0], classicItems[1]);
                        }
                        else if (canReduceItem(collection.items[classicItems[2]])) {
                            setWidth(collection.id);
                            reduceItemsToSmall(k, classicItems[0], classicItems[2], classicItems[1]);
                        }
                        if (!shouldSkip && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                            return true;
                        }
                    }
                }
            }
        }
        else {
            if (this.checkValidCollectionLength(group.collections)) {
                if (shrinkColumns.length > 0) {
                    for (let k = (shrinkColumns.length - 1); k >= 0; k--) {
                        const shrinkColumn = shrinkColumns[parseInt(k.toString(), 10)];
                        const start = parseInt(shrinkColumn.getAttribute('data-start'), 10);
                        const end = parseInt(shrinkColumn.getAttribute('data-end'), 10);
                        //only 2 or 3 itmes alone can be present in shrinked column
                        const collection = group.collections[0];
                        const l = this.checkClassicItem(collection.items, start + 1, false); //next valid item
                        if (canReduceItem(group.collections[0].items[parseInt(start.toString(), 10)])
                            && canReduceItem(group.collections[0].items[parseInt(l.toString(), 10)])) {
                            if (end === l) { //if only 2 item, then next valid item will be the end item, else check for 3 rd item satus.
                                setWidth(shrinkColumn.id);
                                reduceItemsToSmall(0, start, end);
                            }
                            else if (canReduceItem(group.collections[0].items[parseInt(end.toString(), 10)])) {
                                setWidth(shrinkColumn.id);
                                reduceItemsToSmall(0, start, end, l);
                            }
                            if (!shouldSkip && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                                return true;
                            }
                        }
                    }
                }
            }
            else {
                for (let k = (group.collections.length - 1); k >= 0; k--) {
                    const collection = group.collections[parseInt(k.toString(), 10)];
                    for (let l = (collection.items.length - 1); l >= 0; l--) {
                        l = this.checkClassicItem(collection.items, l, false);
                        if (l < 0) {
                            continue;
                        }
                        const item = collection.items[parseInt(l.toString(), 10)];
                        if (canReduceItem(item)) {
                            if (item.type !== RibbonItemType.GroupButton) {
                                setWidth(item.id);
                            }
                            else {
                                setWidth(item.id + constants.RIBBON_GROUP_BUTTON_ID);
                            }
                            reduceItemsToSmall(k, l, l);
                            if (!shouldSkip && (tabContent.offsetWidth > activeContent.offsetWidth)) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    }
    checkGroupExpanding(tabIndex, tabContent, activeContent, isSmall) {
        let isBreak = false;
        const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
        for (let j = 0; (!isBreak && (j < tab.groups.length)); j++) {
            isBreak = isSmall ? this.checkSmallToMedium(tabIndex, tab, j, tabContent, activeContent, false, true) :
                this.checkMediumToLarge(tabIndex, tab, j, tabContent, activeContent, false, true);
        }
        return isBreak;
    }
    // eslint-disable-next-line max-len
    checkSmallToMedium(tabIndex, tab, groupIndex, tabContent, activeContent, shouldSkip, shouldClear) {
        const group = tab.groups[parseInt(groupIndex.toString(), 10)];
        const orientation = group.orientation;
        const ele = activeContent.querySelector('#' + group.id);
        const shrinkColumns = ele.querySelectorAll('.' + 'e-ribbon-shrink');
        const canExpandItem = (item) => {
            return (item.allowedSizes & RibbonItemSize.Medium) && (item.activeSize === RibbonItemSize.Small);
        };
        const expandItemToMedium = (collectionIndex, index, parentEle) => {
            const collection = this.tabs[parseInt(tabIndex.toString(), 10)]
                .groups[parseInt(groupIndex.toString(), 10)].collections[parseInt(collectionIndex.toString(), 10)];
            const item = collection.items[parseInt(index.toString(), 10)];
            if (item.displayOptions & DisplayMode.Classic) {
                let ele = parentEle.id === item.id ? parentEle : parentEle.querySelector('#' + item.id);
                item.setProperties({ activeSize: RibbonItemSize.Medium }, true);
                if (item.type === RibbonItemType.GroupButton) {
                    ele = document.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID);
                }
                this.setItemSize(ele, item);
            }
        };
        const expandCollectionsToMedium = (i) => {
            const collections = this.tabs[parseInt(tabIndex.toString(), 10)]
                .groups[parseInt(groupIndex.toString(), 10)].collections;
            const item = collections[parseInt(i.toString(), 10)].items[0];
            if (item.displayOptions & DisplayMode.Classic) {
                let ele = activeContent.querySelector('#' + collections[parseInt(i.toString(), 10)].items[0].id);
                collections[parseInt(i.toString(), 10)].items[0].setProperties({ activeSize: RibbonItemSize.Medium }, true);
                if (item.type === RibbonItemType.GroupButton) {
                    ele = activeContent.querySelector('#' + collections[parseInt(i.toString(), 10)].items[0].id + constants.RIBBON_GROUP_BUTTON_ID);
                }
                this.setItemSize(ele, collections[parseInt(i.toString(), 10)].items[0]);
            }
        };
        if (orientation === ItemOrientation.Row) {
            // collection length is 1, then the it wll be covered in shrinked columns
            if (!this.checkValidCollectionLength(group.collections)) {
                for (let k = 0; k < group.collections.length; k++) {
                    const collection = group.collections[parseInt(k.toString(), 10)];
                    for (let l = 0; l < collection.items.length; l++) {
                        l = this.checkClassicItem(collection.items, l, true);
                        if (l === collection.items.length) {
                            continue;
                        }
                        const item = collection.items[parseInt(l.toString(), 10)];
                        if (canExpandItem(item)) {
                            let itemEle = activeContent.querySelector('#' + item.id);
                            if (item.type === 'GroupButton') {
                                itemEle = activeContent.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID);
                            }
                            const valString = itemEle.getAttribute('data-medium-width');
                            const value = valString ? parseInt(valString, 10) : null;
                            if (value && (shouldSkip || (tabContent.offsetWidth > value))) {
                                expandItemToMedium(k, l, itemEle);
                                if (!shouldSkip || shouldClear) {
                                    itemEle.removeAttribute('data-medium-width');
                                }
                            }
                            else if (value) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        else {
            for (let k = 0; k < group.collections.length; k++) {
                //If items length is 1 then, it will be handled in shrinked column
                if ((group.collections[parseInt(k.toString(), 10)].items.length > 1)) {
                    const collection = group.collections[parseInt(k.toString(), 10)];
                    const itemEle = activeContent.querySelector('#' + collection.id);
                    const valString = itemEle.getAttribute('data-medium-width');
                    const value = valString ? parseInt(valString, 10) : null;
                    const classicItems = [];
                    for (let i = 0; i < collection.items.length; i++) {
                        if (collection.items[parseInt(i.toString(), 10)].displayOptions & DisplayMode.Classic) {
                            classicItems.push(i);
                        }
                    }
                    if ((classicItems.length > 1) && value && (shouldSkip || (tabContent.offsetWidth > value))) {
                        expandItemToMedium(k, classicItems[0], itemEle);
                        expandItemToMedium(k, classicItems[1], itemEle);
                        if (classicItems.length === 3) {
                            expandItemToMedium(k, classicItems[2], itemEle);
                        }
                        if (!shouldSkip || shouldClear) {
                            itemEle.removeAttribute('data-medium-width');
                        }
                    }
                    else if (value) {
                        return true;
                    }
                }
            }
        }
        if (shrinkColumns.length > 0) {
            for (let k = 0; k < shrinkColumns.length; k++) {
                const shrinkColumn = shrinkColumns[parseInt(k.toString(), 10)];
                const valString = shrinkColumn.getAttribute('data-medium-width');
                const value = valString ? parseInt(valString, 10) : null;
                if (value && (shouldSkip || (tabContent.offsetWidth > value))) {
                    const start = parseInt(shrinkColumn.getAttribute('data-start'), 10);
                    const end = parseInt(shrinkColumn.getAttribute('data-end'), 10);
                    if (orientation === ItemOrientation.Row) {
                        const collection = group.collections[0];
                        const l = this.checkClassicItem(collection.items, start + 1, true); //next valid item
                        expandItemToMedium(0, start, shrinkColumn);
                        expandItemToMedium(0, l, shrinkColumn);
                        // if l == end, then l is the last item, else L is the middle item. If l is middle then call the method for end.
                        if (l !== end) {
                            expandItemToMedium(0, end, shrinkColumn);
                        }
                    }
                    else {
                        const m = this.checkClassicCollection(group.collections, start + 1, true); //next valid item
                        expandCollectionsToMedium(start);
                        expandCollectionsToMedium(m);
                        if (m !== end) {
                            expandCollectionsToMedium(end);
                        }
                    }
                    if (!shouldSkip || shouldClear) {
                        shrinkColumn.removeAttribute('data-medium-width');
                    }
                }
                else if (value) {
                    return true;
                }
            }
        }
        return false;
    }
    checkMediumToLarge(tabIndex, tab, groupIndex, tabContent, activeContent, shouldSkip, shouldClear) {
        const group = tab.groups[parseInt(groupIndex.toString(), 10)];
        const orientation = group.orientation;
        const ele = activeContent.querySelector('#' + group.id);
        const shrinkColumns = ele.querySelectorAll('.' + 'e-ribbon-shrink');
        if (shrinkColumns.length === 0) {
            return false;
        }
        const expandItemsToLarge = (start, end, parentEle, middle) => {
            const items = this.tabs[parseInt(tabIndex.toString(), 10)].
                groups[parseInt(groupIndex.toString(), 10)].collections[0].items;
            const reduce = (i) => {
                const item = items[parseInt(i.toString(), 10)];
                if (item.displayOptions & DisplayMode.Classic) {
                    const container = parentEle.querySelector('#' + item.id + constants.CONTAINER_ID);
                    let ele = container.querySelector('#' + item.id);
                    item.setProperties({ activeSize: RibbonItemSize.Large }, true);
                    if (item.type === RibbonItemType.GroupButton) {
                        ele = container.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID);
                    }
                    this.setItemSize(ele, item);
                    parentEle.insertAdjacentElement('beforebegin', container);
                }
            };
            reduce(start);
            if (middle) {
                reduce(middle);
            }
            reduce(end);
            if (!shouldSkip || shouldClear) {
                remove(parentEle);
            }
        };
        const expandCollectionsToLarge = (start, end, parentEle, middle) => {
            const collections = this.tabs[parseInt(tabIndex.toString(), 10)].
                groups[parseInt(groupIndex.toString(), 10)].collections;
            const reduce = (i) => {
                const collection = collections[parseInt(i.toString(), 10)];
                if (collection.items[0].displayOptions & DisplayMode.Classic) {
                    const collectionEle = parentEle.querySelector('#' + collection.id);
                    let ele = collectionEle.querySelector('#' + collection.items[0].id);
                    collection.items[0].setProperties({ activeSize: RibbonItemSize.Large }, true);
                    if (collection.items[0].type === RibbonItemType.GroupButton) {
                        ele = collectionEle.querySelector('#' + collection.items[0].id + constants.RIBBON_GROUP_BUTTON_ID);
                    }
                    this.setItemSize(ele, collection.items[0]);
                    parentEle.insertAdjacentElement('beforebegin', collectionEle);
                }
            };
            reduce(start);
            if (middle) {
                reduce(middle);
            }
            reduce(end);
            if (!shouldSkip || shouldClear) {
                remove(parentEle);
            }
        };
        for (let k = 0; k < shrinkColumns.length; k++) {
            const shrinkColumn = shrinkColumns[parseInt(k.toString(), 10)];
            const valString = shrinkColumn.getAttribute('data-large-width');
            const value = valString ? parseInt(valString, 10) : null;
            if (value && (shouldSkip || (tabContent.offsetWidth > value))) {
                const start = parseInt(shrinkColumn.getAttribute('data-start'), 10);
                const end = parseInt(shrinkColumn.getAttribute('data-end'), 10);
                if (orientation === ItemOrientation.Row) {
                    const collection = group.collections[0];
                    const l = this.checkClassicItem(collection.items, start + 1, true); //next valid item
                    if (l === end) {
                        expandItemsToLarge(start, end, shrinkColumn);
                    }
                    else {
                        expandItemsToLarge(start, end, shrinkColumn, l);
                    }
                }
                else {
                    const m = this.checkClassicCollection(group.collections, start + 1, true); //next valid item
                    if (m === end) {
                        expandCollectionsToLarge(start, end, shrinkColumn);
                    }
                    else {
                        expandCollectionsToLarge(start, end, shrinkColumn, m);
                    }
                }
                if (!shouldSkip || shouldClear) {
                    shrinkColumn.removeAttribute('data-large-width');
                }
            }
            else if (value) {
                return true;
            }
        }
        return false;
    }
    handleContentSize(itemEle, isRemoveOverflow) {
        const itemContainer = itemEle.closest('.' + constants.RIBBON_GROUP_CONTENT);
        if (isRemoveOverflow) {
            itemContainer.classList.add(constants.RIBBON_CONTENT_HEIGHT);
        }
        else {
            itemContainer.classList.remove(constants.RIBBON_CONTENT_HEIGHT);
        }
    }
    setItemSize(itemEle, item) {
        if (itemEle) {
            const itemContainer = itemEle.closest('.' + constants.RIBBON_ITEM);
            if (item.type === RibbonItemType.Button) {
                this.ribbonButtonModule.updateButtonSize(itemEle, item);
            }
            else if (item.type === RibbonItemType.DropDown) {
                this.ribbonDropDownModule.updateDropDownSize(itemEle, item);
            }
            else if (item.type === RibbonItemType.SplitButton) {
                this.ribbonSplitButtonModule.updateSplitButtonSize(itemEle, item);
            }
            else if (item.type === RibbonItemType.Template) {
                remove(itemEle);
                this.createTemplateContent(item, itemContainer);
            }
            else if (item.type === RibbonItemType.GroupButton) {
                this.ribbonGroupButtonModule.updateGroupButtonSize(itemEle, item);
            }
            itemContainer.classList.remove(constants.RIBBON_CONTENT_HEIGHT, constants.RIBBON_LARGE_ITEM, constants.RIBBON_MEDIUM_ITEM, constants.RIBBON_SMALL_ITEM);
            if (item.activeSize === RibbonItemSize.Large) {
                itemContainer.classList.add(constants.RIBBON_LARGE_ITEM, constants.RIBBON_CONTENT_HEIGHT);
            }
            else {
                itemContainer.classList.add((item.activeSize === RibbonItemSize.Medium) ?
                    constants.RIBBON_MEDIUM_ITEM : constants.RIBBON_SMALL_ITEM);
            }
        }
    }
    createOverflowDropdown(tabIndex, tabContent, activeContent) {
        const collapseOrder = this.getGroupResizeOrder(true, tabIndex);
        if (collapseOrder.length === 0) {
            return;
        }
        for (let i = 0; ((i < collapseOrder.length) && (tabContent.offsetWidth < activeContent.offsetWidth)); i++) {
            const group = collapseOrder[parseInt(i.toString(), 10)];
            const groupEle = this.tabObj.element.querySelector('#' + group.id);
            groupEle.setAttribute('data-expanded-width', activeContent.offsetWidth.toString());
            const groupContainer = groupEle.querySelector('#' + group.id + constants.CONTAINER_ID);
            const groupOverFlow = this.createElement('div', {
                className: constants.RIBBON_GROUP_OVERFLOW + constants.SPACE + constants.RIBBON_LARGE_ITEM,
                id: group.id + constants.OVERFLOW_ID + constants.CONTAINER_ID
            });
            groupEle.insertBefore(groupOverFlow, groupContainer);
            const groupIndex = getIndex(this.tabs[parseInt(tabIndex.toString(), 10)].groups, (e) => { return e.id === group.id; });
            const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
            //Expanding the items in the group to their original expanded state
            this.checkSmallToMedium(tabIndex, tab, groupIndex, tabContent, activeContent, true, false);
            this.checkMediumToLarge(tabIndex, tab, groupIndex, tabContent, activeContent, true, false);
            const dropdown = this.ribbonDropDownModule.createOverFlowDropDown(group.id, group.header, group.groupIconCss, groupContainer, groupOverFlow, this.enableRtl);
            if (group.keyTip) {
                const overflowDDB = group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID;
                this.addKeyTip(tabIndex, group.keyTip, overflowDDB, 'grpoverflow');
            }
            this.tabs[parseInt(tabIndex.toString(), 10)].
                groups[parseInt(groupIndex.toString(), 10)].setProperties({ isCollapsed: true }, true);
            for (let j = 0; j < group.collections.length; j++) {
                const collection = group.collections[parseInt(j.toString(), 10)];
                const collectionEle = groupContainer.querySelector('#' + collection.id);
                for (let k = 0; k < collection.items.length; k++) {
                    const item = collection.items[parseInt(k.toString(), 10)];
                    const itemEle = collectionEle.querySelector('#' + item.id + constants.CONTAINER_ID);
                    if (itemEle !== null) {
                        this.handleContentSize(itemEle);
                        this.addOverflowEvents(item, itemEle, dropdown);
                    }
                    const overflowDDB = document.querySelector('#' + group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID);
                    const overflowButton = getInstance(overflowDDB, DropDownButton);
                    if (overflowButton) {
                        const overflowtarget = overflowButton.target;
                        if (overflowtarget) {
                            if (this.keyTipElements[parseInt(tabIndex.toString(), 10)]) {
                                if (item.type === RibbonItemType.GroupButton) {
                                    for (let i = 0; i < item.groupButtonSettings.items.length; i++) {
                                        if (item.groupButtonSettings.items[parseInt(i.toString(), 10)].keyTip) {
                                            this.addKeyTip(tabIndex, item.groupButtonSettings.items[parseInt(i.toString(), 10)].keyTip, item.id + (constants.RIBBON_GROUP_BUTTON_ID + i), 'grpoverflowpopup');
                                        }
                                    }
                                }
                                if (item.keyTip) {
                                    this.addKeyTip(tabIndex, item.keyTip, item.id, 'grpoverflowpopup');
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    // eslint-disable-next-line max-len
    removeOverflowDropdown(tabContent, activeContent, isClear = false, tabIndex) {
        const expandOrder = this.getGroupResizeOrder(false, tabIndex);
        if (expandOrder.length === 0) {
            return false;
        }
        for (let i = 0; i < expandOrder.length; i++) {
            const group = expandOrder[parseInt(i.toString(), 10)];
            const groupEle = this.tabObj.element.querySelector('#' + group.id);
            if (!groupEle) {
                break;
            } //to handle the rerendering of tabcontrol when a ribbon tab is added/removed
            const width = parseInt(groupEle.getAttribute('data-expanded-width'), 10);
            if (!isClear && (tabContent.offsetWidth < width)) {
                return true;
            }
            this.removeDropdown(group.id);
            const groupIndex = getIndex(this.tabs[parseInt(tabIndex.toString(), 10)].groups, (e) => { return e.id === group.id; });
            this.tabs[parseInt(tabIndex.toString(), 10)].
                groups[parseInt(groupIndex.toString(), 10)].setProperties({ isCollapsed: false }, true);
            const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
            //Shrinking the items in the group to their previous shrinked state (before moving to dropdown)
            this.checkLargeToMedium(tabIndex, tab, groupIndex, tabContent, activeContent, true);
            this.checkMediumToSmall(tabIndex, tab, groupIndex, tabContent, activeContent, true);
            for (let j = 0; j < group.collections.length; j++) {
                const collection = group.collections[parseInt(j.toString(), 10)];
                const collectionEle = groupEle.querySelector('#' + collection.id);
                for (let k = 0; k < collection.items.length; k++) {
                    const item = collection.items[parseInt(k.toString(), 10)];
                    const itemEle = collectionEle.querySelector('#' + item.id + constants.CONTAINER_ID);
                    if (itemEle !== null) {
                        this.handleContentSize(itemEle, true);
                        this.removeOverflowEvent(item, itemEle);
                    }
                }
            }
        }
        return false;
    }
    removeDropdown(groupId) {
        const dropdownElement = this.tabObj.element.querySelector('#' + groupId + constants.OVERFLOW_ID + constants.DROPDOWN_ID);
        if (dropdownElement) {
            const groupOverFlow = dropdownElement.parentElement;
            this.ribbonDropDownModule.removeOverFlowDropDown(dropdownElement);
            remove(groupOverFlow);
        }
    }
    getGroupResizeOrder(isCollapse, tabIndex) {
        let groups = this.tabs[parseInt(tabIndex.toString(), 10)].groups;
        groups = groups.filter((e) => {
            // (isUndefined(e.isCollapsible) || e.isCollapsible) => check whethe rhte item is collapsible
            // if a isCollapsed property is undefined, then it is considered collapsible and included in collapsible list
            // ((isCollapse && !e.isCollapsed)||(!isCollapse && e.isCollapsed)) => isCollapse !== e.isCollapsed
            return (this.activeLayout === 'Classic') ? (isUndefined(e.isCollapsible) || e.isCollapsible) && ((isCollapse &&
                isUndefined(e.isCollapsed)) || (!isUndefined(e.isCollapsed) && (isCollapse !== e.isCollapsed))) : true;
        });
        //sort the collapsible groups based on the priority
        groups.sort((a, b) => a.priority - b.priority);
        //reverse the sorted array to return the array in descending order while collapsing.
        return isCollapse ? groups.reverse() : groups;
    }
    destroyScroll() {
        if (this.scrollModule) {
            this.scrollModule.destroy();
            this.scrollModule = null;
        }
    }
    clearOverflowDropDown(index) {
        const activeContent = this.tabObj.element.querySelector('#' + this.tabs[parseInt(index.toString(), 10)].id + constants.CONTENT_ID);
        if (!activeContent) {
            return;
        }
        const tabContent = activeContent.closest('.' + constants.TAB_CONTENT);
        if (this.activeLayout === 'Simplified') {
            this.removeSimplfiedOverflow(activeContent, tabContent, index, true);
        }
        else {
            this.removeOverflowDropdown(activeContent, tabContent, true, index);
        }
    }
    isContextualTab(tabID) {
        let isContextual = false;
        if (this.contextualTabs.length) {
            for (let i = 0; i < this.contextualTabs.length; i++) {
                for (let j = 0; j < this.contextualTabs[parseInt(i.toString(), 10)].tabs.length; j++) {
                    if (this.contextualTabs[parseInt(i.toString(), 10)].tabs[parseInt(j.toString(), 10)].id === tabID) {
                        isContextual = true;
                        break;
                    }
                }
            }
        }
        return isContextual;
    }
    ribbonTabSelecting(e) {
        this.currentControlIndex = 0;
        const nextTabId = e.selectingItem ? e.selectingItem.getAttribute('data-id') : null;
        const previousTabId = e.previousItem.getAttribute('data-id');
        let nextIndex = getIndex(this.tabs, ((tab) => (tab.id === nextTabId)));
        const isContextual = this.isContextualTab(nextTabId);
        const previousIndex = getIndex(this.tabs, ((tab) => (tab.id === previousTabId)));
        nextIndex = nextIndex === -1 ? this.selectedTab : nextIndex;
        const eventArgs = {
            cancel: e.cancel, isInteracted: e.isInteracted, previousIndex: previousIndex,
            selectedIndex: nextIndex, isContextual: isContextual
        };
        this.trigger('tabSelecting', eventArgs, (args) => {
            if (args.cancel) {
                return;
            }
            this.destroyScroll();
            if (!this.isAddRemove && (previousIndex !== -1)) {
                this.clearOverflowDropDown(previousIndex);
            }
            const selectedTabContent = this.tabObj.items[parseInt(nextIndex.toString(), 10)].content;
            if ((!selectedTabContent.querySelector('.' + constants.RIBBON_GROUP)) && (this.tabs[parseInt(nextIndex.toString(), 10)].groups.length !== 0)) {
                const elements = this.createGroups(this.tabs[parseInt(nextIndex.toString(), 10)].groups, nextIndex);
                append(elements, selectedTabContent);
            }
        });
    }
    createTabItems(tabs) {
        const tabItems = [];
        for (let i = 0; i < tabs.length; i++) {
            const ribbonTab = tabs[parseInt(i.toString(), 10)];
            const header = this.createElement('span', {
                innerHTML: ribbonTab.header,
                id: ribbonTab.id + constants.HEADER_ID
            });
            const tabIndex = getIndex(this.tabs, ((tab) => (tab.id === ribbonTab.id)));
            if (ribbonTab.keyTip) {
                if (!this.keyTipElements[parseInt(tabIndex.toString(), 10)]) {
                    this.keyTipElements[parseInt(tabIndex.toString(), 10)] = {};
                }
                this.addKeyTip(tabIndex, ribbonTab.keyTip, ribbonTab.id, 'tab');
            }
            header.onclick = () => { this.minimize(false); };
            header.ondblclick = () => { this.minimize(true); };
            const tab = { header: { text: header }, id: ribbonTab.id, cssClass: ribbonTab.cssClass };
            const content = this.createElement('div', {
                className: tab.cssClass,
                id: ribbonTab.id + constants.CONTENT_ID
            });
            content.classList.add(constants.RIBBON_TAB_ITEM);
            tab.content = content;
            tabItems.push(tab);
        }
        return tabItems;
    }
    renderInitialTab(index) {
        const elements = this.createGroups(this.tabs[parseInt(index.toString(), 10)].groups, index);
        const content = this.tabObj.items[parseInt(index.toString(), 10)].content;
        append(elements, content);
        if (this.activeLayout === 'Simplified') {
            this.element.classList.add(constants.RIBBON_SIMPLIFIED_MODE);
        }
        const activeContent = this.tabObj.element.querySelector('#' + this.tabs[this.selectedTab].id + constants.CONTENT_ID);
        if (this.ribbonGalleryModule) {
            this.ribbonGalleryModule.checkAvailableHeight(activeContent);
        }
        this.checkOverflow(this.selectedTab, activeContent);
    }
    addOverflowButton(btnId, isGroupOF) {
        const overflowButton = this.createElement('button', {
            id: btnId
        });
        const overflowTarget = this.createElement('div', {
            className: constants.RIBBON_OVERFLOW_TARGET,
            attrs: { 'tabindex': '0' }
        });
        overflowButton.setAttribute('title', isGroupOF ? this.l10n.getConstant('groupOverflow') : this.l10n.getConstant('overflow'));
        const overFlowCss = this.cssClass ? constants.SPACE + this.cssClass : '';
        const overflowDDB = new DropDownButton({
            iconCss: constants.OVERFLOW_ICON,
            cssClass: constants.DROPDOWNBUTTON_HIDE + constants.SPACE + constants.RIBBON_GROUP_OVERFLOW_DDB + overFlowCss,
            target: overflowTarget,
            locale: this.locale,
            enableRtl: this.enableRtl,
            enablePersistence: this.enablePersistence,
            beforeOpen: (args) => {
                const eventArgs = { element: args.element, event: args.event, cancel: args.cancel };
                this.trigger('overflowPopupOpen', eventArgs, (ribbonArgs) => {
                    if (ribbonArgs.cancel) {
                        args.cancel = true;
                    }
                });
            },
            beforeClose: (args) => {
                const ele = args.event ? closest(args.event.target, '.' + constants.RIBBON_POPUP_CONTROL) : null;
                const groupButtonEle = args.event ? closest(args.event.target, '.e-ribbon-group-button-overflow-popup') : null;
                const eventArgs = { element: args.element, event: args.event, cancel: args.cancel };
                this.trigger('overflowPopupClose', eventArgs, (ribbonArgs) => {
                    if (ele || ribbonArgs.cancel || groupButtonEle) {
                        args.element = null;
                        args.cancel = true;
                    }
                });
            }
        }, overflowButton);
        this.element.classList.add(constants.RIBBON_OVERFLOW);
        createTooltip(overflowTarget, this);
        if (!isGroupOF) {
            this.keyTipElements['overflowbtn'] = [];
            this.keyTipElements['overflowbtn'].push({ id: btnId, type: 'overflowbtn', keyTip: '00' });
        }
        let isGroupOf;
        overflowButton.onkeydown = overflowButton.onclick = () => { this.itemIndex = -1; isGroupOf = overflowButton.classList.contains('e-ribbon-overall-of-btn') ? false : true; };
        overflowTarget.onkeydown = (e) => (this.upDownKeyHandler(e, overflowTarget, isGroupOf), this);
        return overflowDDB;
    }
    upDownKeyHandler(e, target, isGroupOf) {
        let items;
        if (isGroupOf) {
            items = target.getElementsByClassName('e-ribbon-item');
        }
        else {
            const currentList = target.querySelector('.e-ribbon-of-tab.e-ribbon-active');
            items = currentList.getElementsByClassName('e-ribbon-item');
        }
        const control = items[(!this.itemIndex || this.itemIndex < 0) ? 0 : this.itemIndex].querySelector('.e-control');
        const comboBoxEle = control && control.classList.contains('e-combobox') ?
            items[(!this.itemIndex || this.itemIndex < 0) ? 0 : this.itemIndex].querySelector('.e-combobox') : null;
        let ribbonItem;
        let templateEle;
        if (comboBoxEle === null || (e.key === 'Tab') || this.itemIndex < 0) {
            if (e.key === 'ArrowDown' || (!e.shiftKey && e.key === 'Tab')) {
                if ((!this.itemIndex && this.itemIndex !== 0) || this.itemIndex < 0 || this.itemIndex === items.length - 1) {
                    this.itemIndex = 0;
                    ribbonItem = items[this.itemIndex].closest('.e-ribbon-item');
                    this.findDisabledItem(ribbonItem, items, true);
                    if (comboBoxEle && e.key === 'Tab') {
                        e.preventDefault();
                        const item = items[this.itemIndex].querySelector('.e-control');
                        if (item) {
                            item.focus();
                        }
                    }
                    templateEle = items[this.itemIndex].querySelector('.e-ribbon-template');
                }
                else if (this.itemIndex < items.length - 1 && this.itemIndex >= 0) {
                    this.itemIndex++;
                    ribbonItem = items[this.itemIndex].closest('.e-ribbon-item');
                    this.findDisabledItem(ribbonItem, items, true);
                    templateEle = items[this.itemIndex].querySelector('.e-ribbon-template');
                }
                if (templateEle) {
                    templateEle.focus();
                }
            }
            else if (e.key === 'ArrowUp' || (e.shiftKey && e.key === 'Tab')) {
                if (comboBoxEle === null || (e.key === 'Tab')) {
                    if (!this.itemIndex || this.itemIndex === -1) {
                        this.itemIndex = items.length - 1;
                        ribbonItem = items[this.itemIndex].closest('.e-ribbon-item');
                        this.findDisabledItem(ribbonItem, items, false);
                        if (comboBoxEle && (e.shiftKey && e.key === 'Tab')) {
                            e.preventDefault();
                            const item = items[this.itemIndex].querySelector('.e-control');
                            if (item) {
                                item.focus();
                            }
                        }
                        templateEle = items[this.itemIndex].querySelector('.e-ribbon-template');
                    }
                    else if (this.itemIndex <= items.length - 1 && this.itemIndex > 0) {
                        this.itemIndex--;
                        ribbonItem = items[this.itemIndex].closest('.e-ribbon-item');
                        this.findDisabledItem(ribbonItem, items, false);
                        templateEle = items[this.itemIndex].querySelector('.e-ribbon-template');
                    }
                    if (templateEle) {
                        templateEle.focus();
                    }
                }
            }
            target.setAttribute('index', this.itemIndex.toString());
        }
        const currentItemIndex = parseInt(target.getAttribute('index'), 10);
        let itemType = '';
        const controlItem = items[parseInt(currentItemIndex.toString(), 10)] ? items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-control') : null;
        if (controlItem) {
            itemType = controlItem.getAttribute('data-control');
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === ' ' || e.key === 'Tab') {
            if (itemType === 'ColorPicker') {
                if (e.key === 'Tab') {
                    e.preventDefault();
                }
                items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-split-colorpicker').focus();
            }
            else {
                if (e.key === 'Tab') {
                    e.preventDefault();
                }
                const elem = items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-control');
                if (elem) {
                    elem.focus();
                }
            }
            if (e.key === ' ' && (itemType === 'CheckBox')) {
                const checkBoxEle = items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-control');
                const checkBox = getComponent(checkBoxEle, CheckBox);
                this.itemIndex = -1;
                checkBox.click();
            }
        }
        if (((itemType === 'SplitButton') && (e.key === 'ArrowRight' || e.key === 'ArrowLeft'))) {
            if (e.key === 'ArrowRight') {
                if (this.enableRtl) {
                    items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-control').focus();
                }
                else {
                    items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-dropdown-btn').focus();
                }
            }
            if (e.key === 'ArrowLeft') {
                if (this.enableRtl) {
                    items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-dropdown-btn').focus();
                }
                else {
                    items[parseInt(currentItemIndex.toString(), 10)].querySelector('.e-control').focus();
                }
            }
        }
        if (e.key === 'Enter') {
            this.itemIndex = -1;
        }
    }
    findDisabledItem(ribbonItem, items, isIncrease) {
        while (ribbonItem && ribbonItem.classList.contains('e-disabled')) {
            if (isIncrease) {
                if (this.itemIndex === items.length - 1 && items[this.itemIndex].closest('.e-ribbon-item').classList.contains('e-disabled')) {
                    this.itemIndex = -1;
                }
            }
            else {
                if (this.itemIndex === 0 && items[this.itemIndex].closest('.e-ribbon-item').classList.contains('e-disabled')) {
                    this.itemIndex = items.length;
                }
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            isIncrease ? this.itemIndex++ : this.itemIndex--;
            ribbonItem = items[this.itemIndex].closest('.e-ribbon-item');
        }
    }
    removeOverflowButton(overflowDDB) {
        if (overflowDDB) {
            const btnEle = overflowDDB.element;
            destroyTooltip(overflowDDB.target);
            overflowDDB.destroy();
            btnEle.remove();
        }
    }
    removeOverflowEvent(item, itemEle) {
        if (itemEle) {
            switch (item.type) {
                case 'Button':
                    this.ribbonButtonModule.removeOverFlowEvents(item, itemEle);
                    break;
                case 'DropDown':
                    this.ribbonDropDownModule.removeOverFlowEvents(item, itemEle);
                    break;
                case 'SplitButton':
                    this.ribbonSplitButtonModule.removeOverFlowEvents(item, itemEle);
                    break;
                case 'CheckBox':
                    this.ribbonCheckBoxModule.removeOverFlowEvents(item, itemEle);
                    break;
                case 'ColorPicker':
                    this.ribbonColorPickerModule.removeOverFlowEvents(item, itemEle);
                    break;
                case 'ComboBox':
                    this.ribbonComboBoxModule.removeOverFlowEvents(item, itemEle);
                    break;
                case 'Gallery':
                    this.ribbonGalleryModule.removeOverFlowEvents(item, itemEle);
                    break;
                case 'GroupButton':
                    this.ribbonGroupButtonModule.removeOverFlowEvents(item, itemEle);
                    break;
            }
        }
    }
    createGroupContainer(groupId, groupHeader) {
        const ofGroupContainer = this.createElement('div', {
            className: constants.RIBBON_OF_GROUP_CONTAINER,
            id: groupId + constants.CONTAINER_ID
        });
        const ofGroupHeader = this.createElement('div', {
            className: constants.RIBBON_OVERFLOW_HEADER,
            id: groupId + constants.HEADER_ID,
            innerHTML: groupHeader
        });
        ofGroupContainer.append(ofGroupHeader);
        return ofGroupContainer;
    }
    addExpandCollapse() {
        this.collapseButton = this.createElement('span', {
            className: constants.RIBBON_COLLAPSE_BUTTON + constants.SPACE + constants.EXPAND_COLLAPSE_ICON,
            id: this.tabObj.element.id + constants.COLLAPSE_BUTTON_ID,
            attrs: { 'tabindex': '0', 'type': 'button', 'aria-label': 'Layout Switcher', 'role': 'button' }
        });
        this.collapseButton.onclick = (e) => { this.toggleLayout(e); };
        this.collapseButton.onkeydown = (e) => {
            if (e.key === 'Enter') {
                this.toggleLayout(e);
            }
        };
        this.element.classList.add(constants.RIBBON_COLLAPSIBLE);
        if (this.activeLayout === 'Simplified') {
            this.collapseButton.classList.add(constants.RIBBON_EXPAND_BUTTON);
        }
        this.tabObj.element.appendChild(this.collapseButton);
        if (this.layoutSwitcherKeyTip) {
            this.keyTipElements['collapse'] = [];
            this.keyTipElements['collapse'].push({ id: this.collapseButton.id, type: 'collapse', keyTip: this.layoutSwitcherKeyTip });
        }
    }
    removeExpandCollapse() {
        const index = getIndex(this.tooltipData, (e) => { return e.id === this.collapseButton.id; });
        if (index !== -1) {
            this.tooltipData.splice(index, 1);
        }
        this.element.classList.remove(constants.RIBBON_COLLAPSIBLE);
        remove(this.tabObj.element.querySelector('.' + constants.RIBBON_COLLAPSE_BUTTON));
        this.collapseButton = null;
    }
    reRenderTabs(tabs) {
        this.destroyScroll();
        this.checkID(this.tabs, 'tab', this.element.id);
        for (const key of Object.keys(tabs)) {
            const index = parseInt(key, 10);
            const tab = tabs[parseInt(index.toString(), 10)];
            let isNewTab = false;
            for (let j = 0; j < (this.tabObj.items.length); j++) {
                if (this.tabs[parseInt(index.toString(), 10)].id === this.tabObj.items[parseInt(j.toString(), 10)].id) {
                    isNewTab = true;
                    break;
                }
            }
            if (!isNewTab) {
                this.destroyTabItems(this.tabsInternal);
                this.tabsInternal = this.tabs.slice();
                this.validateItemSize();
                const tabItems = this.createTabItems(this.tabs);
                if (this.selectedTab >= this.tabs.length) {
                    this.selectedTab = this.tabs.length - 1;
                }
                this.tabObj.setProperties({ items: tabItems, selectedItem: this.selectedTab });
                break;
            }
            else {
                const groups = this.tabs[parseInt(index.toString(), 10)].groups;
                const tabEle = this.tabObj.element;
                const ribbonTab = this.tabs[parseInt(index.toString(), 10)];
                ribbonTab.setProperties(tab, true);
                this.setProperties({ groups: this.checkID(ribbonTab.groups, 'group', ribbonTab.id) }, true);
                this.validateItemSize();
                const contentEle = this.tabObj.items[parseInt(index.toString(), 10)].content;
                if (groups) {
                    // Check whether header is passed by the user and sets the updated values.
                    if (tab.header) {
                        this.tabObj.items[parseInt(index.toString(), 10)].header.text.innerText = ribbonTab.header;
                    }
                    // Check whether cssClass is passed by the user, and if it is, then remove the old values.
                    if (tab.cssClass) {
                        if (this.tabObj.items[parseInt(index.toString(), 10)].cssClass) {
                            contentEle.classList.remove(this.tabObj.items[parseInt(index.toString(), 10)].cssClass);
                            tabEle.querySelector('.e-active').classList.remove(this.tabObj.items[parseInt(index.toString(), 10)].cssClass);
                        }
                        contentEle.classList.add(ribbonTab.cssClass);
                        tabEle.querySelector('.e-active').classList.add(ribbonTab.cssClass);
                    }
                    // Check whether group is passed by the user, and if it is, then remove the old values.
                    if (tab.groups) {
                        for (const group of groups) {
                            const dropdownElement = group.isCollapsed ? contentEle.querySelector('#' + group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID) : null;
                            for (const collection of group.collections) {
                                for (const item of collection.items) {
                                    const ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id) : contentEle.querySelector('#' + item.id);
                                    if (ele) {
                                        this.destroyFunction(item, ele);
                                    }
                                }
                            }
                            if (dropdownElement) {
                                this.ribbonDropDownModule.removeOverFlowDropDown(dropdownElement);
                            }
                        }
                        const groupElements = contentEle.querySelectorAll('.e-ribbon-group');
                        // eslint-disable-next-line @typescript-eslint/tslint/config
                        groupElements.forEach(groupEle => { groupEle.remove(); });
                        const elements = this.createGroups(ribbonTab.groups, index);
                        append(elements, contentEle);
                    }
                }
            }
        }
        if (this.tabsInternal !== this.tabs) {
            this.tabsInternal = this.tabs;
            const tabItems = this.createTabItems(this.tabs);
            if (this.selectedTab >= this.tabs.length) {
                this.selectedTab = this.tabs.length - 1;
            }
            this.tabObj.setProperties({ items: tabItems, selectedItem: this.selectedTab });
        }
        const activeContent = this.tabObj.element.querySelector('#' + this.tabs[this.selectedTab].id + constants.CONTENT_ID);
        this.checkOverflow(this.selectedTab, activeContent);
    }
    switchLayout() {
        this.currentControlIndex = 0;
        this.destroyScroll();
        this.collapseButton.classList.toggle(constants.RIBBON_EXPAND_BUTTON, this.activeLayout === 'Simplified');
        this.element.classList.toggle(constants.RIBBON_SIMPLIFIED_MODE, this.activeLayout === 'Simplified');
        for (let i = 0; i <= (this.tabs.length - 1); i++) {
            const tabIndex = i;
            const contentEle = this.tabObj.items[parseInt(tabIndex.toString(), 10)].content;
            if (contentEle.innerHTML !== '') {
                const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
                const groupList = this.tabs[parseInt(tabIndex.toString(), 10)].groups;
                const activeContent = this.tabObj.element.querySelector('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.CONTENT_ID);
                const tabContent = activeContent.closest('.' + constants.TAB_CONTENT);
                if (this.activeLayout === 'Simplified') {
                    for (let i = 0; i < groupList.length; i++) {
                        const group = groupList[parseInt(i.toString(), 10)];
                        const alignType = groupList[parseInt(i.toString(), 10)].orientation;
                        if (group.isCollapsed) {
                            group.setProperties({ isCollapsed: false }, true);
                            this.removeDropdown(group.id);
                        }
                        else {
                            this.checkSmallToMedium(tabIndex, tab, i, tabContent, activeContent, true, false);
                            this.checkMediumToLarge(tabIndex, tab, i, tabContent, activeContent, true, false);
                        }
                        const groupEle = tabContent.querySelector('#' + group.id);
                        const groupContainer = groupEle.querySelector('#' + group.id + constants.CONTAINER_ID);
                        const shrinkColumns = groupContainer.querySelectorAll('.' + 'e-ribbon-shrink');
                        for (let i = 0; i < shrinkColumns.length; i++) {
                            shrinkColumns[parseInt(i.toString(), 10)].remove();
                        }
                        const groupHeader = groupContainer.querySelector('#' + group.id + constants.HEADER_ID);
                        groupHeader.remove();
                        const groupContent = groupContainer.querySelector('#' + group.id + constants.CONTENT_ID);
                        groupContent.classList.replace(constants.RIBBON_ROW, constants.RIBBON_COLUMN);
                        groupContent.classList.remove(constants.RIBBON_CONTENT_HEIGHT);
                        for (let j = 0; j < group.collections.length; j++) {
                            const collection = group.collections[parseInt(j.toString(), 10)];
                            const groupCollection = groupContainer.querySelector('#' + collection.id);
                            groupCollection.classList.replace(constants.RIBBON_ROW, constants.RIBBON_COLUMN);
                            for (let k = 0; k < collection.items.length; k++) {
                                const itemList = collection.items;
                                let item = collection.items[parseInt(k.toString(), 10)];
                                let flag = true;
                                while ((flag) && (item.displayOptions === DisplayMode.Classic)) {
                                    k++;
                                    const itemEle = groupContainer.querySelector('#' + item.id + constants.CONTAINER_ID);
                                    const ele = itemEle.querySelector('#' + item.id);
                                    this.destroyFunction(item, ele);
                                    itemEle.remove();
                                    if (k < itemList.length) {
                                        item = itemList[parseInt(k.toString(), 10)];
                                    }
                                    else {
                                        flag = false;
                                    }
                                }
                                if (!flag) {
                                    break;
                                }
                                let size = ((item.allowedSizes === RibbonItemSize.Large) ||
                                    (item.allowedSizes & RibbonItemSize.Medium)) ? RibbonItemSize.Medium : RibbonItemSize.Small;
                                size = (!(item.displayOptions & DisplayMode.Simplified) && (item.displayOptions & DisplayMode.Overflow))
                                    ? RibbonItemSize.Medium : size;
                                let itemEle;
                                if (!(item.displayOptions & DisplayMode.Classic)) {
                                    itemEle = this.createItems([item], alignType, group.id, group.header, group.enableGroupOverflow, tabIndex, groupContainer)[0];
                                    if (item.displayOptions & DisplayMode.Simplified) {
                                        groupCollection.append(itemEle);
                                    }
                                }
                                else {
                                    itemEle = groupContainer.querySelector('#' + item.id + constants.CONTAINER_ID);
                                    if (item.displayOptions === (DisplayMode.Classic | DisplayMode.Overflow)) {
                                        this.createOverflowPopup(item, tabIndex, group.enableGroupOverflow, group.id, group.header, itemEle, groupContainer);
                                        if ((item.type === RibbonItemType.DropDown) || (item.type === RibbonItemType.SplitButton) ||
                                            (item.type === RibbonItemType.GroupButton) || (item.type === RibbonItemType.Gallery)) {
                                            this.updatePopupItems(item, itemEle, group.enableGroupOverflow, true);
                                        }
                                    }
                                    if (item.type === RibbonItemType.GroupButton) {
                                        this.ribbonGroupButtonModule.switchGroupButton(item, itemEle);
                                    }
                                    item.setProperties({ activeSize: size }, true);
                                    if (itemEle) {
                                        const ele = itemEle.querySelector('#' + item.id);
                                        this.setItemSize(ele, item);
                                    }
                                }
                                if (item.type === RibbonItemType.Gallery) {
                                    this.ribbonGalleryModule.switchGalleryItems(this.activeLayout, item.id);
                                }
                            }
                        }
                        if (!(group.enableGroupOverflow || groupEle.querySelector('.' + constants.RIBBON_ITEM))) {
                            groupEle.classList.add('e-ribbon-emptyCollection');
                        }
                    }
                }
                else {
                    this.element.classList.remove(constants.RIBBON_OVERFLOW);
                    for (let i = 0; i < groupList.length; i++) {
                        const group = groupList[parseInt(i.toString(), 10)];
                        const alignType = groupList[parseInt(i.toString(), 10)].orientation;
                        const groupContainer = tabContent.querySelector('#' + group.id + constants.CONTAINER_ID);
                        const groupContent = groupContainer.querySelector('#' + group.id + constants.CONTENT_ID);
                        const groupHeader = this.createElement('div', {
                            className: constants.RIBBON_GROUP_HEADER,
                            id: group.id + constants.HEADER_ID,
                            innerHTML: group.header
                        });
                        groupContainer.appendChild(groupHeader);
                        if (alignType === 'Row') {
                            groupContent.classList.replace(constants.RIBBON_COLUMN, constants.RIBBON_ROW);
                        }
                        groupContent.classList.add(constants.RIBBON_CONTENT_HEIGHT);
                        for (let j = 0; j < group.collections.length; j++) {
                            let overflowDDB;
                            let overflowtarget;
                            if (!group.enableGroupOverflow) {
                                overflowDDB = this.overflowDDB;
                                if (overflowDDB) {
                                    overflowtarget = this.overflowDDB.target;
                                }
                            }
                            else {
                                const overflowDDBEle = groupContainer.querySelector('#' + group.id + constants.GROUPOF_BUTTON_ID);
                                if (overflowDDBEle) {
                                    overflowDDB = getInstance(overflowDDBEle, DropDownButton);
                                    overflowtarget = overflowDDB.target;
                                }
                            }
                            const collection = group.collections[parseInt(j.toString(), 10)];
                            const groupCollection = groupContainer.querySelector('#' + collection.id);
                            if (alignType === 'Column') {
                                groupCollection.classList.replace(constants.RIBBON_COLUMN, constants.RIBBON_ROW);
                            }
                            for (let k = 0; k < collection.items.length; k++) {
                                const itemList = collection.items;
                                let item = collection.items[parseInt(k.toString(), 10)];
                                let flag = true;
                                while ((flag) && !(item.displayOptions & DisplayMode.Classic)) {
                                    k++;
                                    let itemEle;
                                    if ((item.displayOptions & DisplayMode.Simplified) || (item.displayOptions & DisplayMode.Overflow)) {
                                        if (item.displayOptions & DisplayMode.Simplified) {
                                            itemEle = groupContainer.querySelector('#' + item.id + constants.CONTAINER_ID);
                                        }
                                        else {
                                            itemEle = overflowtarget.querySelector('#' + item.id + constants.CONTAINER_ID);
                                        }
                                        if (itemEle !== null) {
                                            const ele = itemEle.querySelector('#' + item.id);
                                            this.destroyFunction(item, ele);
                                            itemEle.remove();
                                        }
                                    }
                                    if (k < itemList.length) {
                                        item = itemList[parseInt(k.toString(), 10)];
                                    }
                                    else {
                                        flag = false;
                                    }
                                }
                                if (!flag) {
                                    break;
                                }
                                if (!(item.displayOptions & (DisplayMode.Simplified | DisplayMode.Overflow))) {
                                    const itemEle = this.createItems([item], alignType, group.id, group.header, group.enableGroupOverflow, tabIndex)[0];
                                    groupCollection.append(itemEle);
                                }
                                else {
                                    let itemEle = groupContainer.querySelector('#' + item.id + constants.CONTAINER_ID);
                                    if (!itemEle && overflowtarget) {
                                        itemEle = overflowtarget.querySelector('#' + item.id + constants.CONTAINER_ID);
                                        if ((item.type === RibbonItemType.DropDown) || (item.type === RibbonItemType.SplitButton) ||
                                            (item.type === RibbonItemType.GroupButton) || (item.type === RibbonItemType.Gallery)) {
                                            this.updatePopupItems(item, itemEle, group.enableGroupOverflow, false);
                                        }
                                        this.removeOverflowEvent(item, itemEle);
                                    }
                                    if (item.type === RibbonItemType.GroupButton) {
                                        this.ribbonGroupButtonModule.switchGroupButton(item, itemEle);
                                    }
                                    if (itemEle) {
                                        groupCollection.append(itemEle);
                                    }
                                }
                                let ele = groupContainer.querySelector('#' + item.id);
                                if (item.type === RibbonItemType.GroupButton) {
                                    ele = groupContainer.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID);
                                }
                                const itemsize = (item.allowedSizes & RibbonItemSize.Large) ? RibbonItemSize.Large :
                                    (item.allowedSizes & RibbonItemSize.Medium) ? RibbonItemSize.Medium : RibbonItemSize.Small;
                                item.setProperties({ activeSize: itemsize }, true);
                                this.setItemSize(ele, item);
                                if (item.type === RibbonItemType.Gallery) {
                                    this.ribbonGalleryModule.switchGalleryItems(this.activeLayout, item.id);
                                }
                            }
                            if (group.enableGroupOverflow && overflowDDB) {
                                if (overflowtarget.childElementCount === 0 ||
                                    (overflowtarget.childElementCount === 1 && this.isHeaderVisible(overflowtarget, group.id))) {
                                    this.removeOverflowButton(overflowDDB);
                                }
                            }
                        }
                    }
                }
                if (this.selectedTab === tabIndex) {
                    this.checkOverflow(tabIndex, activeContent);
                }
            }
        }
        if (this.activeLayout === 'Classic') {
            this.removeOverflowButton(this.overflowDDB);
            this.overflowDDB = null;
        }
    }
    createLauncherIcon(groupId, groupContainer, tabIndex) {
        const launcherIcon = this.createElement('div', {
            className: constants.RIBBON_LAUNCHER_ICON_ELE + ' ' + (this.launcherIconCss ? this.launcherIconCss : constants.RIBBON_LAUNCHER_ICON),
            id: groupId + constants.LAUNCHER_ID,
            attrs: { 'tabindex': '0', 'type': 'button', 'aria-label': 'Launcher Icon', 'role': 'button' }
        });
        groupContainer.appendChild(launcherIcon);
        groupContainer.classList.add(constants.RIBBON_LAUNCHER);
        EventHandler.add(launcherIcon, 'click', this.launcherIconClicked.bind(this, groupId), this);
        EventHandler.add(launcherIcon, 'keydown', (e) => {
            if (e.key === 'Enter') {
                this.launcherIconClicked(groupId);
            }
        }, this);
        const itemProp = getGroup(this.tabs, groupId);
        if (itemProp.group.launcherIconKeyTip) {
            this.addKeyTip(tabIndex, itemProp.group.launcherIconKeyTip, launcherIcon.id, 'launcher');
        }
    }
    launcherIconClicked(id) {
        const eventArgs = { groupId: id };
        this.trigger('launcherIconClick', eventArgs);
    }
    createGroups(groupList, tabIndex) {
        const groupElements = [];
        for (let i = 0; i < groupList.length; i++) {
            const group = groupList[parseInt(i.toString(), 10)];
            const alignType = group.orientation;
            const groupEle = this.createElement('div', {
                className: group.cssClass,
                id: group.id
            });
            groupEle.classList.add(constants.RIBBON_GROUP);
            groupElements.push(groupEle);
            const groupContainer = this.createElement('div', {
                className: group.cssClass,
                id: group.id + constants.CONTAINER_ID
            });
            groupContainer.classList.add(constants.RIBBON_GROUP_CONTAINER);
            groupEle.appendChild(groupContainer);
            const groupContent = this.createElement('div', {
                className: this.activeLayout === 'Simplified' ? constants.RIBBON_GROUP_CONTENT : (constants.RIBBON_GROUP_CONTENT + constants.SPACE + constants.RIBBON_CONTENT_HEIGHT),
                id: group.id + constants.CONTENT_ID
            });
            groupContent.classList.add(((alignType === 'Column') || (this.activeLayout === 'Simplified')) ? constants.RIBBON_COLUMN : constants.RIBBON_ROW);
            groupContainer.appendChild(groupContent);
            if (this.activeLayout === 'Classic') {
                const groupHeader = this.createElement('div', {
                    className: constants.RIBBON_GROUP_HEADER,
                    id: group.id + constants.HEADER_ID,
                    innerHTML: group.header
                });
                groupContainer.appendChild(groupHeader);
            }
            if (group.showLauncherIcon) {
                this.createLauncherIcon(group.id, groupContainer, tabIndex);
            }
            const elements = this.createCollection(group.collections, group.orientation, group.id, group.header, group.enableGroupOverflow, tabIndex, groupContainer);
            append(elements, groupContent);
            let isItemsHidden = true;
            for (let j = 0; j < elements.length; j++) {
                if (isItemsHidden) {
                    for (let k = 0; k < elements[parseInt(j.toString(), 10)].children.length; k++) {
                        if (!(elements[parseInt(j.toString(), 10)].children[parseInt(k.toString(), 10)].classList.contains('e-hidden'))) {
                            isItemsHidden = false;
                            break;
                        }
                    }
                }
            }
            if (isItemsHidden) {
                groupEle.classList.add('e-hide-group');
            }
            if ((this.activeLayout === 'Simplified') && !(group.enableGroupOverflow || groupEle.querySelector('.' + constants.RIBBON_ITEM))) {
                groupEle.classList.add('e-ribbon-emptyCollection');
            }
            const initialProps = this.initialPropsData[parseInt(tabIndex.toString(), 10)];
            if (initialProps) {
                if (initialProps.hiddenGroups && initialProps.hiddenGroups.length) {
                    this.updateGroupProps('hiddenGroups', initialProps, groupEle);
                }
                if (initialProps.disabledGroups && initialProps.disabledGroups.length) {
                    this.updateGroupProps('disabledGroups', initialProps, groupEle);
                }
            }
        }
        if (this.initialPropsData[parseInt(tabIndex.toString(), 10)]) {
            delete this.initialPropsData[parseInt(tabIndex.toString(), 10)];
        }
        return groupElements;
    }
    updateGroupProps(key, initialProps, groupEle) {
        // eslint-disable-next-line
        let groupIndex = initialProps[key].indexOf(groupEle.id);
        if (groupIndex !== -1) {
            if (key === 'hiddenGroups') {
                groupEle.classList.add('e-hidden');
            }
            else {
                groupEle.classList.add('e-disabled');
            }
        }
    }
    validateItemSize() {
        for (let k = 0; k < this.tabs.length; k++) {
            const groupList = this.tabs[parseInt(k.toString(), 10)].groups;
            for (let l = 0; l < groupList.length; l++) {
                const collectionList = groupList[parseInt(l.toString(), 10)].collections;
                const alignType = groupList[parseInt(l.toString(), 10)].orientation;
                for (let i = 0; i < collectionList.length; i++) {
                    const items = collectionList[parseInt(i.toString(), 10)].items;
                    for (let j = 0; j < items.length; j++) {
                        const ribbonitem = items[parseInt(j.toString(), 10)];
                        if (!ribbonitem.allowedSizes || (ribbonitem.allowedSizes === 0)) {
                            ribbonitem.setProperties({
                                allowedSizes: (RibbonItemSize.Small | RibbonItemSize.Medium | RibbonItemSize.Large)
                            }, true);
                        }
                        if ((ribbonitem.type === 'ColorPicker') && (ribbonitem.allowedSizes !== RibbonItemSize.Small)) {
                            ribbonitem.setProperties({ allowedSizes: RibbonItemSize.Small }, true);
                        }
                        else if ((ribbonitem.type === 'ComboBox' || ribbonitem.type === 'CheckBox') &&
                            (ribbonitem.allowedSizes !== RibbonItemSize.Medium)) {
                            ribbonitem.setProperties({ allowedSizes: RibbonItemSize.Medium }, true);
                        }
                        else if (((alignType === 'Column') && (items.length > 1)) || ((alignType === 'Row') && (collectionList.length > 1))) {
                            if (ribbonitem.allowedSizes & RibbonItemSize.Large) {
                                // To remove large size, perform 'and' with 011(3).
                                let sizeVal = ribbonitem.allowedSizes & (RibbonItemSize.Small | RibbonItemSize.Medium);
                                sizeVal = sizeVal ? sizeVal : RibbonItemSize.Medium;
                                ribbonitem.setProperties({ allowedSizes: sizeVal }, true);
                            }
                        }
                        const itemsize = (ribbonitem.allowedSizes & RibbonItemSize.Large) ? RibbonItemSize.Large :
                            (ribbonitem.allowedSizes & RibbonItemSize.Medium) ? RibbonItemSize.Medium : RibbonItemSize.Small;
                        ribbonitem.setProperties({ activeSize: itemsize }, true);
                    }
                }
            }
        }
    }
    createCollection(collectionList, alignType, groupId, groupHeader, isGroupOF, tabIndex, groupContainer) {
        const collectionElements = [];
        for (let i = 0; i < collectionList.length; i++) {
            const collection = collectionList[parseInt(i.toString(), 10)];
            const collectionEle = this.createElement('div', {
                className: collection.cssClass,
                id: collection.id
            });
            collectionEle.classList.add(constants.RIBBON_COLLECTION);
            collectionEle.classList.add(((alignType !== 'Column') || (this.activeLayout === 'Simplified')) ?
                constants.RIBBON_COLUMN : constants.RIBBON_ROW);
            collectionElements.push(collectionEle);
            const elements = this.createItems(collection.items, alignType, groupId, groupHeader, isGroupOF, tabIndex, groupContainer);
            append(elements, collectionEle);
            if ((alignType === 'Row') && (i === 2)) {
                break;
            }
        }
        return collectionElements;
    }
    createRibbonItem(item, itemEle) {
        switch (item.type) {
            case 'Button':
                this.ribbonButtonModule.createButton(item, itemEle);
                break;
            case 'DropDown':
                this.ribbonDropDownModule.createDropDown(item, itemEle);
                break;
            case 'SplitButton':
                this.ribbonSplitButtonModule.createSplitButton(item, itemEle);
                break;
            case 'CheckBox':
                this.ribbonCheckBoxModule.createCheckBox(item, itemEle);
                break;
            case 'ColorPicker':
                this.ribbonColorPickerModule.createColorPicker(item, itemEle);
                break;
            case 'ComboBox':
                this.ribbonComboBoxModule.createComboBox(item, itemEle);
                break;
            case 'Template':
                this.createTemplateContent(item, itemEle);
                break;
            case 'GroupButton':
                this.ribbonGroupButtonModule.createGroupButton(item, itemEle);
                break;
            case 'Gallery':
                this.ribbonGalleryModule.createGallery(item, itemEle);
                break;
        }
    }
    createItems(itemList, alignType, groupId, groupHeader, isGroupOF, tabIndex, groupContainer) {
        const itemElements = [];
        for (let i = 0; i < itemList.length; i++) {
            let item = itemList[parseInt(i.toString(), 10)];
            //To stop rendering of items with simplified mode position type as none
            let flag = true;
            while (flag &&
                (((this.activeLayout === 'Simplified') && !(item.displayOptions & (DisplayMode.Simplified | DisplayMode.Overflow))) ||
                    ((this.activeLayout === 'Classic') && !(item.displayOptions & DisplayMode.Classic)))) {
                i++;
                if (i < itemList.length) {
                    item = itemList[parseInt(i.toString(), 10)];
                }
                else {
                    flag = false;
                }
            }
            if (!flag) {
                break;
            }
            const itemEle = this.createElement('div', {
                className: item.cssClass,
                id: item.id + constants.CONTAINER_ID
            });
            itemEle.classList.add(constants.RIBBON_ITEM, ...(item.disabled ? [constants.DISABLED_CSS] : []));
            // To avoid undefined items condition is added
            if (item.ribbonTooltipSettings && isTooltipPresent(item.ribbonTooltipSettings)) {
                itemEle.classList.add(constants.RIBBON_TOOLTIP_TARGET);
                this.tooltipData.push({ id: itemEle.id, data: item.ribbonTooltipSettings });
            }
            if (item.type === RibbonItemType.GroupButton) {
                for (let i = 0; i < item.groupButtonSettings.items.length; i++) {
                    if (this.keyTipElements[parseInt(tabIndex.toString(), 10)] &&
                        item.groupButtonSettings.items[parseInt(i.toString(), 10)].keyTip) {
                        this.addKeyTip(tabIndex, item.groupButtonSettings.items[parseInt(i.toString(), 10)].keyTip, item.id + (constants.RIBBON_GROUP_BUTTON_ID + i), 'item');
                    }
                }
            }
            if (item.keyTip) {
                if (item.type === RibbonItemType.Gallery) {
                    this.addKeyTip(tabIndex, item.keyTip, (item.id + '_popupButton'), 'item');
                }
                else {
                    this.addKeyTip(tabIndex, item.keyTip, item.id, 'item');
                }
            }
            let size = item.activeSize;
            if (!(item.type === RibbonItemType.Gallery)) {
                if (this.activeLayout === 'Simplified') {
                    size = ((item.allowedSizes === RibbonItemSize.Large) || (item.allowedSizes & RibbonItemSize.Medium) ||
                        (item.displayOptions === DisplayMode.Overflow)) ? RibbonItemSize.Medium : RibbonItemSize.Small;
                    item.setProperties({ activeSize: size }, true);
                }
                if (size & RibbonItemSize.Large) {
                    itemEle.classList.add(constants.RIBBON_LARGE_ITEM, constants.RIBBON_CONTENT_HEIGHT);
                }
                else {
                    itemEle.classList.add((size & RibbonItemSize.Medium) ? constants.RIBBON_MEDIUM_ITEM : constants.RIBBON_SMALL_ITEM);
                }
            }
            const initialProps = this.initialPropsData[parseInt(tabIndex.toString(), 10)];
            if (initialProps && initialProps.hiddenItems && initialProps.hiddenItems.length) {
                const itemIndex = initialProps.hiddenItems.indexOf(item.id);
                if (itemIndex !== -1) {
                    itemEle.classList.add('e-hidden');
                }
            }
            this.createRibbonItem(item, itemEle);
            if ((this.activeLayout === 'Simplified') && ((item.displayOptions === DisplayMode.Overflow) || (item.displayOptions === (DisplayMode.Classic | DisplayMode.Overflow)))) {
                this.createOverflowPopup(item, tabIndex, isGroupOF, groupId, groupHeader, itemEle, groupContainer);
                if ((item.type === RibbonItemType.DropDown) || (item.type === RibbonItemType.SplitButton) ||
                    (item.type === RibbonItemType.GroupButton) || (item.type === RibbonItemType.Gallery)) {
                    this.updatePopupItems(item, itemEle, isGroupOF, true);
                }
            }
            else {
                // For normal mode and Simplified mode position type as Group and Auto
                itemElements.push(itemEle);
            }
            if ((alignType === 'Column') && (i === 2)) {
                break;
            }
        }
        return itemElements;
    }
    createHelpPaneTemplate() {
        if (this.helpPaneTemplate) {
            const templateName = 'helpPaneTemplate';
            this.clearTemplate([templateName]);
            this.ribbonTempEle = this.createElement('div', {
                className: constants.RIBBON_HELP_TEMPLATE,
                id: this.element.id + constants.RIBBON_HELP_PANE_TEMPLATE_ID
            });
            const templateFunction = getTemplateFunction(this.helpPaneTemplate);
            append(templateFunction({}, this, templateName, 'helpPaneTemplate', this.isStringTemplate), this.ribbonTempEle);
            const tabEle = this.tabObj.element;
            const toolbarEle = tabEle.querySelector('.e-toolbar');
            toolbarEle.after(this.ribbonTempEle);
            tabEle.style.setProperty(constants.RIBBON_HELP_PANE_TEMPLATE_WIDTH, this.ribbonTempEle.offsetWidth + 'px');
            this.renderReactTemplates();
        }
    }
    createTemplateContent(item, itemElement) {
        const itemEle = this.createElement('div', {
            className: item.cssClass ? (constants.RIBBON_TEMPLATE + constants.SPACE + item.cssClass) : constants.RIBBON_TEMPLATE,
            id: item.id,
            attrs: { 'tabindex': '-1' }
        });
        if (item.disabled) {
            itemEle.classList.add(constants.DISABLED_CSS);
            itemEle.setAttribute('disabled', '');
        }
        itemElement.appendChild(itemEle);
        this.renderItemTemplate(item, itemEle);
    }
    renderItemTemplate(item, itemElement) {
        const templateName = 'ribbon' + item.id + 'itemTemplate';
        this.clearTemplate([templateName]);
        const templateFunction = getTemplateFunction(item.itemTemplate);
        append(templateFunction({ activeSize: RibbonItemSize[item.activeSize] }, this, templateName, (item.id + 'itemTemplate'), this.isStringTemplate), itemElement);
        this.renderReactTemplates();
    }
    checkID(list, type, initId) {
        const key = type === 'tab' ? constants.TAB_ID : type === 'group' ? constants.GROUP_ID :
            type === 'collection' ? constants.COLLECTION_ID : constants.ITEM_ID;
        for (let i = 0; i < list.length; i++) {
            const listitem = list[parseInt(i.toString(), 10)];
            if (!listitem.id) {
                let htmlAttrID;
                if (type === 'item') {
                    htmlAttrID = this.hasHtmlAtrrID(listitem);
                }
                listitem.setProperties({ id: htmlAttrID ? htmlAttrID : initId + key + (this.idIndex++) }, true);
            }
            switch (type) {
                case 'tab':
                    listitem.setProperties({ groups: this.checkID(listitem.groups, 'group', listitem.id) }, true);
                    break;
                case 'group':
                    listitem.setProperties({ collections: this.checkID(listitem.collections, 'collection', listitem.id) }, true);
                    break;
                case 'collection':
                    listitem.setProperties({ items: this.checkID(listitem.items, 'item', listitem.id) }, true);
                    break;
                default:
                    break;
            }
        }
        return list;
    }
    hasHtmlAtrrID(listItem) {
        let id = '';
        if (listItem.buttonSettings.htmlAttributes.id) {
            id = listItem.buttonSettings.htmlAttributes.id;
        }
        else if (listItem.checkBoxSettings.htmlAttributes.id) {
            id = listItem.checkBoxSettings.htmlAttributes.id;
        }
        else if (listItem.colorPickerSettings.htmlAttributes.id) {
            id = listItem.colorPickerSettings.htmlAttributes.id;
        }
        else if (listItem.comboBoxSettings.htmlAttributes.id) {
            id = listItem.comboBoxSettings.htmlAttributes.id;
        }
        else if (listItem.dropDownSettings.htmlAttributes.id) {
            id = listItem.dropDownSettings.htmlAttributes.id;
        }
        else if (listItem.splitButtonSettings.htmlAttributes.id) {
            id = listItem.splitButtonSettings.htmlAttributes.id;
        }
        return id;
    }
    updateCommonProperty(commonProp) {
        this.tabObj.setProperties(commonProp);
        if (this.ribbonFileMenuModule) {
            this.ribbonFileMenuModule.setCommonProperties(commonProp);
        }
        if (this.ribbonBackstageModule) {
            this.ribbonBackstageModule.setCommonProperties(commonProp);
        }
        for (let i = 0; i < this.tabs.length; i++) {
            const tab = this.tabs[parseInt(i.toString(), 10)];
            const contentEle = this.tabObj.items[parseInt(i.toString(), 10)].content;
            if (contentEle.querySelector('.' + constants.RIBBON_GROUP)) {
                for (const group of tab.groups) {
                    let dropdownElement;
                    let dropdown;
                    if (this.activeLayout === RibbonLayout.Classic) {
                        dropdownElement = group.isCollapsed ?
                            contentEle.querySelector('#' + group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID) : null;
                    }
                    else {
                        dropdownElement = group.enableGroupOverflow ?
                            contentEle.querySelector('#' + group.id + constants.GROUPOF_BUTTON_ID) : null;
                        dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
                        if (dropdown) {
                            updateTooltipProp(dropdown.target, commonProp);
                            dropdown.setProperties(commonProp);
                        }
                    }
                    for (const collection of group.collections) {
                        for (const item of collection.items) {
                            let ele = null;
                            if (this.activeLayout === RibbonLayout.Classic) {
                                if (item.displayOptions & DisplayMode.Classic) {
                                    ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id) : item.type === RibbonItemType.GroupButton ? contentEle.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID) : contentEle.querySelector('#' + item.id);
                                }
                            }
                            else {
                                //Checks for Simplified and Auto options (Auto = classic + simplified + popup)
                                ele = (item.displayOptions & DisplayMode.Simplified) ? contentEle.querySelector('#' + item.id) : null;
                                // element will be null for "Popup" and if the item is moved to overflow in "Auto" mode
                                if (!ele) {
                                    ele = dropdown.target.querySelector('#' + item.id);
                                    if (item.type === 'DropDown') {
                                        this.updatePopupItems(item, dropdown.target, group.enableGroupOverflow, true);
                                    }
                                }
                            }
                            if (ele) {
                                const moduleName = this.getItemModuleName(item);
                                if (moduleName !== 'template') {
                                    if (moduleName === 'group-btn' && this.activeLayout === 'Classic') {
                                        for (let i = 0; i < item.groupButtonSettings.items.length; i++) {
                                            const btnEle = ele.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID + i);
                                            updateCommonProperty(btnEle, 'btn', commonProp);
                                        }
                                    }
                                    else if (moduleName === 'group-btn' && this.activeLayout === 'Simplified') {
                                        updateCommonProperty(ele, 'dropdown-btn', commonProp);
                                        for (let i = 0; i < item.groupButtonSettings.items.length; i++) {
                                            const btnEle = document.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID + i);
                                            updateCommonProperty(btnEle, 'btn', commonProp);
                                        }
                                    }
                                    else {
                                        updateCommonProperty(ele, moduleName, commonProp);
                                    }
                                }
                                else if (!isNullOrUndefined(commonProp.enableRtl)) {
                                    ele.classList.toggle(constants.RTL_CSS, commonProp.enableRtl);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    removeLauncherIcon(groupId, dropdownElement, contentEle) {
        const containerId = groupId + constants.CONTAINER_ID;
        const containerEle = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, containerId) : contentEle.querySelector('#' + containerId);
        if (containerEle) {
            containerEle.classList.remove(constants.RIBBON_LAUNCHER);
            const launcherIcon = containerEle.querySelector('#' + groupId + constants.LAUNCHER_ID);
            remove(launcherIcon);
        }
    }
    destroyTabItems(tabs) {
        for (let i = 0; i < tabs.length; i++) {
            const tab = tabs[parseInt(i.toString(), 10)];
            const contentEle = this.tabObj.items[parseInt(i.toString(), 10)].content;
            for (const group of tab.groups) {
                let dropdownElement;
                let dropdown;
                if (this.activeLayout === RibbonLayout.Classic) {
                    dropdownElement = group.isCollapsed ?
                        contentEle.querySelector('#' + group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID) : null;
                    if (group.showLauncherIcon) {
                        this.removeLauncherIcon(group.id, dropdownElement, contentEle);
                    }
                }
                else {
                    dropdownElement = group.enableGroupOverflow ?
                        contentEle.querySelector('#' + group.id + constants.GROUPOF_BUTTON_ID) : null;
                    dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
                }
                for (const collection of group.collections) {
                    for (const item of collection.items) {
                        let ele;
                        if (this.activeLayout === RibbonLayout.Classic) {
                            if (item.displayOptions & DisplayMode.Classic) {
                                ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id) :
                                    contentEle.querySelector('#' + item.id);
                                if (item.type === RibbonItemType.GroupButton) {
                                    ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id +
                                        constants.RIBBON_GROUP_BUTTON_ID) : contentEle.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID);
                                }
                                if (item.type === RibbonItemType.Gallery) {
                                    ele = contentEle.querySelector('#' + item.id + constants.CONTAINER_ID);
                                }
                            }
                        }
                        else {
                            //Checks for Simplified and Auto options (Auto = classic + simplified + popup)
                            ele = (item.displayOptions & DisplayMode.Simplified) ?
                                contentEle.querySelector('#' + item.id) : null;
                            if (item.type === RibbonItemType.Gallery) {
                                ele = (item.displayOptions & DisplayMode.Simplified) ? contentEle.querySelector('#' + item.id + constants.CONTAINER_ID) : null;
                            }
                            // element will be null for "Popup" and if the item is moved to overflow in "Auto" mode
                            if (!ele) {
                                ele = dropdown ? dropdown.target.querySelector('#' + item.id) : null;
                            }
                        }
                        if (ele) {
                            this.destroyFunction(item, ele);
                        }
                    }
                }
                if ((this.activeLayout === RibbonLayout.Classic) && dropdownElement) {
                    this.ribbonDropDownModule.removeOverFlowDropDown(dropdownElement);
                }
                else if ((this.activeLayout === RibbonLayout.Simplified) && group.enableGroupOverflow && dropdownElement) {
                    this.removeOverflowButton(dropdown);
                }
            }
        }
        if (this.overflowDDB) {
            this.removeOverflowButton(this.overflowDDB);
            this.overflowDDB = null;
        }
    }
    destroyFunction(item, ele) {
        const moduleName = this.getItemModuleName(item);
        if (moduleName === 'colorpicker') {
            this.ribbonColorPickerModule.unwireColorPickerEvents(ele);
        }
        else if (moduleName === 'group-btn') {
            if (this.activeLayout === 'Classic') {
                for (let i = 0; i < item.groupButtonSettings.items.length; i++) {
                    const btnEle = ele.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID + i);
                    if (btnEle) {
                        destroyControl(btnEle, 'btn');
                    }
                }
            }
            else {
                this.ribbonGroupButtonModule.destroyDropDown(item);
            }
            for (let i = 0; i < item.groupButtonSettings.items.length; i++) {
                if (item.groupButtonSettings.items[parseInt(i.toString(), 10)].ribbonTooltipSettings) {
                    const groupButtonID = item.id + constants.RIBBON_GROUP_BUTTON_ID + i;
                    const index = getIndex(this.tooltipData, (e) => { return e.id === groupButtonID; });
                    if (index !== -1) {
                        this.tooltipData.splice(index, 1);
                    }
                }
            }
        }
        else if (moduleName === 'gallery') {
            if (ele.closest('.e-ribbon-overflow-target')) {
                destroyControl(ele, 'dropdown-btn');
                const galleryPopupEle = Array.prototype.slice.call(document.querySelectorAll('#' + item.id + '_galleryPopup'));
                galleryPopupEle.concat(Array.prototype.slice.call(document.querySelectorAll('#' + item.id + '-popup')));
                for (let i = 0; i < galleryPopupEle.length; i++) {
                    galleryPopupEle[parseInt(i.toString(), 10)].remove();
                }
                const galleryPopupBtn = document.querySelector('#' + item.id + '_popupButton');
                if (galleryPopupBtn) {
                    galleryPopupBtn.remove();
                }
                const galleryWrapper = document.querySelector('#' + item.id + '_galleryWrapper');
                if (galleryWrapper) {
                    galleryWrapper.remove();
                }
            }
            else {
                const galleryEle = ele.querySelectorAll('.e-ribbon-gallery-item');
                const galleryPopupBtn = ele.parentElement.querySelector('#' + item.id + '_popupButton');
                if (galleryPopupBtn) {
                    galleryPopupBtn.remove();
                }
                for (let i = 0; i < galleryEle.length; i++) {
                    galleryEle[parseInt(i.toString(), 10)].remove();
                }
                const galleryPopupEle = document.querySelectorAll('#' + item.id + '_galleryPopup');
                for (let i = 0; i < galleryPopupEle.length; i++) {
                    galleryPopupEle[parseInt(i.toString(), 10)].remove();
                }
            }
        }
        else if (moduleName !== 'template') {
            destroyControl(ele, moduleName);
        }
        if (item.ribbonTooltipSettings) {
            const index = getIndex(this.tooltipData, (e) => { return e.id === item.id + constants.CONTAINER_ID; });
            if (index !== -1) {
                this.tooltipData.splice(index, 1);
            }
        }
        if (item.type === 'GroupButton') {
            this.ribbonGroupButtonModule.destroyDropDown(item);
        }
    }
    /**
     * Gets the item module name.
     *
     * @param {RibbonItemModel} item - Gets the ribbon item model.
     * @returns {void}
     * @hidden
     */
    getItemModuleName(item) {
        switch (item.type) {
            case 'Button':
                return 'btn';
            case 'DropDown':
                return 'dropdown-btn';
            case 'SplitButton':
                return 'split-btn';
            case 'CheckBox':
                return 'checkbox';
            case 'ColorPicker':
                return 'colorpicker';
            case 'ComboBox':
                return 'combobox';
            case 'GroupButton':
                return 'group-btn';
            case 'Gallery':
                return 'gallery';
            default:
                return 'template';
        }
    }
    clearOverflowResize() {
        this.destroyScroll();
        this.clearOverflowDropDown(this.selectedTab);
        const tab = this.tabs[this.selectedTab];
        const activeContent = this.tabObj.element.querySelector('#' + this.tabs[this.selectedTab].id + constants.CONTENT_ID);
        const tabContent = activeContent.closest('.' + constants.TAB_CONTENT);
        for (let j = 0; (j < tab.groups.length); j++) {
            this.checkSmallToMedium(this.selectedTab, tab, j, tabContent, activeContent, true, true);
            this.checkMediumToLarge(this.selectedTab, tab, j, tabContent, activeContent, true, true);
        }
    }
    /**
     * Refreshes the layout.
     *
     * @returns {void}
     */
    refreshLayout() {
        this.resizeHandler();
    }
    /**
     * Selects the tab
     *
     * @param  {string} tabId - Gets the tab ID
     * @returns {void}
     */
    selectTab(tabId) {
        const index = this.tabObj.getItemIndex(tabId);
        this.setProperties({ selectedTab: index });
    }
    /**
     * Shows a specific tab in the ribbon.
     *
     * @param {string} tabId - The ID of the tab to be shown.
     * @param {boolean} isContextual - The boolean if the rendering is contextual.
     * @returns {void}
     */
    showTab(tabId, isContextual = false) {
        this.showHideTab(tabId, false, isContextual);
    }
    /**
     * Hides a specific tab in the ribbon.
     *
     * @param {string} tabId - The ID of the tab to be hidden.
     * @param {boolean} isContextual - The boolean if the rendering is contextual.
     * @returns {void}
     */
    hideTab(tabId, isContextual = false) {
        this.showHideTab(tabId, true, isContextual);
    }
    showHideTab(tabId, value, isContextual) {
        const index = this.tabObj.getItemIndex(tabId);
        if (index === -1) {
            return;
        }
        this.tabObj.hideTab(index, value);
        if (isContextual) {
            let contextualTab;
            const tabEle = this.tabObj.element;
            for (let i = 0; i < this.contextualTabs.length; i++) {
                for (let j = 0; j < this.contextualTabs[parseInt(i.toString(), 10)].tabs.length; j++) {
                    if (tabId === this.contextualTabs[parseInt(i.toString(), 10)].tabs[parseInt(j.toString(), 10)].id) {
                        contextualTab = this.contextualTabs[parseInt(i.toString(), 10)];
                    }
                }
            }
            if (contextualTab) {
                let isTabHidden = true;
                for (let i = 0; i < contextualTab.tabs.length; i++) {
                    const index = getIndex(this.tabs, (e) => {
                        return e.id === contextualTab.tabs[parseInt(i.toString(), 10)].id;
                    });
                    if (index !== -1) {
                        const toolbarEle = tabEle.querySelectorAll('.e-toolbar-item')[parseInt(index.toString(), 10)];
                        if (!(toolbarEle.classList.contains('e-hidden'))) {
                            isTabHidden = false;
                        }
                    }
                }
                if (isTabHidden) {
                    contextualTab.setProperties({ visible: false }, true);
                }
                else {
                    contextualTab.setProperties({ visible: true }, true);
                }
            }
        }
    }
    /**
     * Enables a specific tab in the ribbon.
     *
     * @param {string} tabId - The ID of the tab to be enabled.
     * @returns {void}
     */
    enableTab(tabId) {
        this.enableDisableTab(tabId, true);
    }
    /**
     * Disables a specific tab in the ribbon.
     *
     * @param {string} tabId - The ID of the tab to be disabled.
     * @returns {void}
     */
    disableTab(tabId) {
        this.enableDisableTab(tabId, false);
    }
    enableDisableTab(tabId, value) {
        const index = getIndex(this.tabs, (e) => { return e.id === tabId; });
        if (index === -1) {
            return;
        }
        const tbItems = this.tabObj.items[parseInt(index.toString(), 10)].content;
        this.tabObj.element.querySelector('#' + tabId + constants.HEADER_ID).classList[value ? 'remove' : 'add']('e-disabled');
        tbItems.classList[value ? 'remove' : 'add']('e-disabled');
        this.tabObj.enableTab(index, value);
    }
    /**
     * Adds the ribbon tab.
     *
     * @param {RibbonTabModel} tab - Gets the ribbon tab model
     * @param {string} targetId  - Gets the ID of the target tab to add the new tab.
     * @param {boolean} isAfter - Defines whether the tab is added before or after the target.
     * @returns {void}
     */
    addTab(tab, targetId, isAfter) {
        let index = targetId ? getIndex(this.tabs, (e) => e.id === targetId) : -1;
        index = (index === -1) ? this.tabs.length : (index + (isAfter ? 1 : 0));
        this.tabsInternal = this.tabs.slice();
        this.tabsInternal.splice(index, 0, tab);
        this.setProperties({ tabs: this.tabsInternal }, true);
        this.checkID([this.tabs[parseInt(index.toString(), 10)]], 'tab', this.element.id);
        this.tabsInternal = this.tabs.slice();
        this.validateItemSize();
        const tabItem = this.createTabItems([tab]);
        this.tabObj.addTab(tabItem, index);
    }
    /**
     * Removes the ribbon tab.
     *
     * @param {string} tabId - Gets the tab ID
     * @returns {void}
     */
    removeTab(tabId) {
        const index = getIndex(this.tabs, (e) => { return e.id === tabId; });
        if (index === -1) {
            return;
        }
        const contentEle = this.tabObj.items[parseInt(index.toString(), 10)].content;
        const groups = this.tabs[parseInt(index.toString(), 10)].groups;
        if (groups && (contentEle.innerHTML !== '')) {
            for (const group of groups) {
                const dropdownElement = group.isCollapsed ? contentEle.querySelector('#' + group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID) : null;
                for (const collection of group.collections) {
                    for (const item of collection.items) {
                        let ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id) : contentEle.querySelector('#' + item.id);
                        if (item.type === RibbonItemType.GroupButton && this.activeLayout === 'Classic') {
                            ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id +
                                constants.RIBBON_GROUP_BUTTON_ID) : contentEle.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID);
                        }
                        if (ele) {
                            this.destroyFunction(item, ele);
                        }
                    }
                }
                if (dropdownElement) {
                    this.ribbonDropDownModule.removeOverFlowDropDown(dropdownElement);
                }
            }
        }
        if (index === this.selectedTab) {
            this.isAddRemove = true;
        }
        this.tabsInternal = this.tabs.slice();
        this.tabsInternal.splice(index, 1);
        this.setProperties({ tabs: this.tabsInternal }, true);
        this.tabObj.removeTab(index);
    }
    /**
     * Adds the ribbon group.
     *
     * @param {string} tabId - Gets the tab ID.
     * @param {RibbonGroupModel} group - Gets the ribbon group model.
     * @param {string} targetId - Gets the ID of the target group to add the new group.
     * @param {boolean} isAfter - Defines whether the group is added before or after the target.
     * @returns {void}
     */
    addGroup(tabId, group, targetId, isAfter) {
        const tabIndex = getIndex(this.tabs, (e) => { return e.id === tabId; });
        if (tabIndex === -1) {
            return;
        }
        if (this.selectedTab === tabIndex) {
            this.clearOverflowResize();
        }
        const tab = this.tabs[parseInt(tabIndex.toString(), 10)];
        const ribbonGroups = tab.groups.slice();
        let index = targetId ? getIndex(ribbonGroups, (e) => { return e.id === targetId; }) : -1;
        index = (index === -1) ? ribbonGroups.length : (index + (isAfter ? 1 : 0));
        ribbonGroups.splice(index, 0, group);
        tab.setProperties({ groups: ribbonGroups }, true);
        this.checkID([tab.groups[parseInt(index.toString(), 10)]], 'group', tabId);
        this.validateItemSize();
        //Check whether the tab items are rendered
        const contentEle = this.tabObj.items[parseInt(tabIndex.toString(), 10)].content;
        if (contentEle.innerHTML !== '') {
            const element = this.createGroups([tab.groups[parseInt(index.toString(), 10)]], tabIndex)[0];
            //insert the element in tab items property.
            const targetEle = targetId ? contentEle.querySelector('#' + targetId) : null;
            if (targetEle) {
                targetEle.insertAdjacentElement(isAfter ? 'afterend' : 'beforebegin', element);
            }
            else {
                contentEle.append(element);
            }
        }
        if (this.selectedTab === tabIndex) {
            this.refreshLayout();
        }
    }
    /**
     * Removes the ribbon group.
     *
     * @param {string} groupId -Gets the group ID.
     * @returns {void}
     */
    removeGroup(groupId) {
        const itemProp = getGroup(this.tabs, groupId);
        if (!itemProp) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        //Check whether the tab items are rendered
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        if (contentEle.innerHTML !== '') {
            let dropdownElement;
            let dropdown;
            if (itemProp.group.showLauncherIcon) {
                this.removeLauncherIcon(itemProp.group.id, null, contentEle);
            }
            if (this.activeLayout === RibbonLayout.Simplified) {
                dropdownElement = itemProp.group.enableGroupOverflow ?
                    contentEle.querySelector('#' + itemProp.group.id + constants.GROUPOF_BUTTON_ID) : null;
                dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
            }
            for (const collection of itemProp.group.collections) {
                for (const item of collection.items) {
                    this.removeItemElement(contentEle, item, dropdown);
                }
            }
            if (this.activeLayout === RibbonLayout.Simplified) {
                if (itemProp.group.enableGroupOverflow) {
                    if (dropdown.target.childElementCount === 0 || (dropdown.target.childElementCount
                        === 1 && this.isHeaderVisible(dropdown.target, itemProp.group.id))) {
                        this.removeOverflowButton(dropdown);
                    }
                }
                else {
                    const ofGroupContainer = dropdown.target.querySelector('#' + itemProp.group.id + constants.CONTAINER_ID);
                    if (ofGroupContainer && ofGroupContainer.childElementCount === 1) {
                        ofGroupContainer.remove();
                    }
                    const ofTabContainer = dropdown.target.querySelector('#' + this.tabs[parseInt(itemProp.tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                    if (ofTabContainer && ofTabContainer.childElementCount === 0) {
                        ofTabContainer.remove();
                    }
                }
            }
            const groupEle = contentEle.querySelector('#' + groupId);
            if (groupEle) {
                groupEle.remove();
            }
        }
        const ribbonGroups = this.tabs[itemProp.tabIndex].groups.slice();
        ribbonGroups.splice(itemProp.groupIndex, 1);
        this.tabs[itemProp.tabIndex].setProperties({ groups: ribbonGroups }, true);
        if (this.selectedTab === itemProp.tabIndex) {
            this.refreshLayout();
        }
    }
    isHeaderVisible(headerEle, groupID) {
        return headerEle.querySelector('#' + groupID + constants.GROUPOF_BUTTON_ID + constants.HEADER_ID) ? true : false;
    }
    /**
     * Hides a specific group within a ribbon tab.
     *
     * @param {string} groupID - The ID of the group to be hidden.
     * @returns {void}
     */
    hideGroup(groupID) {
        this.showHideGroup(groupID, true);
    }
    /**
     * Shows a specific group within a ribbon tab.
     *
     * @param {string} groupID - The ID of the group to be shown.
     * @returns {void}
     */
    showGroup(groupID) {
        this.showHideGroup(groupID, false);
    }
    showHideGroup(groupID, isHidden) {
        let overflowDDB;
        let overflowtarget;
        const itemProp = getGroup(this.tabs, groupID);
        if (!itemProp) {
            return;
        }
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        const groupEle = contentEle.querySelector('#' + groupID);
        if (groupEle) {
            this.updateHiddenElements(itemProp.tabIndex, isHidden ? 'hideGroup' : 'showGroup', groupID, isHidden, groupEle);
        }
        else {
            this.updateInitialProps(itemProp.tabIndex, groupID, 'hiddenGroups', isHidden);
        }
        if (this.overflowDDB) {
            const overflowEle = this.overflowDDB.target;
            const ofTabContainer = overflowEle.querySelector('#' + this.tabs[parseInt(itemProp.tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
            if (itemProp.group.enableGroupOverflow) {
                const overflowDDBEle = contentEle.querySelector('#' + groupID + constants.GROUPOF_BUTTON_ID);
                if (overflowDDBEle) {
                    overflowDDB = getInstance(overflowDDBEle, DropDownButton);
                    overflowtarget = overflowDDB.target;
                }
                if (overflowtarget) {
                    if (isHidden) {
                        overflowtarget.classList.add('e-hidden');
                    }
                    else {
                        overflowtarget.classList.remove('e-hidden');
                    }
                }
            }
            else if (ofTabContainer) {
                const grpContainer = ofTabContainer.querySelector('#' + groupID + constants.CONTAINER_ID);
                if (grpContainer) {
                    if (isHidden) {
                        grpContainer.classList.add('e-hidden');
                    }
                    else {
                        grpContainer.classList.remove('e-hidden');
                    }
                }
            }
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.refreshLayout();
        }
    }
    updateHiddenElements(tabIndex, key, id, isHidden, element, group) {
        if (isHidden) {
            if (!(element.classList.contains('e-hidden'))) {
                this.checkHiddenElements(key, id, tabIndex);
                element.classList.add('e-hidden');
                if (key === 'hideItem') {
                    this.checkHiddenItems(group, isHidden, tabIndex);
                }
                this.calculateHiddenElementsWidth(tabIndex);
            }
        }
        else {
            if (element.classList.contains('e-hidden')) {
                this.checkHiddenElements(key, id, tabIndex);
                element.classList.remove('e-hidden');
                this.calculateHiddenElementsWidth(tabIndex);
                if (key === 'showItem') {
                    this.checkHiddenItems(group, isHidden, tabIndex);
                }
            }
        }
    }
    checkHiddenElements(key, id, tabIndex) {
        if (this.activeLayout === 'Simplified') {
            let hiddenProps = this.hiddenElements[parseInt(tabIndex.toString(), 10)];
            if (!hiddenProps) {
                this.hiddenElements[parseInt(tabIndex.toString(), 10)] = {};
                hiddenProps = this.hiddenElements[parseInt(tabIndex.toString(), 10)];
            }
            if (hiddenProps) {
                if (!hiddenProps[`${key}`]) {
                    hiddenProps[`${key}`] = [];
                }
                if (hiddenProps[`${key}`].length) {
                    const index = hiddenProps[`${key}`].indexOf(id);
                    if (index === -1) {
                        hiddenProps[`${key}`].push(id);
                    }
                }
                else {
                    hiddenProps[`${key}`].push(id);
                }
            }
        }
    }
    updateItemsSimplifiedWidth(tabIndex, key) {
        const hiddenProps = this.hiddenElements[parseInt(tabIndex.toString(), 10)];
        if (hiddenProps && hiddenProps[`${key}`] && hiddenProps[`${key}`].length) {
            for (let i = 0; i < hiddenProps[`${key}`].length; i++) {
                const contentEle = this.tabObj.items[parseInt(tabIndex.toString(), 10)].content;
                let hiddenEle;
                let groupEle;
                let isGroupHidden = false;
                let widthDifference = 0;
                if (key === 'hideGroup' || key === 'showGroup') {
                    hiddenEle = contentEle.querySelector('#' + hiddenProps[`${key}`][parseInt(i.toString(), 10)]);
                }
                else {
                    hiddenEle = contentEle.querySelector('#' + hiddenProps[`${key}`][parseInt(i.toString(), 10)] + constants.CONTAINER_ID);
                }
                if (hiddenEle) {
                    if (key === 'hideGroup' || key === 'hideItem') {
                        let isHidden = false;
                        if (hiddenEle.classList.contains('e-hidden')) {
                            isHidden = true;
                            hiddenEle.classList.remove('e-hidden');
                        }
                        if (key === 'hideItem') {
                            groupEle = hiddenEle.closest('.e-ribbon-group');
                            if (groupEle.classList.contains('e-hide-group')) {
                                isGroupHidden = true;
                                widthDifference = this.checkWidthDifference(hiddenEle, groupEle);
                            }
                        }
                        this.calculateOverflowItemsWidth(hiddenEle.offsetWidth + widthDifference, true, tabIndex);
                        this.calculateMediumDataWidth(hiddenEle.offsetWidth + widthDifference, tabIndex, true);
                        if (isHidden) {
                            hiddenEle.classList.add('e-hidden');
                        }
                    }
                    else {
                        if (key === 'showItem') {
                            groupEle = hiddenEle.closest('.e-ribbon-group');
                            if (groupEle.classList.contains('e-hide-group')) {
                                isGroupHidden = true;
                                groupEle.classList.remove('e-hide-group');
                                groupEle.classList.remove('e-ribbon-emptyCollection');
                                widthDifference = Math.abs(hiddenEle.offsetWidth - groupEle.offsetWidth);
                                if (this.hiddenGroups.indexOf(groupEle.id) !== -1) {
                                    this.hiddenGroups.splice(this.hiddenGroups.indexOf(groupEle.id), 1);
                                }
                            }
                            else {
                                if (this.hiddenGroups.indexOf(groupEle.id) !== -1) {
                                    const hiddenItems = groupEle.querySelectorAll('.e-ribbon-item:not(.e-hidden)');
                                    hiddenItems.forEach((item) => {
                                        if (item.id !== hiddenEle.id) {
                                            item.classList.add('e-hidden');
                                        }
                                    });
                                    widthDifference = Math.abs(hiddenEle.offsetWidth - groupEle.offsetWidth);
                                    hiddenItems.forEach((item) => {
                                        if (item.id !== hiddenEle.id) {
                                            item.classList.remove('e-hidden');
                                        }
                                    });
                                    this.hiddenGroups.splice(this.hiddenGroups.indexOf(groupEle.id), 1);
                                }
                            }
                        }
                        this.calculateOverflowItemsWidth(hiddenEle.offsetWidth + widthDifference, false, tabIndex);
                        this.calculateMediumDataWidth(hiddenEle.offsetWidth + widthDifference, tabIndex, false);
                    }
                    if (isGroupHidden) {
                        groupEle.classList.add('e-hide-group');
                        groupEle.classList.add('e-ribbon-emptyCollection');
                    }
                }
                const index = hiddenProps[`${key}`].indexOf(hiddenProps[`${key}`][parseInt(i.toString(), 10)]);
                if (index !== -1) {
                    hiddenProps[`${key}`].splice(index, 1);
                    i--;
                }
            }
        }
    }
    checkWidthDifference(hiddenEle, groupEle) {
        let widthDifference = 0;
        groupEle.classList.remove('e-hide-group');
        groupEle.classList.remove('e-ribbon-emptyCollection');
        if (this.hiddenGroups.length) {
            if (this.hiddenGroups.indexOf(groupEle.id) === -1) {
                this.hiddenGroups.push(groupEle.id);
                widthDifference = Math.abs(hiddenEle.offsetWidth - groupEle.offsetWidth);
            }
        }
        else {
            this.hiddenGroups.push(groupEle.id);
            widthDifference = Math.abs(hiddenEle.offsetWidth - groupEle.offsetWidth);
        }
        return widthDifference;
    }
    calculateHiddenElementsWidth(tabIndex) {
        if (tabIndex === this.selectedTab && this.activeLayout === 'Simplified') {
            const hiddenProps = this.hiddenElements[parseInt(tabIndex.toString(), 10)];
            if (hiddenProps) {
                for (let i = 0; i < Object.keys(hiddenProps).length; i++) {
                    this.updateItemsSimplifiedWidth(tabIndex, Object.keys(hiddenProps)[parseInt(i.toString(), 10)]);
                }
            }
        }
    }
    calculateMediumDataWidth(hiddenWidth, tabIndex, isHidden) {
        if (this.selectedTab === tabIndex && this.activeLayout === 'Simplified') {
            const activeContent = this.tabObj.element.querySelector('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.CONTENT_ID);
            const mediumDataItems = Array.prototype.slice.call(activeContent.querySelectorAll('.e-ribbon-item'));
            if (this.overflowDDB) {
                const overflowEle = this.overflowDDB.target;
                const overflowItems = overflowEle.querySelectorAll('.e-ribbon-item');
                const selectedOFTab = document.querySelector('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                for (let i = 0; i < overflowItems.length; i++) {
                    const ofTab = overflowItems[parseInt(i.toString(), 10)].closest('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                    if (selectedOFTab && ofTab && selectedOFTab.id === ofTab.id && overflowItems[parseInt(i.toString(), 10)].hasAttribute('data-medium-width')) {
                        mediumDataItems.push(overflowItems[parseInt(i.toString(), 10)]);
                    }
                }
            }
            const groupOFButton = activeContent.querySelectorAll('.e-ribbon-group-of-btn');
            for (let i = 0; i < groupOFButton.length; i++) {
                const overflowButton = getInstance(groupOFButton[parseInt(i.toString(), 10)], DropDownButton);
                const overflowBtnTarget = overflowButton.target;
                const overflowItems = overflowBtnTarget.querySelectorAll('.e-ribbon-item');
                for (let i = 0; i < overflowItems.length; i++) {
                    if (overflowItems[parseInt(i.toString(), 10)].hasAttribute('data-medium-width')) {
                        mediumDataItems.push(overflowItems[parseInt(i.toString(), 10)]);
                    }
                }
            }
            for (let i = 0; i < mediumDataItems.length; i++) {
                if (mediumDataItems[parseInt(i.toString(), 10)].hasAttribute('data-medium-width')) {
                    const previousWidth = parseInt(mediumDataItems[parseInt(i.toString(), 10)].getAttribute('data-medium-width'), 10);
                    mediumDataItems[parseInt(i.toString(), 10)].setAttribute('data-medium-width', isHidden ? (previousWidth - hiddenWidth).toString() : (previousWidth + hiddenWidth).toString());
                }
            }
        }
    }
    calculateOverflowItemsWidth(hiddenItem, isHidden, tabIndex) {
        if (this.selectedTab === tabIndex && this.activeLayout === 'Simplified') {
            const groupList = this.tabs[parseInt(tabIndex.toString(), 10)].groups;
            for (let i = 0; i < groupList.length; i++) {
                const group = groupList[parseInt(i.toString(), 10)];
                if (group.enableGroupOverflow) {
                    const groupContainer = document.querySelector('#' + group.id);
                    let overflowButton;
                    const overflowDDB = groupContainer.querySelector('#' + group.id + constants.GROUPOF_BUTTON_ID);
                    if (overflowDDB) {
                        overflowButton = getInstance(overflowDDB, DropDownButton);
                    }
                    if (overflowButton) {
                        const overflowEle = overflowButton.target;
                        const overflowItems = overflowEle.querySelectorAll('.e-ribbon-item');
                        for (let i = 0; i < overflowItems.length; i++) {
                            const previousWidth = parseInt(overflowItems[parseInt(i.toString(), 10)].getAttribute('data-simplified-width'), 10);
                            if (previousWidth) {
                                overflowItems[parseInt(i.toString(), 10)].setAttribute('data-simplified-width', isHidden ? (previousWidth - hiddenItem).toString() : (previousWidth + hiddenItem).toString());
                            }
                        }
                    }
                }
            }
        }
        if (this.overflowDDB) {
            const selectedOFTab = document.querySelector('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
            const overflowEle = this.overflowDDB.target;
            const overflowItems = overflowEle.querySelectorAll('.e-ribbon-item');
            for (let i = 0; i < overflowItems.length; i++) {
                const ofTab = overflowItems[parseInt(i.toString(), 10)].closest('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                if (selectedOFTab && ofTab && selectedOFTab.id === ofTab.id) {
                    const previousWidth = parseInt(overflowItems[parseInt(i.toString(), 10)].getAttribute('data-simplified-width'), 10);
                    if (previousWidth) {
                        overflowItems[parseInt(i.toString(), 10)].setAttribute('data-simplified-width', isHidden ? (previousWidth - hiddenItem).toString() : (previousWidth + hiddenItem).toString());
                    }
                }
            }
        }
    }
    /**
     * Disables a specific group within a ribbon tab.
     *
     * @param {string} groupID - The ID of the group to be disabled.
     * @returns {void}
     */
    disableGroup(groupID) {
        this.enableDisableGroup(groupID, true);
    }
    /**
     * Enables a specific group within a ribbon tab.
     *
     * @param {string} groupID - The ID of the group to be enabled.
     * @returns {void}
     */
    enableGroup(groupID) {
        this.enableDisableGroup(groupID, false);
    }
    enableDisableGroup(groupID, isDisabled) {
        let overflowDDB;
        let overflowtarget;
        const itemProp = getGroup(this.tabs, groupID);
        if (!itemProp) {
            return;
        }
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        const groupEle = contentEle.querySelector('#' + groupID);
        if (groupEle) {
            if (isDisabled) {
                groupEle.classList.add('e-disabled');
            }
            else {
                groupEle.classList.remove('e-disabled');
            }
        }
        else {
            this.updateInitialProps(itemProp.tabIndex, groupID, 'disabledGroups', isDisabled);
        }
        if (this.overflowDDB) {
            const overflowEle = this.overflowDDB.target;
            const ofTabContainer = overflowEle.querySelector('#' + this.tabs[parseInt(itemProp.tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
            if (itemProp.group.enableGroupOverflow) {
                const overflowDDBEle = contentEle.querySelector('#' + groupID + constants.GROUPOF_BUTTON_ID);
                if (overflowDDBEle) {
                    overflowDDB = getInstance(overflowDDBEle, DropDownButton);
                    overflowtarget = overflowDDB.target;
                }
                if (overflowtarget) {
                    if (isDisabled) {
                        overflowtarget.classList.add('e-disabled');
                    }
                    else {
                        overflowtarget.classList.remove('e-disabled');
                    }
                }
            }
            else if (ofTabContainer) {
                const grpContainer = ofTabContainer.querySelector('#' + groupID + constants.CONTAINER_ID);
                if (grpContainer) {
                    if (isDisabled) {
                        grpContainer.classList.add('e-disabled');
                    }
                    else {
                        grpContainer.classList.remove('e-disabled');
                    }
                }
            }
        }
    }
    /**
     * adds the ribbon collection.
     *
     * @param {string} groupId - Gets the ribbon group ID.
     * @param {RibbonCollectionModel} collection - Gets the ribbon collection model.
     * @param {string} targetId - Gets the ID of the target collection to add the new collection.
     * @param {boolean} isAfter - Defines whether the collection is added before or after the target.
     * @returns {void}
     */
    addCollection(groupId, collection, targetId, isAfter) {
        const itemProp = getGroup(this.tabs, groupId);
        if (!itemProp) {
            return;
        }
        if ((itemProp.group.orientation === 'Row') && (itemProp.group.collections.length === 3)) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        const ribbonCollections = itemProp.group.collections.slice();
        let index = targetId ? getIndex(ribbonCollections, (e) => { return e.id === targetId; }) : -1;
        index = (index === -1) ? ribbonCollections.length : (index + (isAfter ? 1 : 0));
        ribbonCollections.splice(index, 0, collection);
        itemProp.group.setProperties({ collections: ribbonCollections }, true);
        this.checkID([itemProp.group.collections[parseInt(index.toString(), 10)]], 'collection', groupId);
        this.validateItemSize();
        let contentEle = this.tabObj.items[itemProp.tabIndex].content;
        if (contentEle.innerHTML !== '') {
            const collection = itemProp.group.collections[parseInt(index.toString(), 10)];
            const element = this.createCollection([collection], itemProp.group.orientation, itemProp.group.id, itemProp.group.header, itemProp.group.enableGroupOverflow, itemProp.tabIndex)[0];
            if (itemProp.group.isCollapsed) {
                contentEle = this.ribbonDropDownModule.getOverflowDropDownPopup(itemProp, contentEle);
            }
            //insert the element in tab items property.
            const targetEle = targetId ? contentEle.querySelector('#' + targetId) : null;
            if (targetEle) {
                targetEle.insertAdjacentElement(isAfter ? 'afterend' : 'beforebegin', element);
            }
            else {
                contentEle.querySelector('#' + groupId + constants.CONTENT_ID).append(element);
            }
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.refreshLayout();
        }
    }
    /**
     * Removes the ribbon collection.
     *
     * @param {string} collectionId - Gets the collection ID.
     * @returns {void}
     */
    removeCollection(collectionId) {
        const itemProp = getCollection(this.tabs, collectionId);
        if (!itemProp) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        //Check whether the tab items are rendered
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        if (contentEle.innerHTML !== '') {
            let dropdownElement;
            let dropdown;
            if (this.activeLayout === RibbonLayout.Simplified) {
                dropdownElement = itemProp.group.enableGroupOverflow ?
                    contentEle.querySelector('#' + itemProp.group.id + constants.GROUPOF_BUTTON_ID) : null;
                dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
            }
            for (const item of itemProp.collection.items) {
                this.removeItemElement(contentEle, item, dropdown);
            }
            const groupEle = contentEle.querySelector('#' + collectionId);
            if (groupEle) {
                groupEle.remove();
            }
        }
        const ribbonGroup = itemProp.group;
        const ribbonCollections = ribbonGroup.collections.slice();
        ribbonCollections.splice(itemProp.collectionIndex, 1);
        ribbonGroup.setProperties({ collections: ribbonCollections }, true);
        if (this.selectedTab === itemProp.tabIndex) {
            this.refreshLayout();
        }
    }
    /**
     * Adds ribbon item.
     *
     * @param {string} collectionId - Gets the collection ID.
     * @param {RibbonItemModel} item - Gets the ribbon item model.
     * @param {string} targetId - Gets the ID of the target item to add the new item.
     * @param {boolean} isAfter - Defines whether the item is added before or after the target.
     * @returns {void}
     */
    addItem(collectionId, item, targetId, isAfter) {
        const itemProp = getCollection(this.tabs, collectionId);
        if (!itemProp) {
            return;
        }
        if ((itemProp.group.orientation === 'Column') && (itemProp.collection.items.length === 3)) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        const ribbonItems = itemProp.collection.items.slice();
        let index = targetId ? getIndex(ribbonItems, (e) => { return e.id === targetId; }) : -1;
        index = (index === -1) ? ribbonItems.length : (index + (isAfter ? 1 : 0));
        ribbonItems.splice(index, 0, item);
        itemProp.collection.setProperties({ items: ribbonItems }, true);
        this.checkID([itemProp.collection.items[parseInt(index.toString(), 10)]], 'item', collectionId);
        this.validateItemSize();
        let contentEle = this.tabObj.items[itemProp.tabIndex].content;
        const groupContainer = contentEle.querySelector('#' + itemProp.group.id + constants.CONTAINER_ID);
        if (contentEle.innerHTML !== '') {
            const item = itemProp.collection.items[parseInt(index.toString(), 10)];
            const element = this.createItems([item], itemProp.group.orientation, itemProp.group.id, itemProp.group.header, itemProp.group.enableGroupOverflow, itemProp.tabIndex, groupContainer)[0];
            if (itemProp.group.isCollapsed) {
                contentEle = this.ribbonDropDownModule.getOverflowDropDownPopup(itemProp, contentEle);
            }
            //insert the element in tab items property.
            const targetEle = targetId ? contentEle.querySelector('#' + targetId) : null;
            if (targetEle) {
                targetEle.closest('.' + constants.RIBBON_ITEM).insertAdjacentElement(isAfter ? 'afterend' : 'beforebegin', element);
            }
            else {
                if (element) {
                    contentEle.querySelector('#' + collectionId).append(element);
                }
            }
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.refreshLayout();
        }
    }
    /**
     * Removes ribbon item.
     *
     * @param {string} itemId - Gets the item ID.
     * @returns {void}
     */
    removeItem(itemId) {
        const itemProp = getItem(this.tabs, itemId);
        if (!itemProp) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        //Check whether the tab items are rendered
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        if (contentEle.innerHTML !== '') {
            let dropdownElement;
            let dropdown;
            if (this.activeLayout === RibbonLayout.Simplified) {
                dropdownElement = itemProp.group.enableGroupOverflow ?
                    contentEle.querySelector('#' + itemProp.group.id + constants.GROUPOF_BUTTON_ID) : null;
                dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
            }
            const item = itemProp.item;
            this.removeItemElement(contentEle, item, dropdown);
        }
        const ribbonCollection = itemProp.collection;
        const ribbonItems = ribbonCollection.items;
        ribbonItems.splice(itemProp.itemIndex, 1);
        ribbonCollection.setProperties({ items: ribbonItems }, true);
        if (this.selectedTab === itemProp.tabIndex) {
            this.refreshLayout();
        }
    }
    /**
     * Hides a specific ribbon item.
     *
     * @param {string} itemId - The ID of the item to be hidden.
     * @returns {void}
     */
    hideItem(itemId) {
        this.showHideItem(itemId, true);
    }
    /**
     * Shows a specific ribbon item.
     *
     * @param {string} itemId - The ID of the item to be shown.
     * @returns {void}
     */
    showItem(itemId) {
        this.showHideItem(itemId, false);
    }
    showHideItem(itemId, isHidden) {
        const itemProp = getItem(this.tabs, itemId);
        if (!itemProp) {
            return;
        }
        let ele;
        if (itemProp.item.type === 'GroupButton') {
            ele = getItemElement(this, itemId + constants.RIBBON_GROUP_BUTTON_ID, itemProp);
        }
        else {
            ele = getItemElement(this, itemId, itemProp);
        }
        if (ele) {
            const itemEle = closest(ele, '.e-ribbon-item');
            this.updateHiddenElements(itemProp.tabIndex, isHidden ? 'hideItem' : 'showItem', itemId, isHidden, itemEle, itemProp.group);
            if (this.selectedTab === itemProp.tabIndex) {
                this.refreshLayout();
            }
        }
        else {
            this.updateInitialProps(itemProp.tabIndex, itemId, 'hiddenItems', isHidden);
        }
    }
    updateInitialProps(tabIndex, id, key, isInsert) {
        let initialProps = this.initialPropsData[parseInt(tabIndex.toString(), 10)];
        if (!initialProps) {
            this.initialPropsData[parseInt(tabIndex.toString(), 10)] = {};
            initialProps = this.initialPropsData[parseInt(tabIndex.toString(), 10)];
        }
        if (initialProps) {
            if (!initialProps[`${key}`]) {
                initialProps[`${key}`] = [];
            }
            const itemIndex = initialProps[`${key}`].indexOf(id);
            if (isInsert) {
                if (itemIndex === -1) {
                    initialProps[`${key}`].push(id);
                }
            }
            else {
                if (itemIndex !== -1) {
                    initialProps[`${key}`].splice(itemIndex, 1);
                }
                if (initialProps[`${key}`].length === 0) {
                    delete initialProps[`${key}`];
                }
                if (Object.keys(initialProps).length === 0) {
                    delete this.initialPropsData[parseInt(tabIndex.toString(), 10)];
                }
            }
        }
    }
    checkHiddenItems(group, isHidden, tabIndex) {
        let isItemsHidden = true;
        let isEmptyCollection;
        const contentEle = this.tabObj.items[parseInt(tabIndex.toString(), 10)].content;
        const groupEle = contentEle.querySelector('#' + group.id);
        if (isHidden) {
            for (let i = 0; i < group.collections.length; i++) {
                if (isItemsHidden) {
                    const collection = group.collections[parseInt(i.toString(), 10)];
                    for (let j = 0; j < collection.items.length; j++) {
                        const item = collection.items[parseInt(j.toString(), 10)];
                        let itemEle;
                        if (item.type === 'GroupButton') {
                            const itemProp = getItem(this.tabs, item.id);
                            itemEle = getItemElement(this, item.id + constants.RIBBON_GROUP_BUTTON_ID, itemProp);
                        }
                        else {
                            itemEle = getItemElement(this, item.id);
                        }
                        const itemContainer = itemEle ? itemEle.closest('.e-ribbon-item') : null;
                        if (!(itemContainer.classList.contains('e-hidden'))) {
                            isItemsHidden = false;
                            break;
                        }
                    }
                }
            }
            if (isItemsHidden) {
                groupEle.classList.add('e-hide-group');
                this.checkOverflowItems(isHidden, contentEle, group.enableGroupOverflow, tabIndex, group.id);
            }
        }
        else {
            groupEle.classList.remove('e-hide-group');
            this.checkOverflowItems(isHidden, contentEle, group.enableGroupOverflow, tabIndex, group.id);
        }
        if (this.activeLayout === 'Simplified' && !group.enableGroupOverflow) {
            const itemsLength = groupEle.querySelectorAll('.' + constants.RIBBON_ITEM);
            if (itemsLength) {
                isEmptyCollection = this.checkEmptyCollection(itemsLength);
                groupEle.classList[isEmptyCollection ? 'add' : 'remove']('e-ribbon-emptyCollection');
            }
        }
    }
    checkOverflowItems(isHidden, contentEle, isGroupOF, tabIndex, groupID) {
        let overflowDDB;
        let overflowtarget;
        if (isGroupOF) {
            const overflowDDBEle = contentEle.querySelector('#' + groupID + constants.GROUPOF_BUTTON_ID);
            if (overflowDDBEle) {
                overflowDDB = getInstance(overflowDDBEle, DropDownButton);
                overflowtarget = overflowDDB.target;
            }
            if (overflowtarget) {
                if (isHidden) {
                    overflowtarget.classList.add('e-hide-group');
                }
                else {
                    overflowtarget.classList.remove('e-hide-group');
                }
            }
        }
        else {
            if (this.overflowDDB) {
                const overflowEle = this.overflowDDB.target;
                const ofTabContainer = overflowEle.querySelector('#' + this.tabs[parseInt(tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                if (ofTabContainer) {
                    const grpContainer = ofTabContainer.querySelector('#' + groupID + constants.CONTAINER_ID);
                    if (grpContainer) {
                        if (isHidden) {
                            grpContainer.classList.add('e-hide-group');
                        }
                        else {
                            grpContainer.classList.remove('e-hide-group');
                        }
                    }
                }
            }
        }
    }
    /**
     * tab - Gets the ribbon tab to be updated. The id of the tab is a required property. Other properties are optional.
     *
     * @param {RibbonTabModel} tab - Gets the ribbon tab model.
     * @returns {void}
     */
    updateTab(tab) {
        const tabId = tab.id;
        const index = getIndex(this.tabs, (e) => { return e.id === tabId; });
        if (index === -1) {
            return;
        }
        const contentEle = this.tabObj.items[parseInt(index.toString(), 10)].content;
        const groups = this.tabs[parseInt(index.toString(), 10)].groups;
        const tabEle = this.tabObj.element;
        if (groups && (contentEle.innerHTML !== '')) {
            // Check whether cssClass is passed by the user, and if it is, then remove the old values.
            if (tab.cssClass) {
                if (this.tabs[parseInt(index.toString(), 10)].cssClass) {
                    contentEle.classList.remove(this.tabs[parseInt(index.toString(), 10)].cssClass);
                    tabEle.querySelector('.e-active').classList.remove(this.tabs[parseInt(index.toString(), 10)].cssClass);
                }
            }
            // Check whether group is passed by the user, and if it is, then remove the old values.
            if (tab.groups) {
                for (const group of groups) {
                    const dropdownElement = group.isCollapsed ? contentEle.querySelector('#' + group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID) : null;
                    for (const collection of group.collections) {
                        for (const item of collection.items) {
                            let ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id) : contentEle.querySelector('#' + item.id);
                            if (item.type === RibbonItemType.GroupButton && this.activeLayout === 'Classic') {
                                ele = dropdownElement ? this.ribbonDropDownModule.getDDBItemElement(dropdownElement, item.id +
                                    constants.RIBBON_GROUP_BUTTON_ID) : contentEle.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID);
                            }
                            if (ele) {
                                this.destroyFunction(item, ele);
                            }
                        }
                    }
                    if (dropdownElement) {
                        this.ribbonDropDownModule.removeOverFlowDropDown(dropdownElement);
                    }
                }
                const groupElements = contentEle.querySelectorAll('.e-ribbon-group');
                // eslint-disable-next-line @typescript-eslint/tslint/config
                groupElements.forEach(groupEle => { groupEle.remove(); });
            }
        }
        if (index === this.selectedTab) {
            this.isAddRemove = true;
        }
        const ribbonTab = this.tabs[parseInt(index.toString(), 10)];
        ribbonTab.setProperties(tab, true);
        this.setProperties({ groups: this.checkID(ribbonTab.groups, 'group', ribbonTab.id) }, true);
        this.validateItemSize();
        if (contentEle.innerHTML === '') {
            // Check whether group is passed by the user and sets the updated values.
            if (tab.groups) {
                const elements = this.createGroups(ribbonTab.groups, index);
                append(elements, contentEle);
            }
            if (this.selectedTab === index) {
                this.refreshLayout();
            }
            // Check whether cssClass is passed by the user and sets the updated values.
            if (tab.cssClass) {
                contentEle.classList.add(ribbonTab.cssClass);
                tabEle.querySelector('.e-active').classList.add(ribbonTab.cssClass);
            }
            // Check whether header is passed by the user and sets the updated values.
            if (tab.header) {
                tabEle.querySelector('#' + tabId + constants.HEADER_ID).innerText = ribbonTab.header;
            }
        }
    }
    /**
     * group - Gets the ribbon group to be updated. The id of the group is a required property. Other properties are optional.
     *
     * @param {RibbonGroupModel} group - Gets the ribbon group model.
     * @returns {void}
     */
    updateGroup(group) {
        const groupId = group.id;
        const itemProp = getGroup(this.tabs, groupId);
        if (!itemProp) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        //Check whether the tab items are rendered
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        const groupEle = contentEle.querySelector('#' + groupId);
        const groupContainer = groupEle.querySelector('#' + group.id + constants.CONTAINER_ID);
        let dropdownElement;
        let dropdown;
        if (contentEle.innerHTML !== '') {
            if (itemProp.group.showLauncherIcon) {
                this.removeLauncherIcon(itemProp.group.id, null, contentEle);
            }
            if (this.activeLayout === RibbonLayout.Simplified) {
                dropdownElement = itemProp.group.enableGroupOverflow ?
                    contentEle.querySelector('#' + itemProp.group.id + constants.GROUPOF_BUTTON_ID) : null;
                dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
            }
            // Check whether cssClass is passed by the user, and if it is, then remove the old values.
            if (group.cssClass) {
                if (itemProp.group.cssClass) {
                    groupEle.classList.remove(itemProp.group.cssClass);
                    if (groupContainer) {
                        groupContainer.classList.remove(itemProp.group.cssClass);
                    }
                }
            }
            // Check whether collections or orientation is passed by the user, and if it is, then remove the old values.
            if (group.collections || group.orientation) {
                if (itemProp.group.collections || itemProp.group.orientation) {
                    for (const collection of itemProp.group.collections) {
                        for (const item of collection.items) {
                            this.removeItemElement(contentEle, item, dropdown);
                        }
                    }
                    const collectionElements = groupEle.querySelectorAll('.e-ribbon-collection');
                    // eslint-disable-next-line @typescript-eslint/tslint/config
                    collectionElements.forEach(collectionEle => { collectionEle.remove(); });
                    if (group.orientation) {
                        const groupContent = groupContainer.querySelector('.e-ribbon-group-content');
                        const removeCss = groupContent.classList.value.match(/(e-ribbon-[column|row]+)/g);
                        if (removeCss) {
                            removeClass([groupContent], removeCss);
                        }
                    }
                }
            }
            if (this.activeLayout === RibbonLayout.Simplified) {
                if (itemProp.group.enableGroupOverflow) {
                    if (dropdown.target.childElementCount === 0 || (dropdown.target.childElementCount
                        === 1 && this.isHeaderVisible(dropdown.target, itemProp.group.id))) {
                        this.removeOverflowButton(dropdown);
                    }
                }
                else {
                    const ofGroupContainer = dropdown.target.querySelector('#' + itemProp.group.id + constants.CONTAINER_ID);
                    if (ofGroupContainer && ofGroupContainer.childElementCount === 1) {
                        ofGroupContainer.remove();
                    }
                    const ofTabContainer = dropdown.target.querySelector('#' + this.tabs[parseInt(itemProp.tabIndex.toString(), 10)].id + constants.OVERFLOW_ID);
                    if (ofTabContainer && ofTabContainer.childElementCount === 0) {
                        ofTabContainer.remove();
                    }
                }
            }
        }
        const ribbongroup = itemProp.group;
        ribbongroup.setProperties(group, true);
        ribbongroup.setProperties({ collections: this.checkID(ribbongroup.collections, 'collection', ribbongroup.id) }, true);
        this.validateItemSize();
        if (contentEle.innerHTML !== '') {
            // Check whether showLauncherIcon or orientation is passed by the user and sets the updated values.
            if (group.showLauncherIcon) {
                this.createLauncherIcon(ribbongroup.id, groupContainer, itemProp.tabIndex);
            }
            // Check whether collections or orientation is passed by the user and sets the updated values.
            if (group.collections || group.orientation) {
                const groupContent = groupContainer.querySelector('.e-ribbon-group-content');
                groupContent.classList.add(((ribbongroup.orientation === 'Column') || (this.activeLayout === 'Simplified')) ? constants.RIBBON_COLUMN : constants.RIBBON_ROW);
                const elements = this.createCollection(ribbongroup.collections, ribbongroup.orientation, ribbongroup.id, ribbongroup.header, ribbongroup.enableGroupOverflow, itemProp.tabIndex, groupContainer);
                append(elements, groupContent);
            }
            if (this.selectedTab === itemProp.tabIndex) {
                this.refreshLayout();
            }
            // Check whether cssClass is passed by the user and sets the updated values.
            if (group.cssClass) {
                groupEle.classList.add(ribbongroup.cssClass);
                if (groupContainer) {
                    groupContainer.classList.add(ribbongroup.cssClass);
                }
            }
            // Check whether header is passed by the user and sets the updated values.
            if (group.header) {
                if (this.activeLayout === RibbonLayout.Simplified && !group.enableGroupOverflow) {
                    const overflowHeader = dropdown.target.querySelector('#' + group.id + constants.HEADER_ID);
                    if (overflowHeader) {
                        overflowHeader.innerText = ribbongroup.header;
                    }
                }
                else if (this.activeLayout === RibbonLayout.Classic && !ribbongroup.isCollapsed) {
                    groupEle.querySelector('.e-ribbon-group-header').innerText = ribbongroup.header;
                }
                else if (this.activeLayout === RibbonLayout.Classic && ribbongroup.isCollapsed) {
                    const overflowEle = groupEle.querySelector('#' + ribbongroup.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID);
                    // need to set instance for dropdown
                    const dropDownBtn = getInstance(overflowEle, DropDownButton);
                    const overflowHeader = dropDownBtn.target.querySelector('#' + group.id + constants.HEADER_ID);
                    if (overflowHeader) {
                        overflowHeader.innerText = ribbongroup.header;
                    }
                }
            }
        }
    }
    /**
     * collection - Gets the ribbon collection to be updated. The id of the collection is a required property. Other properties are optional.
     *
     * @param {RibbonCollectionModel} collection - Gets the ribbon collection model.
     * @returns {void}
     */
    updateCollection(collection) {
        const collectionId = collection.id;
        const itemProp = getCollection(this.tabs, collectionId);
        if (!itemProp) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        //Check whether the tab items are rendered
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        const collectionEle = contentEle.querySelector('#' + collectionId);
        if (contentEle.innerHTML !== '') {
            let dropdownElement;
            let dropdown;
            if (this.activeLayout === RibbonLayout.Simplified) {
                dropdownElement = itemProp.group.enableGroupOverflow ?
                    contentEle.querySelector('#' + itemProp.group.id + constants.GROUPOF_BUTTON_ID) : null;
                dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
            }
            // Check whether cssClass is passed by the user, and if it is, then remove the old values.
            if (collection.cssClass) {
                if (itemProp.collection.cssClass) {
                    collectionEle.classList.remove(itemProp.collection.cssClass);
                }
            }
            if (collection.items) {
                if (itemProp.collection.items) {
                    for (const item of itemProp.collection.items) {
                        this.removeItemElement(contentEle, item, dropdown);
                    }
                }
            }
        }
        const ribboncollection = itemProp.collection;
        ribboncollection.setProperties(collection, true);
        ribboncollection.setProperties({ items: this.checkID(ribboncollection.items, 'item', ribboncollection.id) }, true);
        this.validateItemSize();
        if (contentEle.innerHTML !== '') {
            if (collection.items) {
                const groupContainer = contentEle.querySelector('#' + itemProp.group.id + constants.CONTAINER_ID);
                const elements = this.createItems(ribboncollection.items, itemProp.group.orientation, itemProp.group.id, itemProp.group.header, itemProp.group.enableGroupOverflow, itemProp.tabIndex, groupContainer);
                append(elements, collectionEle);
            }
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.refreshLayout();
        }
        // Check whether cssClass is passed by the user and sets the updated values.
        if (collection.cssClass) {
            collectionEle.classList.add(ribboncollection.cssClass);
        }
    }
    /**
     * item - Gets the ribbon item to be updated. The id of the item is a required property. Other properties are optional.
     *
     * @param {RibbonItemModel} item - Gets the ribbon item model.
     * @returns {void}
     */
    updateItem(item) {
        const itemId = item.id;
        const itemProp = getItem(this.tabs, itemId);
        if (!itemProp) {
            return;
        }
        if (this.selectedTab === itemProp.tabIndex) {
            this.clearOverflowResize();
        }
        //Check whether the tab items are rendered
        const contentEle = this.tabObj.items[itemProp.tabIndex].content;
        const groupEle = contentEle.querySelector('#' + itemProp.group.id);
        if (groupEle) {
            const groupContainer = groupEle.querySelector('#' + itemProp.group.id + constants.CONTAINER_ID);
            let itemContainer = null;
            let itemEle = null;
            let dropdownElement;
            let dropdown;
            if (contentEle.innerHTML !== '') {
                if (this.activeLayout === RibbonLayout.Simplified) {
                    dropdownElement = itemProp.group.enableGroupOverflow ?
                        contentEle.querySelector('#' + itemProp.group.id + constants.GROUPOF_BUTTON_ID) : null;
                    dropdown = dropdownElement ? getComponent(dropdownElement, DropDownButton) : this.overflowDDB;
                }
                if (this.activeLayout === RibbonLayout.Simplified && itemProp.item.displayOptions === DisplayMode.Overflow) {
                    itemContainer = dropdown.target.querySelector('#' + itemId + constants.CONTAINER_ID);
                    itemEle = dropdown.target.querySelector('#' + itemId);
                    if (item.displayOptions && item.displayOptions !== DisplayMode.Overflow) {
                        const collectionEle = groupContainer.querySelector('#' + itemProp.collection.id);
                        if (collectionEle) {
                            collectionEle.appendChild(itemContainer);
                        }
                    }
                }
                else {
                    itemContainer = groupContainer.querySelector('#' + itemId + constants.CONTAINER_ID);
                    itemEle = contentEle.querySelector('#' + itemId);
                    if (!itemContainer) {
                        itemContainer = dropdown ? dropdown.target.querySelector('#' + itemId + constants.CONTAINER_ID) : groupContainer.querySelector('#' + itemId + constants.CONTAINER_ID);
                    }
                    if (!itemEle) {
                        itemEle = dropdown ? dropdown.target.querySelector('#' + itemId) : contentEle.querySelector('#' + itemId);
                    }
                    if (itemProp.item.type === 'Gallery') {
                        if (!itemEle) {
                            itemEle = contentEle.querySelector('#' + itemId + '_galleryWrapper');
                        }
                    }
                    if (itemProp.item.type === 'GroupButton' && this.activeLayout === RibbonLayout.Classic) {
                        itemEle = contentEle.querySelector('#' + itemId + constants.RIBBON_GROUP_BUTTON_ID);
                    }
                    if (this.activeLayout === RibbonLayout.Simplified && item.displayOptions === DisplayMode.Overflow) {
                        this.createOverflowPopup(itemProp.item, itemProp.tabIndex, itemProp.group.enableGroupOverflow, itemProp.group.id, itemProp.group.header, itemContainer, groupContainer);
                        if ((itemProp.item.type === RibbonItemType.DropDown) || (itemProp.item.type === RibbonItemType.SplitButton) ||
                            (item.type === RibbonItemType.GroupButton) || (item.type === RibbonItemType.Gallery)) {
                            this.updatePopupItems(itemProp.item, itemContainer, itemProp.group.enableGroupOverflow, true);
                        }
                    }
                }
                // Check whether cssClass is passed by the user, and if it is, then remove the old values.
                if (item.cssClass) {
                    if (itemProp.item.cssClass) {
                        itemContainer.classList.remove(itemProp.item.cssClass);
                    }
                }
                this.destroyFunction(itemProp.item, itemEle);
                itemEle.remove();
                const removeCss = itemContainer.classList.value.match(/(e-ribbon-[large|medium|small]+-item)/g);
                if (removeCss) {
                    removeClass([itemContainer], removeCss);
                }
            }
            const ribbonItem = itemProp.item;
            ribbonItem.setProperties(item, true);
            this.validateItemSize();
            if (contentEle.innerHTML !== '') {
                // To avoid undefined items condition is added
                if (ribbonItem.ribbonTooltipSettings && isTooltipPresent(ribbonItem.ribbonTooltipSettings)) {
                    itemContainer.classList.add(constants.RIBBON_TOOLTIP_TARGET);
                    this.tooltipData.push({ id: itemContainer.id, data: ribbonItem.ribbonTooltipSettings });
                }
                let size = ribbonItem.activeSize;
                if (this.activeLayout === 'Simplified') {
                    size = ((ribbonItem.allowedSizes === RibbonItemSize.Large) || (ribbonItem.allowedSizes & RibbonItemSize.Medium) ||
                        (ribbonItem.displayOptions === DisplayMode.Overflow)) ? RibbonItemSize.Medium : RibbonItemSize.Small;
                    ribbonItem.setProperties({ activeSize: size }, true);
                }
                if (size & RibbonItemSize.Large) {
                    itemContainer.classList.add(constants.RIBBON_LARGE_ITEM, constants.RIBBON_CONTENT_HEIGHT);
                }
                else {
                    if (size & RibbonItemSize.Medium) {
                        itemContainer.classList.add(constants.RIBBON_MEDIUM_ITEM);
                    }
                    else {
                        itemContainer.classList.add(constants.RIBBON_SMALL_ITEM);
                    }
                }
                this.createRibbonItem(ribbonItem, itemContainer);
                if (itemProp.item.type === 'Gallery' && document.querySelector('#' + itemId + '_container').closest('.e-ribbon-overflow-target') && item.displayOptions !== DisplayMode.Overflow) {
                    this.createOverflowPopup(itemProp.item, itemProp.tabIndex, itemProp.group.enableGroupOverflow, itemProp.group.id, itemProp.group.header, itemContainer, groupContainer);
                }
                if (this.activeLayout === 'Simplified' && itemProp.group.enableGroupOverflow && dropdown) {
                    if (dropdown.target.childElementCount === 0 ||
                        (dropdown.target.childElementCount === 1 &&
                            this.isHeaderVisible(dropdown.target, itemProp.group.id))) {
                        this.removeOverflowButton(dropdown);
                    }
                }
                if (this.selectedTab === itemProp.tabIndex) {
                    this.refreshLayout();
                }
                if (item.cssClass) {
                    itemContainer.classList.add(ribbonItem.cssClass);
                }
                if (!(ribbonItem.disabled) && itemContainer.classList.contains(constants.DISABLED_CSS)) {
                    itemContainer.classList.remove(constants.DISABLED_CSS);
                }
                this.enableDisableItem(ribbonItem.id, ribbonItem.disabled);
            }
        }
        else {
            this.isUpdateItems = true;
            this.itemsModel.push(item);
            this.targetTabs[item.id] = itemProp.tabIndex;
        }
    }
    removeItemElement(contentEle, item, dropdown) {
        let ele = null;
        if (this.activeLayout === RibbonLayout.Classic) {
            ele = (item.displayOptions & DisplayMode.Classic) ? contentEle.querySelector('#' + item.id) : null;
            if (item.type === 'GroupButton') {
                ele = (item.displayOptions & DisplayMode.Classic) ? contentEle.querySelector('#' + item.id + constants.RIBBON_GROUP_BUTTON_ID) : null;
            }
        }
        else {
            //Checks for Simplified and Auto options (Auto = classic + simplified + popup)
            ele = (item.displayOptions & DisplayMode.Simplified) ? contentEle.querySelector('#' + item.id) : null;
            // element will be null for "Popup" and if the item is moved to overflow in "Auto" mode
            if (!ele) {
                ele = dropdown.target.querySelector('#' + item.id);
            }
        }
        if (ele) {
            this.destroyFunction(item, ele);
            if (item.type === 'GroupButton' && this.activeLayout === RibbonLayout.Simplified) {
                document.getElementById(item.id + constants.CONTAINER_ID).remove();
            }
            else {
                ele.closest('#' + item.id + constants.CONTAINER_ID).remove();
            }
        }
    }
    /**
     * Enables ribbon item.
     *
     * @param {string} itemId - Gets the item ID.
     * @returns {void}
     */
    enableItem(itemId) {
        this.enableDisableItem(itemId, false);
    }
    /**
     * Disables ribbon item.
     *
     * @param {string} itemId - Gets the item ID.
     * @returns {void}
     */
    disableItem(itemId) {
        this.enableDisableItem(itemId, true);
    }
    /**
     * Gets the Ribbon item model associated with the specified item ID.
     *
     * @param {string} itemId - The unique ID of the Ribbon item.
     * @returns {RibbonItemModel} - Returns the Ribbon item model.
     */
    getItem(itemId) {
        return getItem(this.tabs, itemId).item;
    }
    enableDisableItem(itemId, isDisabled) {
        let isUpdated = false;
        let isOverflow = false;
        const itemProp = getItem(this.tabs, itemId);
        if (!itemProp) {
            return;
        }
        itemProp.item.setProperties({ disabled: isDisabled }, true);
        let ele;
        if (itemProp.item.type === 'GroupButton') {
            ele = getItemElement(this, itemId + constants.RIBBON_GROUP_BUTTON_ID, itemProp);
        }
        else if (itemProp.item.type === 'Gallery') {
            ele = document.querySelector('#' + itemId);
            if (!ele) {
                ele = document.querySelector('#' + itemId + '_galleryWrapper');
            }
            else {
                isOverflow = true;
            }
        }
        else {
            ele = getItemElement(this, itemId, itemProp);
        }
        if (ele) {
            const itemEle = closest(ele, '.e-ribbon-item');
            const moduleName = this.getItemModuleName(itemProp.item);
            isUpdated = isDisabled ? !itemEle.classList.contains(constants.DISABLED_CSS) :
                itemEle.classList.contains(constants.DISABLED_CSS);
            if (moduleName !== 'template') {
                if (isUpdated) {
                    if (moduleName === 'group-btn' && this.activeLayout === 'Simplified') {
                        updateControlDisabled(ele, 'dropdown-btn', isDisabled);
                        for (let i = 0; i < itemProp.item.groupButtonSettings.items.length; i++) {
                            const btnEle = document.querySelector('#' + itemId + constants.RIBBON_GROUP_BUTTON_ID + i);
                            updateControlDisabled(btnEle, 'btn', isDisabled);
                        }
                    }
                    else if (moduleName === 'group-btn' && this.activeLayout === 'Classic') {
                        for (let i = 0; i < itemProp.item.groupButtonSettings.items.length; i++) {
                            const btnEle = ele.querySelector('#' + itemId + constants.RIBBON_GROUP_BUTTON_ID + i);
                            updateControlDisabled(btnEle, 'btn', isDisabled);
                        }
                    }
                    else if (moduleName === 'gallery') {
                        ele.classList.toggle(constants.DISABLED_CSS, isDisabled);
                        document.getElementById(itemId + '_popupButton').classList.toggle(constants.DISABLED_CSS, isDisabled);
                        if (isOverflow) {
                            const galleryEle = document.getElementById(itemId + '_galleryWrapper');
                            galleryEle.classList.toggle(constants.DISABLED_CSS, isDisabled);
                        }
                    }
                    else {
                        updateControlDisabled(ele, moduleName, isDisabled);
                    }
                }
            }
            else {
                ele.classList.toggle(constants.DISABLED_CSS, isDisabled);
                ele.toggleAttribute('disabled', isDisabled);
            }
            itemEle.classList.toggle(constants.DISABLED_CSS, itemProp.item.disabled);
        }
    }
    unwireEvents() {
        EventHandler.remove(window, 'resize', this.resizeListener);
        EventHandler.remove(document.body, 'keydown', this.keytipActionHandler);
        EventHandler.remove(document, 'mousedown', this.mouseEventHandler);
        EventHandler.remove(document, 'scroll', this.mouseEventHandler);
    }
    destroy() {
        this.keyboardModuleRibbon.destroy();
        this.keyboardModuleRibbon = null;
        destroyTooltip(this.element);
        if (this.refreshing) {
            this.clearOverflowDropDown(this.selectedTab);
        }
        this.destroyTabItems(this.tabs);
        if (!this.hideLayoutSwitcher) {
            this.removeExpandCollapse();
        }
        this.collapseButton = undefined;
        if (this.scrollModule) {
            this.scrollModule.destroy();
            this.scrollModule = null;
        }
        if (this.ribbonTempEle) {
            remove(this.ribbonTempEle);
            this.ribbonTempEle = null;
        }
        super.destroy();
        this.tabObj.destroy();
        this.tabObj = undefined;
        this.l10n = null;
        this.initialPropsData = {};
        this.hiddenGroups = [];
        this.hiddenElements = {};
        this.keyTipElements = {};
        this.itemsModel = [];
        this.targetTabs = {};
        remove(this.element.querySelector('#' + this.element.id + constants.TAB_ID));
        this.element.style.removeProperty(constants.RIBBON_FILE_MENU_WIDTH);
        this.element.style.removeProperty(constants.RIBBON_HELP_PANE_TEMPLATE_WIDTH);
        this.element.style.removeProperty('width');
        if (this.cssClass) {
            removeClass([this.element], this.cssClass.split(constants.SPACE));
        }
        this.element.classList.remove(constants.RTL_CSS, constants.RIBBON_SIMPLIFIED_MODE, constants.RIBBON_OVERFLOW, constants.RIBBON_COLLAPSIBLE, constants.RIBBON_MINIMIZE, 'e-rbn');
        this.unwireEvents();
    }
    /**
     * Called internally if any of the property value changed.
     *
     * @param  {RibbonModel} newProp - Specifies new properties
     * @param  {RibbonModel} oldProp - Specifies old properties
     * @returns {void}
     * @private
     */
    onPropertyChanged(newProp, oldProp) {
        for (const prop of Object.keys(newProp)) {
            switch (prop) {
                case 'activeLayout':
                    this.switchLayout();
                    break;
                case 'cssClass':
                    if (oldProp.cssClass) {
                        this.element.classList.remove(...oldProp.cssClass.split(constants.SPACE));
                    }
                    if (newProp.cssClass) {
                        this.element.classList.add(...newProp.cssClass.split(constants.SPACE));
                    }
                    break;
                case 'isMinimized':
                    this.element.classList.toggle(constants.RIBBON_MINIMIZE, this.isMinimized);
                    this.tabObj.element.querySelector('.e-content').style.display = this.isMinimized ? 'none' : 'block';
                    if (!this.isMinimized) {
                        this.refreshLayout();
                    }
                    break;
                case 'locale':
                    this.l10n.setLocale(this.locale);
                    this.updateCommonProperty({ locale: this.locale });
                    break;
                case 'enablePersistence':
                    this.updateCommonProperty({ enablePersistence: this.enablePersistence });
                    break;
                case 'enableRtl':
                    this.element.classList.toggle(constants.RTL_CSS, this.enableRtl);
                    this.updateCommonProperty({ enableRtl: newProp.enableRtl });
                    if (this.scrollModule) {
                        this.scrollModule.setProperties({ enableRtl: newProp.enableRtl });
                    }
                    break;
                case 'launcherIconCss':
                    for (let i = 0; i < this.tabs.length; i++) {
                        const tabContent = this.tabObj.items[parseInt(i.toString(), 10)].content;
                        const tab = this.tabs[parseInt(i.toString(), 10)];
                        if (tabContent.querySelector('.' + constants.RIBBON_GROUP)) {
                            for (const group of tab.groups) {
                                if (group.showLauncherIcon) {
                                    const className = constants.RIBBON_LAUNCHER_ICON_ELE + ' ' + (this.launcherIconCss || constants.RIBBON_LAUNCHER_ICON);
                                    if (group.isCollapsed) {
                                        const element = tabContent.querySelector('#' + group.id + constants.OVERFLOW_ID + constants.DROPDOWN_ID);
                                        const dropdown = getComponent(element, DropDownButton);
                                        const launcherIconEle = dropdown.target.querySelector('#' + group.id + constants.LAUNCHER_ID);
                                        launcherIconEle.className = className;
                                    }
                                    else {
                                        const element = tabContent.querySelector('#' + group.id + constants.LAUNCHER_ID);
                                        element.className = className;
                                    }
                                }
                            }
                        }
                    }
                    break;
                case 'selectedTab': {
                    const tabEle = this.tabObj.element;
                    const toolbarItem = tabEle.querySelectorAll('.e-toolbar-item')[parseInt(newProp.selectedTab.toString(), 10)];
                    if (!(toolbarItem.classList.contains('e-hidden') || toolbarItem.classList.contains('e-disable'))) {
                        this.tabObj.setProperties({ selectedItem: newProp.selectedTab });
                    }
                    break;
                }
                case 'tabAnimation':
                    this.tabObj.setProperties({ animation: newProp.tabAnimation });
                    break;
                case 'tabs':
                    this.reRenderTabs(newProp.tabs);
                    break;
                case 'contextualTabs':
                    for (let i = 0; i < this.contextualTabs.length; i++) {
                        if (newProp.contextualTabs[parseInt(i.toString(), 10)]) {
                            this.ribbonContextualTabModule.updateContextualTabs(newProp.contextualTabs[parseInt(i.toString(), 10)], this.contextualTabs[parseInt(i.toString(), 10)]);
                        }
                    }
                    break;
                case 'width':
                    this.element.style.width = formatUnit(newProp.width);
                    this.refreshLayout();
                    break;
                case 'fileMenu': {
                    if (this.ribbonFileMenuModule) {
                        this.ribbonFileMenuModule.updateFileMenu(this.fileMenu);
                    }
                    const toolbarEle = this.tabObj['tbObj'];
                    toolbarEle.refreshOverflow();
                    break;
                }
                case 'backStageMenu': {
                    if (this.ribbonBackstageModule) {
                        this.ribbonBackstageModule.updateBackStageMenu(this.backStageMenu);
                    }
                    const toolbarElement = this.tabObj['tbObj'];
                    toolbarElement.refreshOverflow();
                    break;
                }
                case 'helpPaneTemplate': {
                    if (this.ribbonTempEle) {
                        remove(this.ribbonTempEle);
                        this.ribbonTempEle = null;
                        this.tabObj.element.style.setProperty(constants.RIBBON_HELP_PANE_TEMPLATE_WIDTH, '0px');
                    }
                    if (this.helpPaneTemplate) {
                        this.createHelpPaneTemplate();
                    }
                    const toolbar = this.tabObj['tbObj'];
                    toolbar.refreshOverflow();
                    break;
                }
                case 'hideLayoutSwitcher':
                    if (this.hideLayoutSwitcher) {
                        this.removeExpandCollapse();
                    }
                    else {
                        this.addExpandCollapse();
                    }
                    break;
            }
        }
    }
};
__decorate([
    Property('Classic')
], Ribbon.prototype, "activeLayout", void 0);
__decorate([
    Property('')
], Ribbon.prototype, "cssClass", void 0);
__decorate([
    Property(false)
], Ribbon.prototype, "enableKeyTips", void 0);
__decorate([
    Property('')
], Ribbon.prototype, "layoutSwitcherKeyTip", void 0);
__decorate([
    Complex({}, FileMenuSettings)
], Ribbon.prototype, "fileMenu", void 0);
__decorate([
    Complex({}, BackStageMenu)
], Ribbon.prototype, "backStageMenu", void 0);
__decorate([
    Property('')
], Ribbon.prototype, "launcherIconCss", void 0);
__decorate([
    Property(false)
], Ribbon.prototype, "isMinimized", void 0);
__decorate([
    Property('en-us')
], Ribbon.prototype, "locale", void 0);
__decorate([
    Property(0)
], Ribbon.prototype, "selectedTab", void 0);
__decorate([
    Complex({}, TabAnimationSettings)
], Ribbon.prototype, "tabAnimation", void 0);
__decorate([
    Collection([], RibbonTab)
], Ribbon.prototype, "tabs", void 0);
__decorate([
    Collection([], RibbonContextualTabSettings)
], Ribbon.prototype, "contextualTabs", void 0);
__decorate([
    Property('100%')
], Ribbon.prototype, "width", void 0);
__decorate([
    Property('')
], Ribbon.prototype, "helpPaneTemplate", void 0);
__decorate([
    Property(false)
], Ribbon.prototype, "hideLayoutSwitcher", void 0);
__decorate([
    Event()
], Ribbon.prototype, "tabSelecting", void 0);
__decorate([
    Event()
], Ribbon.prototype, "tabSelected", void 0);
__decorate([
    Event()
], Ribbon.prototype, "ribbonExpanding", void 0);
__decorate([
    Event()
], Ribbon.prototype, "ribbonCollapsing", void 0);
__decorate([
    Event()
], Ribbon.prototype, "ribbonLayoutSwitched", void 0);
__decorate([
    Event()
], Ribbon.prototype, "launcherIconClick", void 0);
__decorate([
    Event()
], Ribbon.prototype, "created", void 0);
__decorate([
    Event()
], Ribbon.prototype, "overflowPopupOpen", void 0);
__decorate([
    Event()
], Ribbon.prototype, "overflowPopupClose", void 0);
Ribbon = Ribbon_1 = __decorate([
    NotifyPropertyChanges
], Ribbon);
export { Ribbon };
